﻿import {
  ATOMIC_BLAST_EFFECT,
  CATOLE_BLAST_EFFECT,
  CYCLONE_CUT_EFFECT,
  FIREBALL_BLAST_EFFECT,
  HUNT_BALANCE,
  HUNT_BOMB_BODY_RADIUS,
  HUNT_CATOLE_BODY_BPS,
  HUNT_MISSILE_BODY_RADIUS,
  HuntClock,
  HuntSimulation,
  LIFESTEAL_HEAL_EFFECT,
  MISSILE_EFFECT,
  type ProjectileEntity,
} from '@kenocyber/game-domain';
import type { SkinRuntimeManifest } from '@kenocyber/shared';
import {
  type AssetManifest,
  CANNON_CATALOG,
  CANNON_RARITIES,
  type CannonRarity,
  findEnergyColor,
  type LoadoutWire,
  type PetEnergyColor,
  type SkinVisualPlan,
  simulationSeed,
} from '@kenocyber/shared';
import Phaser from 'phaser';
import { DEV_TOOLS_ALLOWED } from '../dev-tools';
import { spriteRotationForWorldAngle } from './art-orientation';
import { assetUrl, findAnimatedAsset, findStaticAsset, staticAssetUrl } from './assets';
import { AtomicBlastSystem } from './atomic-blast';
import {
  ATOMIC_BOMB_BODY_SPAN,
  atomicBombRollFrame,
  ensureAtomicBombTexture,
} from './atomic-bomb-art';
import { BastionFx } from './bastion-fx';
import { bleedAudioFrom } from './bleed-audio';
import {
  BLEED_BURST_POOL_CEILING,
  BleedBurstSystem,
  BleedMarkSystem,
  type BleedMarkTarget,
} from './bleed-mark';
import type { GameBridge, HuntSessionMessage } from './bridge';
import { cannonAssetId, cannonTextureKey } from './cannon-art';
import { CATOLE_SHAKE_INTENSITY, CATOLE_SHAKE_MS, CardBlastFx } from './card-blast-fx';
import { cardColourStore } from './card-colour';
import { MagicCircleFx, SolarLanceFx } from './card-fx';
import {
  COMBAT_TEXT_BLEED_STYLE,
  COMBAT_TEXT_DAMAGE_COLOR,
  COMBAT_TEXT_HEAL_COLOR,
  COMBAT_TEXT_HEAL_OFFSET_PX,
  COMBAT_TEXT_LIFE_MS,
  COMBAT_TEXT_RISE_PX,
  COMBAT_TEXT_SPREAD_STEPS,
  COMBAT_TEXT_STYLE,
  combatTextSpread,
  formatCombatNumber,
  formatHealthPair,
} from './combat-text';
import { CycloneCutSystem } from './cyclone-cut';
import { type ConfirmedHeal, healNumbersFrom } from './heal-numbers';
import { HealTally } from './heal-tally';
import { deathAnnouncement } from './hunt-death-announcement';
import {
  ensureHuntMapBodyPendingTexture,
  HUNT_MAP_BODY_PENDING_TEXTURE,
  type HuntMapBodyKind,
  huntMapBodyAnimKey,
  huntMapBodyTextureKey,
  releaseHuntMapBody,
  requestHuntMapBody,
} from './hunt-map-art';
import {
  type AirParticleSpec,
  airParticlesForMap,
  BACKGROUND_DRIFT_UNITS_PER_SECOND,
  backgroundDriftForMap,
  backgroundForMap,
  backgroundTextureKey,
  type EnergyTraceSpec,
  energyTracesForMap,
  type GroundLifeSpec,
  groundLifeForMap,
  type LightningSpec,
  lightningForMap,
  type MapBackground,
  type ShootingStarSpec,
  STARFIELD_BACKGROUND,
  shootingStarsForMap,
  showsStarfield,
} from './map-background';
import {
  ensureMissileBodyTexture,
  ensureMissileFlameTexture,
  ensureMissileTrailTexture,
  MISSILE_BLAST_RADIUS_FACTOR,
  MISSILE_BODY_SPAN,
  MISSILE_FLAME_ANCHOR,
  MISSILE_FLAME_SCALE,
  MISSILE_TRAIL_FADE_MS,
  MISSILE_TRAIL_INTERVAL_MS,
  MISSILE_TRAIL_SCALE,
  MissileBlastSystem,
  missileFlameFrame,
  missileRotation,
} from './missile-art';
import {
  bulletPhaseTurns,
  PET_BEAM_COLOR_FADE_MS,
  PET_BEAM_PHASE_TURNS,
  PET_BULLET_PHASE_TURNS,
  petEnergyColorAt,
} from './pet-energy-color';
import { petMood } from './pet-mood';
import {
  POISON_MARK_MIN_PX,
  POISON_MARK_TARGET_RATIO,
  PoisonMarkSystem,
  type PoisonMarkTarget,
  poisonBandHeight,
} from './poison-mark';
import {
  BEAM_ARENA_THICKNESS,
  BEAM_EVOLUTION_GAUGE,
  BEAM_LAYER_ALPHA,
  BEAM_LAYERS,
  BEAM_PULSE_COUNT,
  BEAM_PULSE_TRAVEL_MS,
  type BeamLayer,
  DASH_LATERAL_SCALE,
  DASH_ORIGIN_X,
  DASH_RENDER_SCALE,
  ENERGY_BALL_ARENA_SIZE,
  ENERGY_BALL_TEXTURE_SIZE,
  ensureBeamPulseTexture,
  ensureBeamTexture,
  ensureDashTexture,
  ensureEnergyBallTexture,
  ensureImpactTexture,
  ensureMissileGlowTexture,
  ensureMissileTexture,
  ensureShockwaveTexture,
  ensureStreakTexture,
  MISSILE_GLOW_SCALE,
  MISSILE_ORIGIN_X,
  MISSILE_RENDER_SCALE,
  STREAK_RENDER_SCALE,
} from './projectile-art';
import { SkinRenderer } from './SkinRenderer';
import { SawBladeSystem } from './saw-blade';
import { requestSharedTexture } from './shared-texture-requests';
import { SKIN_VFX_BASE_URL, SKIN_VFX_TEXTURES } from './skin-art';
import {
  type ResolvedSkinContent,
  resolveSkinContent,
  skinContentChanged,
} from './skin-resolution';
import { fetchSkinManifest } from './skin-runtime';
import {
  ensureStarCrossTexture,
  ensureStarGlowTexture,
  ensureStarStreakTexture,
  STAR_LAYERS,
  STAR_TINTS,
} from './starfield';
import { requestDroneTexture, SUMMON_DRONE_TEXTURE } from './summon-art';
import { VitalHealFlashSystem } from './vital-heal-flash';

/**
 * Renders the hunt simulation. The simulation is the single source of truth;
 * this scene only draws its state and forwards presentation events. It never
 * decides damage, rewards or deaths.
 *
 * Only the assets of the CURRENT hunt are loaded: ship1, the equipped cannon,
 * the map's mob sheet and the map's boss sheet - four files.
 */

export interface HuntSceneOptions {
  readonly bridge: GameBridge;
  readonly manifest: AssetManifest;
  readonly debug: boolean;
  readonly bossFast: boolean;
  /** DEV ONLY - very high cadence, still drawn as discrete projectiles. */
  readonly rapidFire: boolean;
  /** Server-authorised session the scene must run. Never invented locally. */
  readonly session: HuntSessionMessage;
}

const ARENA_W = HUNT_BALANCE.arena.width;
const ARENA_H = HUNT_BALANCE.arena.height;

/** Enemy health bars are red for every enemy, mobs and boss alike. */
const ENEMY_BAR_BACKDROP = 0x05050a;
const ENEMY_BAR_BORDER = 0x521018;
const ENEMY_BAR_GLOW = 0xff2e4d;
const ENEMY_BAR_CORE = 0xff2222;

/**
 * Damage popups and enemy health text.
 *
 * White with a black outline so they stay readable over any monster art, plus
 * a restrained cyan glow that matches the rest of the interface. Small and
 * sharp: these are read at a glance while something is shooting.
 */
const COMBAT_STROKE = '#04040a';
/**
 * O atacante da caça, para a chave partilhada `(atacante, tique)`.
 *
 * Há uma nave, portanto o campo é constante - e existe na mesma para os dois
 * modos usarem a MESMA agregação em vez de duas que podem divergir.
 */
const HUNT_SHIP = 'ship';

/** Bar thickness, tall enough for the number inside it and no taller. */
const MOB_BAR_THICKNESS = 11;
const BOSS_BAR_THICKNESS = 16;

/**
 * One animated thing lying on a map's ground.
 *
 * `crawl` is its own sideways speed; the downward speed is the map's, because
 * it is not moving down at all - the ground under it is.
 */
type Rgb = readonly [number, number, number];

interface AirEmitter {
  readonly emitter: Phaser.GameObjects.Particles.ParticleEmitter;
  readonly spec: AirParticleSpec;
  /** Fractional particles owed since the last whole one was born. */
  owed: number;
}

/**
 * Every air particle a map may ask for.
 *
 * A named union rather than an inline one so the palette below can be keyed
 * by it and CHECKED against it.
 */
type AirTextureKind =
  | 'fire'
  | 'blood'
  | 'violet'
  | 'current'
  | 'gold'
  | 'ember'
  | 'spore'
  | 'sand'
  | 'crucifix'
  | 'spore-green'
  | 'petal-blue'
  | 'bloom-blue'
  | 'cloud-green'
  | 'cross'
  | 'cross-violet'
  | 'cross-gold'
  | 'cross-blue'
  | 'vent'
  | 'rune-blue'
  | 'halo-blue'
  | 'star-violet'
  | 'cloud';

/**
 * The kinds drawn as a round glow, which are the ones that need a skirt.
 *
 * Derived by subtraction rather than listed again: a new kind is round
 * unless it is explicitly given a shape, so forgetting to add it here is not
 * possible - it lands in this set on its own and the palette stops compiling.
 */
type RoundAirKind = Exclude<
  AirTextureKind,
  | 'cloud'
  | 'cloud-green'
  | 'crucifix'
  | 'bloom-blue'
  | 'halo-blue'
  | 'cross'
  | 'cross-violet'
  | 'cross-gold'
  | 'cross-blue'
  | 'star-violet'
>;
interface GroundCreature {
  readonly sprite: Phaser.GameObjects.Sprite;
  readonly crawl: number;
  /** Radians per second, signed. Zero for anything that does not turn. */
  readonly turn: number;
  /**
   * Set when this thing is one rung of a LADDER, null for anything scattered.
   *
   * Carries what the band has to be recomputed from rather than the band
   * itself, because the band depends on the backdrop's magnification and that
   * changes with the window. A number fixed at build time would be right until
   * the first resize.
   */
  readonly ladder: { readonly span: number; readonly count: number } | null;
  /** A mark pinned to this one that never turns with it, or null. */
  readonly companion: Phaser.GameObjects.Sprite | null;
  /** Its own size, before the backdrop's magnification is applied. */
  readonly baseScale: number;
  readonly halfHeight: number;
}

interface StarSprite {
  readonly sprite: Phaser.GameObjects.Image;
  readonly speed: number;
  readonly baseAlpha: number;
  readonly twinkleAmplitude: number;
  twinklePhase: number;
  readonly twinkleRate: number;
}

interface MobView {
  readonly sprite: Phaser.GameObjects.Sprite;
  /**
   * A chave com que o corpo está desenhado AGORA (gate V28I.BN).
   *
   * Ela existe para a troca acontecer **uma vez**: enquanto a folha do mapa não
   * chega, a vista fica no marcador de lugar; no quadro em que a folha existe, o
   * corpo passa para ela, a animação daquele mapa começa e a escala é refeita
   * sobre a largura nova. Vazia significa "ainda no marcador".
   */
  bodyKey: string;
}

/**
 * Every sprite the continuous beam is drawn from.
 *
 * Built once and hidden when the beam stops, never destroyed and rebuilt: a
 * weapon that stays on for minutes cannot allocate per frame. Three concentric
 * strips, a fixed pool of travelling packets and one bloom at the impact.
 */
interface BeamViews {
  readonly strips: readonly Phaser.GameObjects.Image[];
  readonly pulses: readonly Phaser.GameObjects.Image[];
  readonly impact: Phaser.GameObjects.Image;
}

/**
 * DEV/E2E ONLY - one entry per time the scene took state from the server.
 *
 * `tickBefore` and `tickAfter` are read in the SAME synchronous handler, so
 * the pair describes the adoption itself and nothing else. That is what makes
 * "the run never goes backwards" checkable exactly, instead of inferred from
 * two samples with an interaction between them.
 */
interface SceneAdoption {
  /**
   * Which message brought the state in.
   *
   * `session` is the only one allowed to reset the clock: bootstrap, restart
   * and travel replace the run on purpose. Every other source is continuing
   * the same one.
   */
  readonly source: 'reconfigure' | 'pet' | 'correct' | 'session';
  /** True when a snapshot actually travelled and was restored from. */
  readonly restored: boolean;
  readonly tickBefore: number;
  readonly tickAfter: number;
  /** `performance.now()` when the handler ran, for ordering two tabs. */
  readonly at: number;
}

interface ProjectileView {
  readonly sprite: Phaser.GameObjects.Image;
  /**
   * The additive halo behind a missile, and null for every other shot.
   *
   * A missile's hull is drawn with a NORMAL blend - additive would make a dark
   * object transparent - so it cannot bloom on its own. The glow is a second
   * sprite underneath it, which is the same trick the 3D pets use with an
   * additive shell around every emissive volume.
   */
  readonly glow: Phaser.GameObjects.Image | null;
  /** Heading of the previous frame; the trail streak follows THIS angle. */
  lastAngle: number;
  /** Frames until the next trail streak, so the trail stays cheap. */
  trailCooldown: number;
  /**
   * The pet energy this shot LEFT THE GUN with, or null for the ship's.
   *
   * Frozen on purpose: swapping the pet's colour must change the next volley,
   * never the bullets already travelling.
   */
  readonly energy: PetEnergyColor | null;
  /** This shot's offset along an animated ramp, so a volley is not one colour. */
  readonly phase: number;
  /** The colour the bolt itself was baked in. */
  readonly color: number;
  /** True for an energy ball that will detonate: drawn round, and it spins. */
  readonly explosive: boolean;
  /**
   * Verdadeiro para o Núcleo Atômico (gate V28G.J).
   *
   * Lido do MESMO campo que a simulação usa para o fazer voar e detonar -
   * `atomicBomb` no projétil - e nunca inferido pela velocidade ou pelo raio:
   * uma cena que adivinhasse a entidade a partir de dois quadros erraria em
   * todos os que chegassem atrasados.
   */
  readonly bomb: boolean;
}

/**
 * Um míssil desenhado (gate V28G.L).
 *
 * Muito mais curto do que {@link ProjectileView}, e isso é o ponto: o míssil não
 * tem energia de pet, não tem cor congelada, não tem fase de rampa e não é uma
 * bola. Ele é um objeto branco com pontas vermelhas, e a única coisa que anima
 * dentro dele é a chama - que vive na textura.
 */
interface MissileView {
  readonly sprite: Phaser.GameObjects.Image;
  /**
   * A propulsão, presa à traseira real e viva enquanto o míssil viver.
   *
   * Um sprite e não um emitter: o contrato proíbe um emitter por míssil, e
   * trezentos deles seriam trezentos sistemas de partículas. Volta ao pool no
   * mesmo instante em que o corpo desaparece.
   */
  readonly flame: Phaser.GameObjects.Image;
  /** Milissegundos até à próxima marca de esteira. Mantém a esteira barata. */
  trailCooldownMs: number;
}

export class HuntScene extends Phaser.Scene {
  static readonly KEY = 'hunt';

  private readonly options: HuntSceneOptions;
  private sim!: HuntSimulation;

  private stars: StarSprite[] = [];
  private shipSprite: Phaser.GameObjects.Image | null = null;
  /**
   * The skin currently worn, or null (ADR 0041).
   *
   * Owned here and released by `applySkin(null, null)` and by the scene's own
   * shutdown, so a skin can never outlive the ship it decorates.
   */
  private skinRenderer: SkinRenderer | null = null;
  /*
   * How many times a renderer has been BUILT and TORN DOWN, and how many times
   * the hull swapped texture (gate V24D.1B).
   *
   * Counters rather than a boolean, because the question a swap test has to
   * answer is not 'did it change' but 'how many times'. Swapping between two
   * copies of the SAME skin must rebuild nothing - `skinContentChanged` sees
   * identical content and returns early - while changing DEFINITION must rebuild
   * exactly once. A test that counted only the visible result could not tell a
   * silent double rebuild from a single one, and a double rebuild drops every
   * particle in flight twice.
   *
   * They are three integers on a method that already runs. Nothing reads them
   * except the dev inspector, which no production bundle carries.
   */
  private skinRendererBuilds = 0;
  private skinRendererTeardowns = 0;
  private hullTextureSwaps = 0;
  /** The ship's last measured drift, in world units per second. For the census. */
  private lastSkinSpeed = 0;
  /** The direction it was last seen travelling in, in degrees. For the census. */
  private lastTravelDeg: number | null = null;
  /** The hull texture to go back to when a skin is taken off. */
  /** The skin sidecar, once per scene. Null when this build ships none. */
  private skinManifestPromise: Promise<SkinRuntimeManifest | null> | null = null;
  /** What the authority says is worn, already resolved to content. */
  private wornSkin: ResolvedSkinContent | null = null;
  /** Asset keys already reported missing, so a failure is said once. */
  private readonly reportedMissingSkins = new Set<string>();
  private baseHullTextureKey = 'ship';
  private lastSkinFrameMs: number | null = null;
  private lastSkinShipAt: { x: number; y: number } | null = null;
  /** One sprite per cannon of the equipped formation. */
  private cannonSprites: Phaser.GameObjects.Image[] = [];
  /**
   * Os dois canhões temporários dos Super Canhões (gate V28H.H).
   *
   * Um pool de DOIS, criados na primeira activação e reutilizados para sempre:
   * a habilidade liga e desliga a cada 14-35 segundos durante uma corrida
   * inteira, e criar-destruir dois `Image` a cada ciclo seria lixo por minuto
   * sem nada em troca. Ligados e desligados com `setVisible`.
   *
   * Sem canvas, sem cena e sem contexto WebGL novo: são dois `Image` na mesma
   * cena, com a MESMA textura que os canhões equipados já usam.
   */
  private superCannonSprites: Phaser.GameObjects.Image[] = [];
  /** O tween de montagem/desaparecimento em curso, para não acumular dois. */
  private superCannonTween: Phaser.Tweens.Tween | null = null;
  /** Se os dois estavam visíveis no quadro anterior, para detectar as bordas. */
  private superCannonsShown = false;
  /**
   * Texture key -> the manifest file it was loaded from.
   *
   * Phaser decodes images through blob URLs, so the texture itself no longer
   * remembers where it came from. Keeping the mapping lets an E2E test prove
   * the rarities really are drawing three different PNGs.
   */
  private readonly cannonSourceUrls = new Map<string, string>();
  private mobViews = new Map<number, MobView>();
  private bossView: Phaser.GameObjects.Sprite | null = null;
  /**
   * A chave com que o CHEFE está desenhado. Vazia enquanto está no marcador.
   *
   * O mesmo papel que `MobView.bodyKey` tem, e pela mesma razão.
   */
  private bossBodyKey = '';
  /** As folhas de mapa em voo. Uma chave aqui não é re-pedida. */
  private readonly pendingMapArt = new Set<string>();
  /**
   * O mapa cujas duas folhas esta cena tem carregadas. **Um par, e só um.**
   *
   * A caça carrega quatro texturas — casco, canhão, alienígena e chefe — e essa
   * regra não muda por haver vinte mapas: guardar os vinte pares seria uma
   * retenção que cresce a cada viagem e nunca desce. Quando o destino chega, o
   * par do mapa anterior é largado, e voltar atrás volta a pedi-lo — do disco do
   * navegador, na esmagadora maioria das vezes.
   */
  private loadedMapArt: number | null = null;
  /** Índices cuja arte já foi recusada. A recusa é dita uma vez por índice. */
  private readonly refusedMapArt = new Set<string>();
  private bossGlow: Phaser.GameObjects.Arc | null = null;
  private projectileViews = new Map<number, ProjectileView>();
  /** The beam's sprites, built on the first frame one exists. */
  private beamViews: BeamViews | null = null;
  /** How far through the 150 ms colour transition the beam is. */
  private beamFadeMs = PET_BEAM_COLOR_FADE_MS;
  /** The colour each beam part is currently drawn in, for the cross-fade. */
  private readonly beamColors = new Map<string, number>();
  /** Decays from 1 after each damage instance: a pulse, never a blink. */
  private beamFlash = 0;
  /** Where the 3D model says its emitter is, or null before one is loaded. */
  private petEmitter: { x: number; y: number } | null = null;
  /**
   * Health readouts, one persistent text per living entity.
   *
   * Keyed by entity id and reused frame after frame: creating text objects per
   * frame would allocate constantly in the middle of the render loop.
   */
  private healthTexts = new Map<number, Phaser.GameObjects.Text>();
  /**
   * Idle damage popups waiting to be used again.
   *
   * Every hit gets its own number - three shots landing together are three
   * numbers, twenty-one are twenty-one - so the objects are recycled rather
   * than created and destroyed. The pool grows to whatever the fight needs.
   */
  private readonly damagePool: Phaser.GameObjects.Text[] = [];
  private readonly liveDamageTexts = new Set<Phaser.GameObjects.Text>();
  /**
   * O pool dos números de DETONAÇÃO, separado do do dano (gate V28G.N).
   *
   * A mesma separação que o duelo faz, e pela mesma razão: o estilo é outro -
   * 2,4× o tamanho, peso 900, halo néon - e um texto emprestado teria de trocar
   * cinco propriedades à ida e repô-las todas à volta.
   */
  private readonly bleedTextPool: Phaser.GameObjects.Text[] = [];
  private readonly liveBleedTexts = new Set<Phaser.GameObjects.Text>();
  /** Spreads simultaneous hits apart so each number stays its own. */
  private damageSpreadIndex = 0;
  /**
   * **O `+TOTAL` da nave, e o total que ele está a mostrar** (decisão final).
   *
   * Um texto por nave, e a caça tem uma - portanto este mapa tem no máximo uma
   * entrada. Existe como mapa na mesma, e não como um par de campos soltos,
   * porque a regra é a mesma do duelo e uma regra só se escreve uma vez.
   */
  private readonly healTally = new HealTally<Phaser.GameObjects.Text>();

  /**
   * O brilho de uma CURA confirmada (gate V28G.U).
   *
   * Na caça há uma nave, portanto a lei de seleção escolhe sempre a própria e o
   * que sai é sempre um BRILHO - o feixe existe no sistema partilhado e nunca é
   * chamado daqui, porque não há segunda nave a quem ligar.
   */
  private vitalHeals: VitalHealFlashSystem | null = null;
  /**
   * **O pool da CURA, separado do do dano** (decisão final).
   *
   * Partilhavam um. Isso obrigava o dano a repor a cor a cada uso - um objeto
   * reciclado da cura vinha verde - e, pior, punha os dois debaixo de um teto
   * só: `24 + 6`, em que o 24 é herdado e não descreve o que a caça faz. O pool
   * chegou a 33 numa prova e a 54 noutra, e o número que estourou não era o da
   * cura.
   *
   * Separados, cada um responde por si. Este tem no máximo
   * {@link HEAL_TEXT_BUDGET} objetos, porque uma nave tem um texto e a caça tem
   * uma nave; o do dano fica exatamente como estava antes deste gate.
   */
  private readonly healPool: Phaser.GameObjects.Text[] = [];
  private readonly liveHealTexts = new Set<Phaser.GameObjects.Text>();
  private barsGraphics!: Phaser.GameObjects.Graphics;
  /** Elongated streaks behind projectiles. Never round. */
  private trailParticles!: Phaser.GameObjects.Particles.ParticleEmitter;
  /** Round bursts - impacts and kills only, separate from the projectile. */
  private impactParticles!: Phaser.GameObjects.Particles.ParticleEmitter;
  private damageFlash!: Phaser.GameObjects.Rectangle;

  private projectileColor = 0x2f9bff;
  private dashTexture = '';
  /**
   * The pet fires in ITS OWN ENERGY, and in nothing else.
   *
   * The colour is the equipped energy of the active copy - the same catalog
   * entry the 3D model lights its core, slits and spheres from - so a Void
   * Purple orb fires violet and an Anomaly orb fires fire. The CHASSIS never
   * reaches this: paint is one axis, energy is the other, and only energy is
   * allowed to touch what comes out of the guns.
   *
   * A texture per colour rather than a tint: the dash art carries a near-white
   * core that a multiply tint would flatten to the body colour, which is why
   * the ship's shots bake their colour too. `ensureDashTexture` caches by
   * colour, so a fixed palette costs exactly one texture for the session.
   */
  private petEnergy: PetEnergyColor | null = null;
  /** Used only when the equipped energy id is not in the catalog. */
  private static readonly PET_BULLET_FALLBACK = 0xff2a2a;
  /** Simulated time the SHIP last took damage, or null. Presentation only. */
  private lastShipDamageAtMs: number | null = null;
  /** The 2D sprite of the active pet. Null when none is active or in 3D mode. */
  private petSprite: Phaser.GameObjects.Image | null = null;
  /**
   * As invocações desenhadas, por ID (gate V28H.M).
   *
   * Por id e não por tipo: um jogador pode ter as duas ao mesmo tempo, e a
   * identidade de uma entidade é a que a simulação lhe deu. Uma que saia da
   * lista é DESTRUÍDA e não escondida - ao contrário de uma lâmina, ela não
   * volta: o id é irrepetível.
   */
  private readonly summonSprites = new Map<number, Phaser.GameObjects.Container>();
  /**
   * **As composições das duas cartas que desenham no casco** (gate V28H.N).
   *
   * Duas classes e não seis objectos soltos, e a razão é a que a primeira
   * tentativa provou: um rectângulo a girar e uma faixa a atravessar o ecrã
   * foram **reprovados pelo dono**. O que o contrato descreve são composições -
   * um círculo mágico com anéis, símbolos e uma lâmina de três camadas; uma
   * lança com núcleo, halo, bordas, corona, faíscas e dissipação - e uma
   * composição tem partes que se movem umas em relação às outras.
   *
   * Criadas à primeira activação e depois **escondidas**, nunca recriadas.
   */
  private cardCircle: MagicCircleFx | null = null;
  private cardLance: SolarLanceFx | null = null;
  /**
   * As bombas de carta em voo, e as duas explosões delas (gate V28H.T).
   *
   * Criada à primeira bomba e escondida depois. Uma caçada sem a Esfera nem o
   * Catolé não paga um único objecto por elas.
   */
  private cardBlasts: CardBlastFx | null = null;
  /**
   * **A composição do Bastião**, uma só, escondida em vez de destruída.
   *
   * Persistente pelos quatro segundos e não um clarão: o que a carta faz - os
   * tiros atravessarem - não se vê acontecer, portanto a única maneira de um
   * jogador saber que ela está a correr é uma marca que fica.
   *
   * Aqui esteve um anel azul, e o dono reprovou-o (gate V28H.U). O que ficou é a
   * mesma classe que o duelo desenha: estrelas a orbitar e a destacar-se, e o
   * casco a cintilar em branco, dourado e néon.
   */
  private bastionFx: BastionFx | null = null;
  /** Recoil offset per muzzle, decaying every frame. Presentation only. */
  private petRecoil = 0;
  /** Which representation is drawn. DEV can switch; production is 2D. */
  private petRenderMode: 'pet-2d' | 'pet-3d' = 'pet-2d';
  /** DEV flags captured at create time; reused when a test session restarts. */
  private bossFast = false;
  /**
   * DEV ONLY - multiplies attack speed to exercise very high cadence.
   *
   * Named `rapidFire`, not `laser`: it changes HOW OFTEN the ship shoots and
   * nothing about how a shot is drawn. Every one of them is still a discrete
   * projectile with its own damage number.
   */
  private rapidFire = false;
  /**
   * World rectangle the camera can actually see.
   *
   * With a uniform zoom the logical 1280x720 arena is centred, and whatever
   * room the container has left over shows world coordinates outside
   * `0..1280` / `0..720`. The starfield uses these bounds - not the arena
   * constants - so it reaches the real screen edges instead of stopping at an
   * invisible line.
   */
  private visibleBounds: { left: number; right: number; top: number; bottom: number } = {
    left: 0,
    right: ARENA_W,
    top: 0,
    bottom: ARENA_H,
  };
  /** The session this run belongs to, replaced only by an authorised one. */
  private session: HuntSessionMessage;
  /** Owns pause state and discards the frame that spans a hidden period. */
  private readonly clock = new HuntClock();
  /**
   * Whether React has been told about this run's death.
   *
   * Cleared wherever a fresh simulation is adopted, because the latch belongs to
   * the simulation that was REPLACED. There is no companion "pending" flag: the
   * announcement is not deferred to a frame, it rides the snapshot.
   */
  private deadNotified = false;
  /**
   * DEV/E2E ONLY - what every adoption did to the run's clock.
   *
   * Continuity is a statement about the instant a message is applied, not
   * about a window around it: a run that is rewound and then allowed to climb
   * again for a few seconds satisfies "later is bigger than earlier" while
   * having gone backwards. Both continuity specs used to be written that way
   * and both were satisfiable BY THE BUG.
   *
   * So the scene writes down the tick on either side of each adoption, in the
   * same synchronous handler, and a spec asserts on those pairs instead of on
   * two far-apart samples. Read-only, capped, and behind `DEV_TOOLS_ALLOWED`
   * so the whole thing leaves the production bundle.
   */
  private readonly adoptions: SceneAdoption[] = [];
  /** Which backdrop this map resolved to. Presentation, never simulation. */
  private mapBackground: MapBackground = STARFIELD_BACKGROUND;
  /**
   * How fast that backdrop drifts. Resolved with the art, not per frame.
   *
   * A ground scene sits at the near end of the depth stack and a nebula at the
   * far end, so the speed belongs to the map exactly as the picture does.
   */
  private backdropDrift = BACKGROUND_DRIFT_UNITS_PER_SECOND;
  /**
   * How much the backdrop tile is magnified to cover a wide window.
   *
   * One on a 16:9 canvas. Kept on the scene because the ground life has to
   * move and be sized by the same factor - see `fitBackdrop`.
   */
  private backdropCover = 1;
  /** Whether this map's backdrop is ground. Decides the starfield too. */
  /** Whether the shared star layers are drawn in front of this backdrop. */
  private starfield = true;
  /**
   * The animated things living on this map's ground.
   *
   * Rebuilt on travel and empty for most maps. Presentation only: nothing here
   * is an enemy, and none of it reaches the simulation.
   */
  private groundLife: GroundCreature[] = [];
  /**
   * Which map the current ground life belongs to.
   *
   * A load is asynchronous and the player may travel again while one is in
   * flight, so the callback checks this before building anything - the same
   * guard the backdrop's own load uses, and for the same reason.
   */
  private groundLifeMap = 0;
  /** The air over this map: embers, ash, smoke. Rebuilt on travel. */
  private airEmitters: AirEmitter[] = [];
  /** The map's shower, or null where nothing falls through its sky. */
  private shower: ShootingStarSpec | null = null;
  /**
   * One entry per streak, each on its own clock.
   *
   * Same shape as the bolts and for the same reason: independent timers drift
   * in and out of phase, so the sky never settles into a rhythm the player can
   * count along with.
   */
  private streaks: {
    readonly image: Phaser.GameObjects.Image;
    /** Milliseconds until this one crosses again. */
    waitMs: number;
    /** How far through its current crossing it is, or null between them. */
    ageMs: number | null;
    /** Where this crossing began, in arena units. */
    fromX: number;
    fromY: number;
  }[] = [];

  /** What runs across this map's floor, or null where the floor is inert. */
  private traces: EnergyTraceSpec | null = null;
  /**
   * One entry per pulse, each on its own clock and each carrying its own path.
   *
   * The images are allocated once, at the WIDEST path the spec allows, and a
   * shorter path simply leaves the spare ones hidden. Building a sprite per
   * turn would allocate in the middle of the effect that is being watched.
   */
  private pulses: {
    readonly bars: Phaser.GameObjects.Image[];
    readonly head: Phaser.GameObjects.Image;
    /** Corner to corner, in arena units. Index 0 is where the pulse starts. */
    path: { x: number; y: number }[];
    /** The length of each run, and the distance at which each one begins. */
    legLength: number[];
    legStart: number[];
    totalLength: number;
    /** Milliseconds until this one runs again. */
    waitMs: number;
    /** How far through its current run it is, or null between them. */
    ageMs: number | null;
    /**
     * How far the deck has scrolled under it since it started.
     *
     * The path is fixed to the FLOOR, not to the screen. A pulse that held
     * still while the plates moved past would read as an overlay drawn on the
     * glass rather than as current in the deck, which is the whole illusion.
     */
    driftY: number;
  }[] = [];

  /** The map's weather, or null where the sky is still. */
  private lightning: LightningSpec | null = null;
  /**
   * One entry per bolt the storm runs, each on its own clock.
   *
   * The sprites are built once and reused for every strike: a bolt that
   * allocated on firing would stutter the exact frame it is being looked at.
   */
  private bolts: {
    readonly image: Phaser.GameObjects.Image;
    /** Milliseconds until this one strikes again. */
    waitMs: number;
    /** How far through its current strike it is, or null between them. */
    ageMs: number | null;
  }[] = [];
  /** The ONE backdrop object. Its texture changes; it never does. */
  private backdrop: Phaser.GameObjects.TileSprite | null = null;
  /** Raw timestamp of the previous frame; null until the first one lands. */
  private lastFrameTimeMs: number | null = null;
  private snapshotTimerMs = 0;
  private readonly unsubscribers: (() => void)[] = [];

  constructor(options: HuntSceneOptions) {
    super(HuntScene.KEY);
    this.options = options;
    this.session = options.session;
  }

  /* ------------------------------------------------------------------ */
  /*  Loading                                                           */
  /* ------------------------------------------------------------------ */

  preload(): void {
    const { manifest } = this.options;
    // The EQUIPPED ship's art, by index, from the server-resolved loadout.
    // A hunt still loads exactly four textures; which ship it is now depends
    // on what the player actually owns.
    const equipped = this.options.session.loadout;
    const ship = findStaticAsset(manifest, `ship${equipped.ship.shipIndex}`);

    /*
     * **O alienígena e o chefe DO MAPA DESTA SESSÃO** (gate V28I.BN).
     *
     * Isto lia `MAP_CATALOG[0]` e carregava as duas folhas do mapa 1 sob as
     * chaves fixas `mob` e `boss`, em todos os mapas. O fundo, a ambiência, a
     * vida e a escala já acompanhavam o mapa; só as criaturas é que não.
     *
     * O `preload` do mundo aberto tem a mesma linha e uma desculpa que aqui
     * nunca valeu: lá ele corre antes de o servidor dizer o que há no quadrante.
     * Aqui a sessão autorizada chega no construtor, e `session.mapIndex` é
     * autoritativo antes do primeiro quadro — por isso a folha certa entra na
     * mesma fila que o casco, e a primeira sessão nunca vê um marcador.
     *
     * Sem `startNow`: o Phaser arranca este carregador a seguir ao `preload`.
     */
    this.requestMapBody('mob', this.options.session.mapIndex, false);
    this.requestMapBody('boss', this.options.session.mapIndex, false);
    this.load.image('ship', assetUrl(manifest, ship));

    // Each cannon rarity has its OWN art. A production build loads only the
    // EQUIPPED one; the dev build loads all three so the DEBUG selector can
    // switch instantly. The asset id always comes from the typed catalog -
    // never from a hard-coded file name or a scattered conditional.
    const rarities: CannonRarity[] =
      DEV_TOOLS_ALLOWED && this.options.debug
        ? [...new Set<CannonRarity>([...CANNON_RARITIES, equipped.cannon.rarity])]
        : [equipped.cannon.rarity];
    for (const rarity of rarities) {
      const assetId = cannonAssetId(rarity);
      const key = cannonTextureKey(rarity);
      const url = assetUrl(manifest, findStaticAsset(manifest, assetId));
      this.cannonSourceUrls.set(key, url);
      /*
       * Pelo registo do jogo inteiro (gate V28I.CF.3): o mundo aberto e o duelo
       * pedem as mesmas chaves de canhão, e podem pedi-las antes de este
       * `preload` acabar. Sem `start`: o Phaser arranca este carregador sozinho.
       */
      requestSharedTexture(this, key, url, undefined, false);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Scene construction                                                */
  /* ------------------------------------------------------------------ */

  create(): void {
    const { bridge } = this.options;
    // Every dev affordance is gated by the BUILD first. In a production build
    // `DEV_TOOLS_ALLOWED` is a literal `false`, so these branches are stripped
    // and no option passed in from anywhere can revive them.
    const devToolsOn = DEV_TOOLS_ALLOWED && this.options.debug;
    const bossFast = devToolsOn && this.options.bossFast;
    const rapidFire = devToolsOn && this.options.rapidFire;
    this.projectileColor = Phaser.Display.Color.HexStringToColor(
      CANNON_CATALOG[this.options.session.loadout.cannon.rarity].projectileColor,
    ).color;
    this.bossFast = bossFast;
    this.rapidFire = rapidFire;
    // Which energy the active pet wears. Its bullets, wake, muzzle flashes and
    // impacts all come from this and from nothing else.
    this.petEnergy = findEnergyColor(this.options.session.activePet?.energyId ?? '') ?? null;

    // The loadout the server resolved from the account's real inventory.
    this.sim = this.buildSimulation(this.options.session);
    /*
     * A run that was ALREADY dead when this scene opened still needs its death
     * screen, and it gets one from the first snapshot this scene publishes -
     * see the `pushSnapshot` at the end of `create`.
     *
     * The screen is raised by the ship-death EVENT, and an event only exists
     * for a death that happens while somebody is watching. Adopting a snapshot
     * whose ship is already down produces none, so the player arrived at a top
     * bar reading "Abatida" with nothing on screen to press.
     *
     * Announcing it here, in the middle of `create`, was too early: the bar
     * still read "Iniciando" because no snapshot had reached React yet, and the
     * overlay came up over a hunt that had not finished starting. Deferring it
     * to the first frame instead was this defect's other half. Publishing the
     * death WITH the snapshot answers both: the bar and the overlay are filled
     * by the same batch, and there is no frame in between for either to be
     * alone on screen.
     */

    // Fit the camera before anything is placed, so the starfield is built for
    // the real visible rectangle rather than the logical arena.
    this.applyCameraFit();
    this.createStars();
    /*
     * The map the scene opened on. `adoptSession` covers every later change,
     * but a run that never travels would otherwise play on the clear colour -
     * which is what the first screenshot of this feature showed.
     */
    this.applyMapBackground(this.session.mapIndex);
    this.createShip();
    /*
     * And the skin the server says is worn, on the very first frame.
     *
     * `createShip` places the base hull, which is right for an account wearing
     * nothing and wrong for everybody else: without this, reloading with a skin
     * equipped rebuilt the ship undressed and it stayed that way until the next
     * reconfigure. The bootstrap that opened this scene already carries the
     * authoritative `loadout.skin`; this is the scene agreeing with it.
     *
     * Only the skin, not the whole of `applyEquippedArt`: `preload` fetched the
     * hull and the cannon this session needs and `createShip` has just used
     * them, so re-requesting them here would be work with no picture behind it.
     * The PNG is a different case - a skin's art is never preloaded, because
     * which one is worn is not known until the session is.
     */
    void this.applyWornSkin(this.session.loadout.skin ?? null);
    /*
     * O marcador de lugar, e o registo de qual par está carregado.
     *
     * `createAnimations` vivia aqui e criava `mob-idle` e `boss-idle` a partir
     * de `alien1` e `boss1` — duas animações fixas para vinte mapas. Elas saíram
     * com as chaves fixas: a animação de cada mapa nasce com a folha dele, no
     * instante em que o carregador a entrega, com a cadência dos metadados
     * daquele recurso.
     */
    ensureHuntMapBodyPendingTexture(this);
    this.loadedMapArt = this.session.mapIndex;
    this.createFxObjects();

    this.scale.on(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    this.registerBridge(bridge);
    /*
     * O sistema do corte, criado com a cena e destruido com ela.
     *
     * Ele nao sabe o que originou o efeito: recebe {@link CycloneCut} e mais
     * nada. A cena tambem nao - ela reencaminha um evento que a autoridade
     * confirmou, e e essa a fronteira inteira.
     */
    this.cycloneCuts = new CycloneCutSystem(this as never);
    /*
     * E o sistema da CURA, pela mesma fronteira exata (gate V28G.U).
     *
     * Ele também não sabe o que a originou: recebe uma silhueta e uma
     * transformação, e desenha. As profundidades põem o brilho à volta da nave -
     * o casco está em 30 - sem tapar os projéteis nem os números.
     */
    this.vitalHeals = new VitalHealFlashSystem(this as never, 30, 31);
    /*
     * E o sistema da EXPLOSÃO, pela mesma fronteira exata (gate V28G.J).
     *
     * Ele também não sabe o que a originou: recebe um centro, um raio e um id de
     * evento. A onda de choque é escalada a partir do MESMO anel que a explosão
     * do pet já usa - uma segunda textura idêntica seria a mesma imagem paga
     * duas vezes.
     */
    this.atomicBlasts = new AtomicBlastSystem(this as never, {
      depth: 26,
      waveTexture: ensureShockwaveTexture(this),
      blend: { add: Phaser.BlendModes.ADD, normal: Phaser.BlendModes.NORMAL },
    });
    /*
     * E o do MÍSSIL, que é um sistema à parte (gate V28G.L).
     *
     * Não recebe textura de onda porque não tem camada de onda: o míssil não tem
     * área, e a defesa estrutural contra desenhar um círculo que pareça uma zona
     * de dano é não existir onda nenhuma para ligar. As quatro texturas de fogo
     * são as mesmas, reutilizadas do gestor de texturas.
     */
    this.missileBlasts = new MissileBlastSystem(this as never, {
      depth: 26,
      blend: { add: Phaser.BlendModes.ADD, normal: Phaser.BlendModes.NORMAL },
    });
    /*
     * E o sistema das MARCAS, pela mesma fronteira exata (gate V28G.M).
     *
     * Ele também não sabe o que as originou: recebe uma lista de alvos com uma
     * posição, um diâmetro desenhado e as marcas que cada um carrega. A
     * profundidade é a das barras de vida mais uma, porque as gotas ficam
     * imediatamente acima delas e nunca por baixo.
     */
    /*
     * E o sistema das SERRAS, pela mesma fronteira exata (gate V28G.P).
     *
     * Ele também não sabe o que as originou: recebe uma posição, um passo
     * angular e um raio por lâmina, e mais nada. Não lê inventário, não lê
     * perfil e não sabe que existe uma joia chamada Diamante Serrilhado.
     *
     * A profundidade fica ABAIXO das barras de vida e dos números de dano - as
     * lâminas são objetos do mundo, e nada do que o jogador precisa de ler pode
     * passar por trás delas.
     */
    this.sawBlades = new SawBladeSystem(this as never, 24);
    this.poisonMarks = new PoisonMarkSystem(this as never, 42);
    /* E os do sangramento, na mesma linha e pelo mesmo motivo (V28G.N). */
    this.bleedMarks = new BleedMarkSystem(this, 43, 'bleed-hunt');
    this.bleedBursts = new BleedBurstSystem(
      this as never,
      44,
      BLEED_BURST_POOL_CEILING,
      bleedAudioFrom(this.sound as { readonly context?: unknown }),
      (finale) => this.showBleedDamage(finale.damage, finale.x, finale.y),
    );

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      for (const unsubscribe of this.unsubscribers) {
        unsubscribe();
      }
      this.unsubscribers.length = 0;
      this.scale.off(Phaser.Scale.Events.RESIZE, this.handleResize, this);
      // Damage popups own tweens; leaving them running past a shutdown would
      // keep the scene referenced and fire callbacks into a dead scene.
      /*
       * As invocações de carta são desta corrida (gate V28H.M): um contentor
       * deixado para trás manteria a cena referenciada depois de ela morrer.
       */
      for (const container of this.summonSprites.values()) {
        container.destroy(true);
      }
      this.summonSprites.clear();
      /* E as duas composições de carta, que são desta corrida. */
      this.cardCircle?.destroy();
      this.cardCircle = null;
      this.cardLance?.destroy();
      this.cardLance = null;
      /* E as bombas e o anel do Bastião, que são desta corrida (V28H.T). */
      this.cardBlasts?.destroy();
      this.cardBlasts = null;
      this.bastionFx?.destroy();
      this.bastionFx = null;
      this.clearDamageTexts();
      this.clearBleedTexts();
      for (const text of this.bleedTextPool) {
        text.destroy();
      }
      this.bleedTextPool.length = 0;
      this.damagePool.length = 0;
      this.clearHealTexts();
      for (const text of this.healPool) {
        text.destroy();
      }
      this.healPool.length = 0;
      // A skin owns emitters and sprites; leaving them would keep the scene
      // referenced and go on emitting into a dead arena.
      this.skinRenderer?.destroy();
      this.skinRenderer = null;
      /* Sprites e tweens do corte sao deste sistema e morrem com a cena. */
      this.cycloneCuts?.destroy();
      this.cycloneCuts = null;
      /* E o brilho da cura, pela mesma razão exata (gate V28G.U). */
      this.vitalHeals?.destroy();
      this.vitalHeals = null;
      /* Idem para a explosão: sprites e tweens dela morrem com a cena. */
      this.atomicBlasts?.destroy();
      this.atomicBlasts = null;
      /* E o fogo do míssil, com os seus sprites, tweens e trilha (V28G.L). */
      this.missileBlasts?.destroy();
      this.missileBlasts = null;
      /*
       * E os dois canhões temporários, com o tween que os monta (V28H.H).
       *
       * Um tween a correr depois do shutdown mantém a cena referenciada e
       * dispara o seu onComplete contra objectos já destruídos.
       */
      this.clearSuperCannons();
      /*
       * E o par de folhas deste mapa (gate V28I.BN).
       *
       * O gestor de texturas é do JOGO e não da cena, portanto uma folha
       * carregada aqui sobreviveria a um reinício da cena e a uma saída para o
       * mundo aberto. Um par de cada vez é a regra, e ela vale também para zero.
       */
      if (this.loadedMapArt !== null) {
        /*
         * As vistas primeiro, e a folha depois. Uma textura removida por baixo
         * de um sprite que ainda existe é um quadro sem imagem por trás — e
         * ainda que esta cena já não volte a desenhar, a ordem certa não custa
         * nada e não depende de quando o motor destrói a lista de exibição.
         */
        for (const view of this.mobViews.values()) {
          view.sprite.destroy();
        }
        this.mobViews.clear();
        this.bossView?.destroy();
        this.bossView = null;
        this.bossBodyKey = '';
        for (const kind of ['mob', 'boss'] as const) {
          releaseHuntMapBody({
            scene: this,
            kind,
            mapIndex: this.loadedMapArt,
            pending: this.pendingMapArt,
          });
        }
        this.loadedMapArt = null;
      }
      /* E as marcas: um objeto gráfico por alvo, todos deste sistema (V28G.M). */
      /* E as lâminas: um sprite por serra, todos deste sistema (V28G.P). */
      this.sawBlades?.destroy();
      this.sawBlades = null;
      this.poisonMarks?.destroy();
      this.poisonMarks = null;
      this.bleedMarks?.destroy();
      this.bleedMarks = null;
      this.bleedBursts?.destroy();
      this.bleedBursts = null;
      this.clearMissileViews();
      for (const sprite of [...this.missileTrailPool, ...this.missileFlamePool]) {
        sprite.destroy();
      }
      this.missileTrailPool.length = 0;
      this.missileFlamePool.length = 0;
    });

    if (devToolsOn) {
      // Diagnostic hook for E2E only; never used by game logic and never
      // present in a production bundle.
      (window as unknown as Record<string, unknown>).__KENO_DEV__ = {
        texturesLoaded: [
          'ship',
          cannonTextureKey(this.options.session.loadout.cannon.rarity),
          /* As duas folhas DO MAPA desta sessão, e não as fixas (V28I.BN). */
          huntMapBodyTextureKey('mob', this.options.session.mapIndex),
          huntMapBodyTextureKey('boss', this.options.session.mapIndex),
        ].every((key) => this.textures.exists(key)),
        /**
         * The deterministic snapshot, for continuity assertions.
         *
         * A spec that wants to prove "the field was not cleared" needs the
         * SIMULATION's own answer about which mobs are alive - pixels cannot
         * distinguish a surviving mob from a freshly spawned one wearing the
         * same sprite. This is the state the checkpoint digest hashes.
         */
        inspectSnapshot: () => this.sim.exportSnapshot(),
        /*
         * Whether the 2D pet sprite is on screen, and in which mode.
         *
         * The scene keeps a 2D sprite for the WebGL-less fallback. When nothing
         * told it that the 3D overlay was live it drew BOTH, and a spec cannot
         * tell two overlapping pets apart from pixels alone - the 3D one is on
         * a separate canvas. This reports the fact directly.
         */
        inspectPetRender: () => ({
          mode: this.petRenderMode,
          spriteVisible: this.petSprite?.visible === true,
        }),
        /**
         * Os cortes CONFIRMADOS que esta corrida ja anunciou.
         *
         * Existe porque uma prova visual tem de registar QUE evento fotografou -
         * o id, o efeito e a posicao autoritativa - e um pixel nao diz nenhuma
         * das tres coisas. A cena consome o evento e nao o guarda; isto guarda
         * uma copia do que foi consumido, e mais nada.
         *
         * DEV ONLY: vive dentro do mesmo `__KENO_DEV__` que a porta de
         * ferramentas ja fecha, e `production-lockdown.spec.ts` prova a sua
         * ausencia no `dist` a serio.
         */
        inspectProcs: () => this.procLog.map((entry) => ({ ...entry })),
        inspectProjectiles: () => this.inspectProjectiles(),
        inspectCombatText: () => this.inspectCombatText(),
        /**
         * Every adoption this scene has made, with the clock on either side.
         *
         * A continuity spec used to sample the clock before an interaction and
         * again after it, which a rewind survives as long as the run climbs
         * back over the gap - both specs were satisfiable by the bug they were
         * written against. This is the pair measured across the adoption
         * itself, in the handler that performed it, so `tickAfter < tickBefore`
         * is a rewind and nothing else.
         *
         * A copy, so a spec cannot mutate the scene's own record.
         */
        inspectAdoptions: (): readonly SceneAdoption[] => [...this.adoptions],
        /**
         * What the ship is WEARING, and what that costs (gate V23.2A.1).
         *
         * A browser spec cannot tell a Silver hull from a Golden one by
         * counting pixels alone, and it certainly cannot tell "the renderer was
         * replaced" from "a second one was added". This reports the facts: the
         * content resolved from the authoritative loadout, the texture the hull
         * sprite is actually drawing, the hull it would fall back to, and the
         * emitter and particle counts the census needs.
         *
         * Read-only and DEV-gated like everything beside it. It changes
         * nothing, and no product code path reads it - the player's own action
         * is a click on a real button, answered by the real route.
         */
        /**
         * Where the hull is drawn ON THE CANVAS, and how big, normalised 0-1.
         *
         * The ship drifts continuously, so a visual gate that framed a fixed
         * rectangle would compare two skins at two different subjects - and
         * world coordinates alone are not enough to fix that, because the
         * camera is fitted to the visible rectangle and the canvas is scaled
         * again by CSS. Normalising against `worldView` here leaves the caller
         * one multiplication by the element's own box, with no assumption about
         * either transform.
         *
         * The drawn SIZE travels too, so a crop can be sized to the hull rather
         * than guessed: a 320 px window on a 1920 px frame is mostly starfield.
         */
        inspectShipScreen: () => {
          const sprite = this.shipSprite;
          const view = this.cameras.main.worldView;
          if (!sprite || view.width === 0 || view.height === 0) {
            return null;
          }
          return {
            u: (sprite.x - view.x) / view.width,
            v: (sprite.y - view.y) / view.height,
            w: sprite.displayWidth / view.width,
            h: sprite.displayHeight / view.height,
          };
        },
        /**
         * What this scene is HOLDING, for a leak census.
         *
         * A skin owns emitters, sprites and textures, and the question a census
         * asks is whether taking it off gives them back. Counting from the
         * outside cannot answer that: the DOM sees one canvas either way. These
         * are the scene's own registries.
         */
        inspectResources: () => ({
          textures: this.textures.getTextureKeys().length,
          scenes: this.scene.manager.getScenes(true).length,
          tweens: this.tweens.getTweens().length,
          displayObjects: this.children.length,
        }),
        /**
         * Where the hull is DRAWN, beside where the ship authoritatively IS.
         *
         * The pair is the point. A skin cannot move the ship - the simulation
         * owns `shipPosition` and nothing here can write it - so the only way
         * a hull can look wrong is by being drawn somewhere the ship is not,
         * and the only way to see that is to report both numbers from the same
         * frame and let the spec subtract them.
         *
         * Read-only, and deliberately raw: origin, scale and rotation exactly
         * as the sprite holds them, plus the texture's own pixel size, so the
         * caller can place the ART's centre itself rather than trust a number
         * this scene computed. A probe that did the arithmetic would be
         * agreeing with the code it is meant to check.
         */
        inspectHullGeometry: () => {
          const sprite = this.shipSprite;
          if (!sprite) {
            return null;
          }
          const source = sprite.texture.getSourceImage() as { width: number; height: number };
          const camera = this.cameras.main;
          return {
            authoritative: this.sim.shipPosition(),
            facing: this.sim.shipFacing(),
            drawn: {
              x: sprite.x,
              y: sprite.y,
              rotation: sprite.rotation,
              originX: sprite.originX,
              originY: sprite.originY,
              scaleX: sprite.scaleX,
              scaleY: sprite.scaleY,
              displayWidth: sprite.displayWidth,
              displayHeight: sprite.displayHeight,
            },
            texture: {
              key: sprite.texture.key,
              width: source?.width ?? 0,
              height: source?.height ?? 0,
            },
            /* The plan's own numbers, so a capture records what produced it. */
            plan: this.wornSkin
              ? {
                  definitionId: this.wornSkin.definitionId,
                  pivot: this.wornSkin.plan.pivot,
                  hullCenter: this.wornSkin.plan.hullCenter,
                  baseScale: this.wornSkin.plan.baseScale,
                }
              : null,
            camera: { zoom: camera.zoom, width: this.scale.width, height: this.scale.height },
          };
        },
        inspectSkin: () => ({
          definitionId: this.wornSkin?.definitionId ?? null,
          textureKey: this.wornSkin?.textureKey ?? null,
          hullTexture: this.shipSprite?.texture.key ?? null,
          baseHullTextureKey: this.baseHullTextureKey,
          /*
           * The three counters, so a swap test can say HOW MANY rather than
           * whether. Exposed only here, which is the object no production
           * bundle carries.
           */
          rendererBuilds: this.skinRendererBuilds,
          rendererTeardowns: this.skinRendererTeardowns,
          hullTextureSwaps: this.hullTextureSwaps,
          ...this.skinCensus(),
        }),
        dashSourceImage: () => this.textures.get(this.dashTexture).getSourceImage(),
      };
    }

    /*
     * The first thing React hears about this run, and the death that goes with
     * it when the run opened dead.
     *
     * Before `game:ready` so the arena is announced with a top bar that already
     * has numbers in it rather than "Iniciando".
     */
    this.pushSnapshot();

    bridge.sendToUi('game:ready', {
      rendererWidth: this.scale.width,
      rendererHeight: this.scale.height,
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Responsive camera                                                 */
  /* ------------------------------------------------------------------ */

  /**
   * Fits the logical arena into whatever size the container has.
   *
   * A single uniform zoom is used for both axes, so nothing is ever stretched:
   * the ship, cannons, mobs, boss and projectiles keep their proportions. The
   * zoom is the SMALLER of the two ratios, which guarantees the whole
   * 1280x720 play area stays visible; the surplus becomes extra visible space
   * on the wider axis.
   */
  private applyCameraFit(): void {
    const width = Math.max(1, this.scale.width);
    const height = Math.max(1, this.scale.height);
    const zoom = Math.min(width / ARENA_W, height / ARENA_H);
    const camera = this.cameras.main;
    camera.setSize(width, height);
    camera.setZoom(zoom);
    // Centre the logical arena; extra room falls symmetrically on both sides.
    camera.centerOn(ARENA_W / 2, ARENA_H / 2);

    // Computed rather than read from `camera.worldView`, which only refreshes
    // during the render pass and would be stale here.
    const halfWidth = width / zoom / 2;
    const halfHeight = height / zoom / 2;
    this.visibleBounds = {
      left: ARENA_W / 2 - halfWidth,
      right: ARENA_W / 2 + halfWidth,
      top: ARENA_H / 2 - halfHeight,
      bottom: ARENA_H / 2 + halfHeight,
    };

    if (this.damageFlash) {
      // The damage flash covers everything the player can see, not just the
      // logical arena.
      this.damageFlash.setPosition(ARENA_W / 2, ARENA_H / 2);
      this.damageFlash.setSize(halfWidth * 2, halfHeight * 2);
    }
  }

  /**
   * Re-fits after a window resize.
   *
   * Only the camera and the star positions change - the Phaser game, the
   * canvas and the simulation are all untouched, so a resize never restarts
   * the hunt.
   */
  private handleResize(): void {
    this.applyCameraFit();
    this.redistributeStars();
    this.fitBackdrop();
  }

  /** Spreads the pooled stars across the current visible rectangle. */
  private redistributeStars(): void {
    const { left, right, top, bottom } = this.visibleBounds;
    for (const star of this.stars) {
      star.sprite.setPosition(
        Phaser.Math.FloatBetween(left, right),
        Phaser.Math.FloatBetween(top, bottom),
      );
    }
  }

  /**
   * Builds the run the SERVER authorised.
   *
   * The seed, the map and the progression all come from the bootstrap
   * response, and the deterministic snapshot is restored as-is - so after a
   * reload the browser continues exactly the run the server last validated
   * instead of starting a private one.
   */
  private buildSimulation(session: HuntSessionMessage): HuntSimulation {
    const config = {
      mapIndex: session.mapIndex,
      seed: simulationSeed(BigInt(session.seed)),
      // Straight from the server-resolved loadout. The client has no other
      // source of equipment and no way to choose one: a tampered value here
      // would diverge from the server's re-simulation and be corrected at the
      // next checkpoint, having earned nothing.
      shipIndex: session.loadout.ship.shipIndex,
      shipLevel: session.loadout.ship.level,
      cannonRarity: session.loadout.cannon.rarity,
      cannonLevel: session.loadout.cannon.level,
      // The artifact passives the server froze into this session, in the same
      // exact integer units it re-simulates with. Not the account's current
      // artifacts: the run is bound to what it was created with.
      artifactEffects: session.artifactEffects,
      // The equipment totals the server froze into this session, in the same
      // exact integer units it re-simulates with. Without these the ship in
      // the arena fought with none of its components.
      equipmentTotals: session.equipmentTotals,
      /*
       * **As joias que o servidor congelou nesta sessão.**
       *
       * Faltavam, e a falta era invisível: nenhuma conta consegue obter uma
       * joia - `jewel-acquisition.test.ts` prova que nada em produção concede
       * uma - portanto nenhuma sessão real chegou a ter um perfil não vazio, e
       * a linha em falta nunca teve como se notar.
       *
       * O que ela custava é maior do que um efeito por desenhar. O SERVIDOR
       * re-simula a janela com o perfil (`hunt/service.ts` passa-o para a sua
       * própria configuração), e este lado corria sem ele: a Chama não somava,
       * o Pulso não encurtava a recarga, o Ciclone não sorteava, e o digest de
       * cada checkpoint divergiria - uma correção a cada trinta segundos numa
       * conta que tivesse joias.
       *
       * Encontrada pela folha visual da caça: a sessão congelava
       * `jewel_blade_cut x6`, as texturas do corte nasciam, e em sessenta
       * segundos de combate real a loteria não sorteou uma única vez - porque
       * a simulação do browser não sabia que a joia existia.
       */
      jewels: session.loadout.jewels,
      // The pet the SERVER froze into this session. Never the account's
      // current one: the run is bound to what it was created with.
      ...(session.activePet
        ? {
            pet: {
              definitionId: session.activePet.definitionId,
              evolution: session.activePet.evolution,
            },
          }
        : {}),
      initialProgression: session.progression,
      /*
       * The boss belongs to the SERVER, and this copy knows it.
       *
       * Without the flag this simulation would re-arm its own countdown from
       * the public seed the moment a boss died, invent a delay nobody
       * persisted, and diverge until the next checkpoint corrected it. With
       * it, the run simply goes quiet after a defeat and waits for the
       * countdown the settlement hands back.
       *
       * A fast-boss debug run is deliberately excluded: it is non-economic and
       * has no encounter behind it.
       */
      ...(this.bossFast ? {} : { serverManagedBoss: true }),
      ...(this.bossFast ? { debugBossDelaySeconds: 10 } : {}),
      ...(this.rapidFire ? { devAttackSpeedBoost: 40 } : {}),
    };
    // The fast-boss shortcut overrides a countdown that lives INSIDE the
    // snapshot, so restoring would silently reinstate the real 3-16 min roll.
    // A debug run is non-economic anyway, so it starts a fresh local run.
    return this.bossFast
      ? new HuntSimulation(config)
      : HuntSimulation.restore(config, session.snapshot);
  }

  /** Swaps in a new authorised session without recreating the scene. */
  /**
   * Swaps in a new authorised session inside the running scene.
   *
   * Order matters, and it is the whole point of this method. The replacement
   * simulation is built FIRST, while nothing has been touched: if the snapshot
   * cannot be restored, the throw happens before a single sprite is destroyed
   * and the player keeps playing the run they were already on. Only once the
   * new simulation exists do the old views come down.
   *
   * A failure here used to be invisible - the exception travelled back through
   * the bridge into a fetch handler, was reported as a network wobble, and the
   * arena was left half torn down, which is a black rectangle to the player.
   * It now says so out loud, so a test sees it and so does the console.
   */
  private adoptSession(session: HuntSessionMessage): void {
    let replacement: HuntSimulation;
    try {
      replacement = this.buildSimulation(session);
    } catch (error) {
      this.reportSceneFailure('adopt-session', error);
      return;
    }

    try {
      this.session = session;
      this.sim = replacement;
      /*
       * ALL the art the loadout decides, through the one method that owns it.
       *
       * This used to set the projectile tint and the dash texture by hand and
       * stop there, which meant a session adopted here kept whatever hull and
       * whatever SKIN the previous run was wearing. Two ways to see it: reload
       * with a skin on and the ship came back undressed, because the bootstrap
       * arrives through this path and nothing here asked about `loadout.skin`;
       * and swap accounts, and the arena went on drawing the previous account's
       * cosmetic - including its emitters - until something else happened to
       * change the loadout.
       *
       * `applyEquippedArt` does the tint, the dash texture, the hull, the
       * battery and the worn skin, and a null skin is an explicit undress
       * rather than an omission. It runs before the views are rebuilt below so
       * the first frame after an adoption is already correct.
       */
      this.applyEquippedArt(session.loadout);
      this.deadNotified = false;
      // The backdrop belongs to the DESTINATION now. Resolved through the one
      // function that answers it, so the day a map gets its own art this line
      // is already asking the right question.
      this.applyMapBackground(session.mapIndex);
      this.clearEntityViews();
      /*
       * **E as duas criaturas do DESTINO** (gate V28I.BN).
       *
       * Depois de `clearEntityViews`, e é a ordem que torna a libertação segura:
       * nesse instante nenhum sprite desenha a folha do mapa anterior, portanto
       * ela pode ser largada sem deixar um corpo a apontar para uma textura que
       * já não existe. O destino é pedido primeiro, para uma viagem de ida e
       * volta ao mesmo mapa não largar o que acabou de pedir.
       *
       * Nenhum quadro mostra `alien1` no lugar de `alienN`: as vistas foram
       * destruídas aqui, e as que nascerem antes de a folha chegar nascem no
       * marcador de lugar, que é legivelmente "ainda não carregou".
       */
      this.adoptMapArt(session.mapIndex);
      this.rebuildCannonSprites();
      this.syncShipOrientation();
      this.options.bridge.sendToUi('hunt:restarted', {});
      /*
       * And raise the death screen again when the run being adopted is DEAD -
       * in the SAME batch, which is what `pushSnapshot` now guarantees.
       *
       * `hunt:restarted` above tells React the run was replaced, and React
       * answers by clearing the death overlay. That is right for the caller it
       * was written for - a restart the player asked for - and wrong for the
       * other one: coming back from a PvP match the hunt is re-authorised
       * through this same path, and the server hands back the session it still
       * holds, ship included, dead if it died while the player was in a lobby.
       *
       * Adopting a snapshot produces no `ship-death` EVENT, so nothing raised
       * the screen again. Gate V24D.4 answered that by asking for the death on
       * the next FRAME, and the interval between the two was a legal render of
       * this product: dead run, no death screen, and the offline report taking
       * the place the death should have had. Measured sixteen times in one
       * three-cycle reproduction - 131 ms at the shortest, 466 ms at the
       * longest - and it is what left `Continuar` mounting and unmounting under
       * the soak's clicks.
       *
       * `pushSnapshot` publishes the death first and the snapshot second, both
       * inside this call, so React renders once and never sees a dead run
       * without the way out of it. Nothing here is conditional: the decision is
       * silent unless the phase really is `dead`, so a live adoption, a restart
       * and a travel change nothing at all.
       */
      this.pushSnapshot();
    } catch (error) {
      // The views are the recoverable half: rebuilding them is exactly what
      // the next frame does anyway, so the run continues rather than freezing.
      this.reportSceneFailure('adopt-views', error);
    }
  }

  /**
   * Continues the SAME session from a simulation the server produced.
   *
   * The other three doors an authoritative run comes through: a checkpoint
   * whose digest diverged (`hunt:correct`), an equip, a merge, a spent point or
   * a skin change (`hunt:reconfigure`), and setting the active pet
   * (`hunt:pet`). None of them is a new session, so none announces a restart -
   * what they share with `adoptSession` is the part that was missing from all
   * three.
   *
   * ## Why the events are dropped
   *
   * They belong to a window this player already watched. The server drains its
   * own after every re-simulation slice, so this is normally empty; discarding
   * it anyway guarantees that a correction can never fire 30 s of hits and
   * kills in one frame.
   *
   * ## And why the death has to be asked for again (gate V24D.4)
   *
   * Because dropping those events drops the only thing that would have raised
   * the death screen. The server re-simulates the window it credits and a ship
   * can die inside that re-simulation - nobody is watching a checkpoint, and a
   * PvP lobby plus six wardrobes is minutes of exactly that. The authoritative
   * snapshot then comes back with `phase: 'dead'` and an empty mailbox, so the
   * browser went from a live run to a dead one with nothing to announce it: the
   * top bar read "Abatida", no overlay was raised, and there was no restart to
   * press. A player in that state has no move at all.
   *
   * The latch belongs to the simulation that was REPLACED, which is why it is
   * cleared here rather than left to the new run to notice - `announceDeath`
   * already documents that rule, and these are the other three places it has to
   * hold. It stays silent unless the phase really is `dead`, so a live
   * correction, a live equip, a live merge and a live pet change nothing at
   * all.
   */
  private adoptAuthoritativeSimulation(next: HuntSimulation): void {
    this.sim = next;
    this.sim.drainEvents();
    /*
     * Os cortes no ecra pertenciam a linha temporal que acabou de ser
     * descartada. Deixa-los a desvanecer seria mostrar um golpe de uma corrida
     * que o servidor corrigiu - e o `drainEvents` acima ja garante que o
     * documento novo nao os torna a anunciar.
     */
    this.cycloneCuts?.clear();
    /* E as explosões, pela mesma razão: pertenciam à linha temporal descartada. */
    this.atomicBlasts?.clear();
    /* E o fogo e os mísseis do míssil, pela mesma razão exata (V28G.L). */
    this.missileBlasts?.clear();
    this.clearMissileViews();
    /*
     * E as marcas: os stacks que as produziam pertenciam à linha temporal
     * descartada. A simulação nova traz os seus, e o próximo quadro desenha-os -
     * incluindo os MESMOS, se o servidor confirmou que eram esses.
     */
    this.poisonMarks?.clear();
    this.bleedMarks?.clear();
    this.bleedBursts?.clear();
    /*
     * E as lâminas: a órbita que estava no ecrã era a da linha temporal que
     * acabou de ser descartada. A simulação nova traz a sua fase, e o próximo
     * quadro desenha-a - no ângulo em que o servidor diz que ela está.
     */
    this.sawBlades?.clear();
    this.deadNotified = false;
  }

  /**
   * Points the backdrop at a map.
   *
   * Every map answers `starfield` today, which is not a placeholder: it is the
   * look the game ships with, and a map without its own art simply keeps it.
   * The call is here anyway rather than "later", because a resolver nothing
   * consults is a resolver nobody notices is wrong.
   *
   * Presentation only. Nothing here touches the simulation, the snapshot or
   * the digest - two players on the same map with different backdrops still
   * agree on every checkpoint.
   */
  private applyMapBackground(mapIndex: number): void {
    const background = backgroundForMap(mapIndex);
    this.mapBackground = background;
    this.backdropDrift = backgroundDriftForMap(mapIndex);
    this.starfield = showsStarfield(mapIndex);
    this.applyGroundLife(mapIndex);
    this.applyAirParticles(mapIndex);
    this.applyLightning(mapIndex);
    this.applyShootingStars(mapIndex);
    this.applyEnergyTraces(mapIndex);
    if (background.kind !== 'texture') {
      /*
       * The shared field, and nothing else. It is already running and is not
       * rebuilt - recycling the pooled stars is the whole reason they are
       * pooled - so a map without a tile simply hides whatever tile was up.
       */
      this.backdrop?.setVisible(false);
      this.setStarsVisible(true);
      return;
    }

    const key = backgroundTextureKey(mapIndex);
    if (this.textures.exists(key)) {
      this.showBackdrop(key);
      return;
    }
    const url = staticAssetUrl(this.options.manifest, background.assetId);
    if (!url) {
      // The manifest does not carry it. Not a failure: the starfield is a
      // complete backdrop on its own, and an arena must never go down over
      // scenery.
      this.backdrop?.setVisible(false);
      this.setStarsVisible(true);
      return;
    }
    this.load.image(key, url);
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      // Guarded: the player may have travelled again while this was in flight,
      // and the map that asked for this tile is no longer the map being drawn.
      if (this.textures.exists(key) && this.mapBackground === background) {
        this.showBackdrop(key);
      }
    });
    this.load.start();
  }

  /**
   * Puts a loaded tile on screen, creating the one `TileSprite` if needed.
   *
   * ONE object, reused for every map for the whole life of the scene. Swapping
   * its texture is what makes travelling a texture change rather than anything
   * that touches the canvas.
   */
  private showBackdrop(key: string): void {
    if (!this.backdrop) {
      const { left, top, right, bottom } = this.visibleBounds;
      this.backdrop = this.add
        .tileSprite((left + right) / 2, (top + bottom) / 2, right - left, bottom - top, key)
        /*
         * Behind everything. The far star layer is at depth 0, so a backdrop
         * has to be below that or it would draw over the field it is meant to
         * sit behind.
         */
        /*
         * The DEFAULT scroll factor, deliberately. Pinning it to the camera
         * with `setScrollFactor(0)` sized it in arena units and then drew it
         * in screen space, so at 1920x1080 it covered part of the window and
         * left the clear colour showing across the rest. The damage flash is
         * the existing precedent for a full-screen object here, and it lives
         * in arena coordinates like everything else.
         */
        .setDepth(-1);
    } else {
      this.backdrop.setTexture(key);
    }
    this.backdrop.setVisible(true);
    /*
     * Stars are SKY. On a map whose backdrop is the ground, they are drawn in
     * front of it - the far layer sits at depth 0 and the backdrop at -1 - and
     * a star crossing a dune makes the sand look transparent, which is exactly
     * how it was reported. Nothing is destroyed: the pool is intact, the
     * sprites keep moving, and the moment the player travels to a sky map they
     * are visible again.
     *
     * Hidden HERE rather than beside the drift, so a map whose tile failed to
     * load keeps its starfield: the field is the fallback backdrop, and an
     * arena must never end up with neither.
     */
    this.setStarsVisible(this.starfield);
    this.fitBackdrop();
  }

  /** Shows or hides the whole pooled starfield. Presentation only. */
  private setStarsVisible(visible: boolean): void {
    for (const star of this.stars) {
      star.sprite.setVisible(visible);
    }
  }

  /**
   * Populates the map's ground with whatever lives on it.
   *
   * ## Loaded on demand, like the backdrop
   *
   * A hunt loads four textures - ship, cannon, mob, boss - and this adds a map's
   * own scenery to that only when the map it belongs to is the one being
   * played. Nobody downloads the maggots to fly over the night city.
   *
   * ## Rebuilt rather than pooled across maps
   *
   * Travelling destroys these and makes the next map's, because the two
   * populations have nothing in common - different textures, different counts,
   * different sizes. The starfield is pooled because every map shares it; this
   * is the opposite case.
   */
  private applyGroundLife(mapIndex: number): void {
    for (const creature of this.groundLife) {
      creature.sprite.destroy();
      creature.companion?.destroy();
    }
    this.groundLife = [];
    this.groundLifeMap = mapIndex;

    const specs = groundLifeForMap(mapIndex);
    if (specs.length === 0) {
      return;
    }
    const { manifest } = this.options;
    const pending: string[] = [];
    /*
     * A spec may name TWO sheets, and both have to be queued here.
     *
     * The companion is loaded on the same pass as the thing it is pinned to,
     * because it is built in the same place: queueing only the first would
     * leave the mark's texture missing at the moment its sprite is created,
     * and it would silently never appear. Both, or the ward turns empty.
     */
    for (const spec of specs) {
      for (const id of spec.companion ? [spec.assetId, spec.companion] : [spec.assetId]) {
        if (this.textures.exists(id)) {
          continue;
        }
        const asset = findAnimatedAsset(manifest, id);
        this.load.spritesheet(id, assetUrl(manifest, asset), {
          frameWidth: asset.layout.frameWidth,
          frameHeight: asset.layout.frameHeight,
        });
        pending.push(id);
      }
    }
    if (pending.length === 0) {
      this.buildGroundLife(specs);
      return;
    }
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      // The player may have travelled while this was in flight; the map that
      // asked for these creatures is no longer the map being drawn.
      if (this.groundLifeMap === mapIndex) {
        this.buildGroundLife(specs);
      }
    });
    this.load.start();
  }

  private buildGroundLife(specs: readonly GroundLifeSpec[]): void {
    const { manifest } = this.options;
    const { left, right, top, bottom } = this.visibleBounds;
    for (const spec of specs) {
      if (!this.textures.exists(spec.assetId)) {
        // The sheet failed to load. Not a failure worth taking an arena down
        // for: the map simply has less on it.
        continue;
      }
      const asset = findAnimatedAsset(manifest, spec.assetId);
      const key = `${spec.assetId}-idle`;
      if (!this.anims.exists(key)) {
        // Frame rate from the MANIFEST, never from anything in this file.
        const animation = asset.animations[0];
        this.anims.create({
          key,
          frames: this.anims.generateFrameNumbers(spec.assetId, {
            start: 0,
            end: asset.layout.frameCount - 1,
          }),
          frameRate: animation?.frameRate ?? 8,
          repeat: -1,
        });
      }
      /*
       * A LADDER: spaced once, spaced forever.
       *
       * Everything lying on the ground moves at exactly one speed, so the gaps
       * between these never change - which is the whole reason this works.
       * Laying them out at even intervals down a band taller than the arena
       * guarantees no two are ever close enough to touch, and recycling by
       * subtracting one whole band preserves that exactly.
       *
       * The alternative, and what was there before, is a fresh random position
       * every time one wraps. That is not a weaker version of this: it is a
       * different thing, which produces a collision whenever two rolls land
       * near each other, and re-rolling on a collision only makes it rarer.
       * Five wards a third of the arena across collide often enough to see.
       */
      const ladder =
        spec.ladder === true
          ? { span: asset.layout.frameHeight * spec.maxScale, count: spec.count }
          : null;
      const rungSpan = ladder ? this.ladderBand(ladder) : 0;
      for (let index = 0; index < spec.count; index += 1) {
        const scale = Phaser.Math.FloatBetween(spec.minScale, spec.maxScale);
        const sprite = this.add
          .sprite(
            Phaser.Math.FloatBetween(left, right),
            ladder
              ? top - (ladder.span * this.backdropCover) / 2 + (rungSpan * index) / spec.count
              : Phaser.Math.FloatBetween(top, bottom),
            spec.assetId,
          )
          .setScale(scale)
          .setAlpha(spec.alpha)
          /*
           * Between the backdrop at -1 and everything else at 0 and above.
           * These lie ON the ground, so nothing about the game may end up
           * behind them.
           */
          .setDepth(-0.5);
        // Each one starts at its own point of the loop, so a field of maggots
        // does not wriggle in unison.
        sprite.play(key);
        sprite.anims.setProgress(Phaser.Math.FloatBetween(0, 1));
        if (spec.timeScale) {
          sprite.anims.timeScale = Phaser.Math.FloatBetween(spec.timeScale[0], spec.timeScale[1]);
        }
        // Turned if it walks, or if the spec asks for it outright: a fire
        // stands upright whatever else does, and an eye has no upright at all.
        const crawl = spec.crawl === 0 ? 0 : Phaser.Math.FloatBetween(-spec.crawl, spec.crawl);
        if (spec.crawl !== 0 || spec.spin === true) {
          sprite.setRotation(Phaser.Math.FloatBetween(0, Math.PI * 2));
        }
        // A continuous turn, either way, at its own rate.
        const turn = spec.turn
          ? Phaser.Math.FloatBetween(spec.turn[0], spec.turn[1]) *
            (Phaser.Math.Between(0, 1) === 0 ? 1 : -1)
          : 0;
        /*
         * The companion, if this thing has one.
         *
         * Same texture size, same scale, one step in front, and NEVER
         * rotated - which is the whole reason it is a second sprite rather
         * than more of the first one's frames. Its animation runs on its
         * own clock so a ward and the mark inside it do not pulse together.
         */
        let companion: Phaser.GameObjects.Sprite | null = null;
        if (spec.companion) {
          const markKey = `${spec.companion}-idle`;
          if (!this.anims.exists(markKey) && this.textures.exists(spec.companion)) {
            const mark = findAnimatedAsset(manifest, spec.companion);
            this.anims.create({
              key: markKey,
              frames: this.anims.generateFrameNumbers(spec.companion, {
                start: 0,
                end: mark.layout.frameCount - 1,
              }),
              frameRate: mark.animations[0]?.frameRate ?? 8,
              repeat: -1,
            });
          }
          if (this.textures.exists(spec.companion)) {
            companion = this.add
              .sprite(sprite.x, sprite.y, spec.companion)
              .setScale(scale)
              .setAlpha(spec.alpha)
              .setDepth(-0.49);
            companion.play(markKey);
            companion.anims.setProgress(Phaser.Math.FloatBetween(0, 1));
          }
        }
        this.groundLife.push({
          sprite,
          crawl,
          turn,
          companion,
          ladder,
          baseScale: scale,
          halfHeight: (asset.layout.frameHeight * scale) / 2,
        });
      }
    }
  }

  /**
   * Writes one air-particle texture, pixel by pixel.
   *
   * ## Why raw pixels and not stacked circles
   *
   * A glow is a PROFILE - a specific curve of brightness against radius - and
   * stacking translucent circles cannot express one. Painted that way the
   * brush came out capped at 0.41 alpha in its middle, which is why every
   * emitter that used it was invisible however loud its own settings were. A
   * canvas written directly says exactly what each pixel is and cannot be
   * wrong by accident.
   *
   * ## And why the COLOUR is in the texture rather than in a tint
   *
   * This is the mistake that made the last attempt read as fog. Additive
   * blending adds `texture * tint`, so a white texture tinted orange
   * contributes orange EVERYWHERE - the middle is the same hue as the edge,
   * just more of it, and a blob of uniform orange is a blob. What makes an
   * ember look hot is a white core bleeding out into colour, and that is a
   * gradient of HUE, which one tint cannot carry. So each texture bakes its
   * own ramp and nothing is tinted.
   */
  private ensureAirTexture(kind: AirTextureKind): string {
    const key = `air-${kind}`;
    if (this.textures.exists(key)) {
      return key;
    }
    const size = kind === 'cloud' || kind === 'cloud-green' ? 128 : 64;
    const canvas = this.textures.createCanvas(key, size, size);
    if (!canvas) {
      // Texture creation failed. Scenery is never worth an exception.
      return key;
    }
    const context = canvas.getContext();
    const image = context.createImageData(size, size);
    const half = size / 2;

    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const dx = x + 0.5 - half;
        const dy = y + 0.5 - half;
        const offset = (y * size + x) * 4;

        if (kind === 'bloom-blue') {
          /*
           * A five-petalled flower, from a ROSE CURVE.
           *
           * The whole shape is one polar radius: `cos(5t)` swept round gives
           * five lobes, and everything inside that radius is petal. It needs no
           * per-petal geometry and no ordering, and it stays a flower at any
           * size because the lobes scale with it.
           */
          const distance = Math.sqrt(dx * dx + dy * dy) / half;
          const around = Math.atan2(dy, dx);
          const lobe = 0.52 + 0.36 * Math.abs(Math.cos(around * 2.5));
          const into = distance / lobe;

          // Soft at the rim, and a bright eye at the middle where a flower
          // gathers - a flat silhouette reads as a paper cut-out.
          const body = Math.max(0, Math.min(1, (1 - into) / 0.28));
          const eye = Math.exp(-((distance / 0.16) ** 2));
          const alpha = Math.min(1, body * 0.85 + eye);

          const petal: Rgb = [72, 138, 255];
          const hot = Math.min(1, eye * 1.5);
          for (let channel = 0; channel < 3; channel += 1) {
            const base = petal[channel] ?? 0;
            image.data[offset + channel] = base + (255 - base) * hot;
          }
          image.data[offset + 3] = alpha * 255;
        } else if (kind === 'crucifix') {
          /*
           * A Latin cross, and it is a different SHAPE from the four-pointed
           * star above rather than a recolour of it.
           *
           * That star is symmetric - four equal spikes from a point - and it
           * reads as a spark, which is what it is for. A crucifix has a long
           * upright, a crossbar set high on it, and square ends: it is an
           * object, not a glint, and none of those three properties survive
           * being drawn as four rays.
           *
           * Built as the union of two rectangles measured as a SIGNED
           * DISTANCE, which is what gives both an antialiased edge and the
           * glow around it from the same number: negative inside the shape,
           * positive outside, and the size of it is how far away you are.
           */
          const nx = dx / half;
          const ny = dy / half;
          const upright = Math.max(Math.abs(nx) - 0.15, Math.max(-0.9 - ny, ny - 0.93));
          const crossbar = Math.max(Math.abs(nx) - 0.6, Math.abs(ny + 0.3) - 0.15);
          const distance = Math.min(upright, crossbar);

          const body = Math.max(0, Math.min(1, -distance / 0.07));
          const bloom = Math.exp(-((Math.max(0, distance) / 0.2) ** 2));
          const alpha = Math.min(1, body + bloom * 0.55);

          // The project's profile: the solid interior runs to white, the glow
          // around it keeps the hue.
          const hot = Math.min(1, body ** 2.5);
          const arm: Rgb = [168, 74, 255];
          for (let channel = 0; channel < 3; channel += 1) {
            const base = arm[channel] ?? 0;
            image.data[offset + channel] = base + (255 - base) * hot;
          }
          image.data[offset + 3] = alpha * 255;
        } else if (
          kind === 'cross' ||
          kind === 'cross-violet' ||
          kind === 'cross-gold' ||
          kind === 'cross-blue'
        ) {
          /*
           * A four-pointed star: a tight core plus two crossed blades.
           *
           * Each blade is a gaussian across its own width times a linear fade
           * along its length, so it comes to a POINT rather than stopping - a
           * spike with a blunt end reads as a plus sign, and a plus sign reads
           * as a user interface.
           *
           * The COLOUR follows the same standard as every round particle here:
           * a core that runs to white, and blades carrying the map's own hue.
           * Map 7's ash is white because white is its hue, not because a cross
           * has to be - map 10's are arcane purple, from the same code.
           */
          const ax = Math.abs(dx) / half;
          const ay = Math.abs(dy) / half;
          const u = Math.sqrt(dx * dx + dy * dy) / half;
          const core = Math.exp(-((u / 0.1) ** 2));
          const across = Math.exp(-((ay / 0.04) ** 2)) * Math.max(0, 1 - ax) ** 1.5;
          const down = Math.exp(-((ax / 0.04) ** 2)) * Math.max(0, 1 - ay) ** 1.5;
          const alpha = Math.min(1, core + (across + down) * 0.85);
          const BLADES: Record<string, Rgb> = {
            'cross-violet': [168, 74, 255],
            'cross-gold': [255, 214, 24],
            // Bluer than the streak's skirt on purpose: the green channel is
            // what a blue loses to make it read as blue rather than as sky.
            'cross-blue': [38, 112, 255],
          };
          const blade: Rgb = BLADES[kind] ?? [255, 255, 255];
          const hot = Math.min(1, core * 1.7);
          for (let channel = 0; channel < 3; channel += 1) {
            const base = blade[channel] ?? 0;
            image.data[offset + channel] = base + (255 - base) * hot;
          }
          image.data[offset + 3] = alpha * 255;
        } else if (kind === 'star-violet') {
          /*
           * A five-pointed STAR, and it is a different shape from the
           * four-pointed spark above rather than a recolour of it.
           *
           * That spark is four equal rays from a point and reads as a
           * glint - which is what it is for. A star is a solid with five
           * points and a body between them; it is an object, and none of
           * that survives being drawn as rays.
           *
           * Built from ONE polar radius. The angle is folded into a single
           * point of the star, and the boundary runs from the valley out to
           * the tip across that fold - so the whole silhouette is one
           * comparison, it needs no per-point geometry and no ordering, and
           * it stays a star at any size because the points scale with it.
           */
          const u = Math.sqrt(dx * dx + dy * dy) / half;
          const around = Math.atan2(dy, dx);
          const POINTS = 5;
          const wedge = ((((around * POINTS) / (Math.PI * 2) + 1.25) % 1) + 1) % 1;
          // 1 at the middle of a point, 0 in the valley between two.
          const toward = Math.abs(wedge - 0.5) * 2;
          /*
           * Raised to a POWER, and that is what makes it a star.
           *
           * Linear, the radius grows evenly from valley to tip and the lobes
           * come out round - a five-petalled flower, which the map already has
           * a texture for and did not need a second of. An exponent keeps the
           * radius near the valley almost everywhere and lets it run only
           * close to the tip, which is a spike.
           */
          const edge = 0.2 + toward ** 2.6 * 0.78;

          // Soft at the rim and brightest at the middle, where a star
          // gathers: a flat silhouette reads as a paper cut-out.
          const body = Math.max(0, Math.min(1, (edge - u) / 0.08));
          const eye = Math.exp(-((u / 0.14) ** 2));
          const alpha = Math.min(1, body * 0.9 + eye);

          // The project's profile: a core running to white, the hue around it.
          const petal: Rgb = [150, 62, 255];
          const hot = Math.min(1, eye * 1.5);
          for (let channel = 0; channel < 3; channel += 1) {
            const base = petal[channel] ?? 0;
            image.data[offset + channel] = base + (255 - base) * hot;
          }
          image.data[offset + 3] = alpha * 255;
        } else if (kind === 'halo-blue') {
          /*
           * A halo: the ring these creatures wear, small enough to be dust.
           *
           * HOLLOW on purpose - a filled disc at this size is just a second
           * mote, and the hole is the only thing that says what it is. The
           * light is on the same profile as everything else here: the band
           * runs to white at its middle and carries the hue on both sides.
           */
          const u = Math.sqrt(dx * dx + dy * dy) / half;
          const band = Math.abs(u - 0.58);
          const core = Math.exp(-((band / 0.055) ** 2));
          const glow = Math.exp(-((band / 0.19) ** 2));
          const alpha = Math.min(1, core + glow * 0.4);
          const skirt: Rgb = [14, 58, 224];
          const hot = Math.min(1, core * 1.6);
          for (let channel = 0; channel < 3; channel += 1) {
            const base = skirt[channel] ?? 0;
            image.data[offset + channel] = base + (255 - base) * hot;
          }
          image.data[offset + 3] = alpha * 255;
        } else if (kind !== 'cloud' && kind !== 'cloud-green') {
          const u = Math.sqrt(dx * dx + dy * dy) / half;
          /*
           * Two gaussians: a very tight core and a wide halo.
           *
           * The core is a tenth of the radius, so it is a HARD point of light
           * rather than a gradient - that is the difference between an ember
           * and a smudge. The halo carries the colour outwards and is what
           * makes the particle glow rather than merely exist.
           */
          const core = Math.exp(-((u / 0.11) ** 2));
          const halo = Math.exp(-((u / 0.4) ** 2));
          /*
           * How far the glow reaches past the core, per kind.
           *
           * 0.5 for everything that was here before it was a number at all -
           * this is a widening for one map, not a change to the rest. Map 11
           * has nothing solid in its air, so its points carry the whole
           * brightness of the scene and are allowed a longer skirt.
           */
          const HALOES: Record<string, number> = { gold: 0.9, sand: 0.85 };
          const alpha = Math.min(1, core + halo * (HALOES[kind] ?? 0.5));
          /*
           * White at the centre, falling to the skirt's own hue.
           *
           * The ramp is what makes an ember look hot rather than merely
           * coloured, and the only thing that changes between the two is where
           * it lands: orange for fire, arterial red for blood. Both keep the
           * white core, because that is the part that reads as incandescent.
           */
          const hot = Math.min(1, core * 1.6);
          /*
           * Where the ramp LANDS. The white core is common to all of them -
           * it is the part that reads as incandescent - and only the skirt
           * carries the map's own hue.
           */
          /*
           * The skirt carries all THREE channels, and pinning one was a bug.
           *
           * Red used to be hard-coded to 255 here, on the unexamined
           * assumption that an ember is red - which is true of fire and blood
           * and false of everything else. The violet map's motes came out
           * magenta and the machine's came out pink, because a blue skirt with
           * red forced to full is not blue. Only the CORE is white; the skirt
           * is whatever the map burns with, in full.
           */
          /*
           * Keyed by RoundAirKind, and that is load-bearing.
           *
           * This was `Record<string, Rgb>` with a `?? SKIRTS.blood` fallback,
           * which meant a kind with no entry here silently came out ARTERIAL
           * RED. Two shipped that way before anyone saw them: map 18's vents
           * and map 19's runes, both specified as blue, both drawn as blood
           * over a black floor. A default that is a real colour cannot be
           * told apart from a choice.
           *
           * Keyed by the union, the compiler refuses a missing entry, and the
           * fallback is gone because there is nothing left for it to catch.
           */
          const SKIRTS: Record<RoundAirKind, Rgb> = {
            fire: [255, 118, 18],
            blood: [255, 14, 16],
            violet: [150, 62, 255],
            current: [46, 140, 255],
            gold: [255, 214, 24],
            ember: [255, 46, 12],
            spore: [222, 196, 182],
            sand: [238, 214, 168],
            'spore-green': [70, 230, 110],
            'petal-blue': [72, 138, 255],
            // Cold and pale: what a machine bleeds out of a seam.
            vent: [150, 210, 255],
            'rune-blue': [40, 120, 255],
            // The blue this map is engraved with, and the wards turn in.
          };
          const skirt = SKIRTS[kind];
          for (let channel = 0; channel < 3; channel += 1) {
            const base = skirt[channel] ?? 0;
            image.data[offset + channel] = base + (255 - base) * hot;
          }
          image.data[offset + 3] = alpha * 255;
        } else {
          /*
           * A cloud, and clouds are not discs.
           *
           * The radius is modulated by two harmonics of the angle, so the puff
           * is lumpy - a circle at low opacity reads as a lens flare, and
           * several lumpy ones overlapping read as a bank of smoke.
           */
          const angle = Math.atan2(dy, dx);
          const wobble = 1 + 0.2 * Math.sin(angle * 3 + 1.1) + 0.13 * Math.sin(angle * 5 - 0.4);
          const u = Math.sqrt(dx * dx + dy * dy) / (half * wobble);
          const alpha = u >= 1 ? 0 : (1 - u) ** 1.9;
          /*
           * Smoke is warm grey because it is lit from below by whatever made
           * it. A toxic bank is lit from WITHIN, so the green one is not a
           * tinted version of the same thing - it is brighter than its
           * surroundings rather than darker, and it is drawn additively by the
           * spec that asks for it.
           */
          const body: Rgb = kind === 'cloud-green' ? [46, 150, 74] : [168, 158, 146];
          for (let channel = 0; channel < 3; channel += 1) {
            image.data[offset + channel] = body[channel] ?? 0;
          }
          image.data[offset + 3] = alpha * (kind === 'cloud-green' ? 150 : 190);
        }
      }
    }
    canvas.putData(image, 0, 0);
    canvas.refresh();
    return key;
  }

  /**
   * Fills the air over a map with whatever is burning near it.
   *
   * ## Rebuilt on travel, like the ground life
   *
   * Emitters are destroyed and remade rather than reconfigured: the three
   * looks differ in tint, blend mode and gravity, and a reconfigured emitter
   * keeps particles already in flight under the old settings - so a journey
   * from the valley would rain its ash over the next map for several seconds.
   *
   * ## Behind the game, always
   *
   * Depth sits between the backdrop and zero, so every one of these is behind
   * the ship, the enemies, the projectiles and the HUD. An ember is scenery;
   * the moment one could be mistaken for a shot it would be a gameplay bug in
   * an art costume.
   */
  /**
   * Arms the map's weather, or disarms it.
   *
   * ONE sprite and one texture, built the first time a storm map is entered
   * and reused for every strike after it. A bolt that allocated a texture per
   * flash would stutter the frame it fired on, which is the one frame the
   * player is looking at it.
   */
  /**
   * Arms the map's meteor shower, or disarms it.
   *
   * One sprite per streak, built once and reused. A crossing allocates nothing.
   */
  private applyShootingStars(mapIndex: number): void {
    this.shower = shootingStarsForMap(mapIndex);
    for (const streak of this.streaks) {
      streak.image.destroy();
    }
    this.streaks = [];
    if (!this.shower) {
      return;
    }
    for (let index = 0; index < this.shower.count; index += 1) {
      const image = this.add
        .image(0, 0, this.ensureStreakTexture(this.shower.hue))
        /*
         * The origin sits ON the head, which is no longer the right edge - the
         * cross needs half its own height of room to the right of it, so the
         * anchor is inset by exactly that fraction. Getting this wrong puts the
         * streak half a head off its own path.
         */
        .setOrigin(1 - 95 / 900, 0.5)
        .setRotation(this.shower.angle)
        .setBlendMode(Phaser.BlendModes.ADD)
        // Behind the game, like every other piece of scenery in this file.
        .setDepth(-0.2)
        .setVisible(false);
      this.streaks.push({
        image,
        waitMs: Phaser.Math.Between(this.shower.delayMs[0], this.shower.delayMs[1]),
        ageMs: null,
        fromX: 0,
        fromY: 0,
      });
    }
  }

  /**
   * One streak, drawn once into a canvas texture.
   *
   * A head that runs to white and a tail that does not simply fade: it thins
   * as well, because a meteor trail narrows behind the head. A tail of even
   * width reading only in alpha looks like a smear, which is the difference
   * between a shooting star and a scratch on the lens.
   */
  private ensureStreakTexture(hue: string): string {
    const key = `streak-${hue}`;
    if (this.textures.exists(key)) {
      return key;
    }
    /*
     * Wide enough for a long trail and TALL enough for a cross.
     *
     * The first version was a 420 by 26 strip, which can hold a tail and
     * nothing else - there was no room above or below the centre line for a
     * vertical blade, so the head could only ever be a point. The height is
     * what makes the star a star.
     */
    /*
     * Widened from 560, which is the tail getting longer.
     *
     * The head sits half a height in from the right and the tail is
     * everything to the left of it, so the tail's length in head-widths is
     * what this number controls. The height is untouched: a taller texture
     * would make the head bigger, not the tail longer.
     */
    const width = 900;
    const height = 190;
    const canvas = this.textures.createCanvas(key, width, height);
    if (!canvas) {
      // Texture creation failed. Scenery is never worth an exception.
      return key;
    }
    const context = canvas.getContext();
    const image = context.createImageData(width, height);
    const middle = height / 2;
    // Where the head sits, in pixels from the leading edge.
    const headAt = width - middle;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * 4;
        const fromMiddle = Math.abs(y + 0.5 - middle);

        /*
         * The TAIL, running back from the head and narrowing as it goes. It
         * stops at the head rather than passing through it, so the cross is
         * not sitting on a bar.
         */
        const along = Math.max(0, Math.min(1, x / headAt));
        const thickness = middle * (0.02 + along ** 3 * 0.1);
        const acrossTail = fromMiddle / thickness;
        const tail = x > headAt || acrossTail >= 1 ? 0 : (1 - acrossTail) ** 1.3 * along ** 1.7;

        /*
         * And the head: a four-pointed cross, built the same way as the
         * particle of the same shape - a gaussian across each blade times a
         * linear fade along it, so both come to a POINT. A blade with a blunt
         * end reads as a plus sign, and a plus sign reads as an interface.
         */
        const dx = x + 0.5 - headAt;
        const dy = y + 0.5 - middle;
        const reach = middle * 0.94;
        const ax = Math.abs(dx) / reach;
        const ay = Math.abs(dy) / reach;
        const blade = middle * 0.05;
        const flat = Math.exp(-((dy / blade) ** 2)) * Math.max(0, 1 - ax) ** 1.4;
        const upright = Math.exp(-((dx / blade) ** 2)) * Math.max(0, 1 - ay) ** 1.4;
        const radius = Math.sqrt(dx * dx + dy * dy) / reach;
        const core = Math.exp(-((radius / 0.09) ** 2));
        const glow = Math.exp(-((radius / 0.4) ** 2));

        const alpha = Math.min(1, tail * 1.1 + (flat + upright) * 0.9 + core + glow * 0.45);

        // The project\u0027s profile: a core running to white, the hue around it.
        const hot = Math.min(1, core * 1.8);
        /*
         * From the HUE, and it was hard-coded blue until now.
         *
         * The texture is cached per hue and every caller passes one, so it
         * looked parameterised - but the skirt, which is all of the streak
         * except a few pixels of core, ignored it. Map 17 asked for blue and
         * got blue by luck; map 20 asked for violet and got map 17's blue.
         */
        const tint = Phaser.Display.Color.ValueToColor(hue);
        const skirt = [tint.red, tint.green, tint.blue];
        for (let channel = 0; channel < 3; channel += 1) {
          const base = skirt[channel] ?? 0;
          image.data[offset + channel] = base + (255 - base) * hot;
        }
        image.data[offset + 3] = alpha * 255;
      }
    }
    canvas.putData(image, 0, 0);
    canvas.refresh();
    return key;
  }

  /**
   * Counts each streak down and flies the ones that are crossing.
   *
   * Every pass starts OFF the arena and ends off the other side, so a streak is
   * never seen to appear or stop in mid-air. The distance is taken from the
   * diagonal, which is the longest line the heading could have to cover.
   */
  private updateShootingStars(deltaMs: number): void {
    const spec = this.shower;
    if (!spec) {
      return;
    }
    const { width, height } = this.scale.gameSize;
    const span = Math.sqrt(width * width + height * height) + 260;
    const dirX = Math.cos(spec.angle);
    const dirY = Math.sin(spec.angle);

    for (const streak of this.streaks) {
      if (streak.ageMs === null) {
        streak.waitMs -= deltaMs;
        if (streak.waitMs > 0) {
          continue;
        }
        /*
         * Somewhere random on the upwind side. Backing the start off along the
         * heading by half the span puts it comfortably outside whatever corner
         * this particular angle enters from, without needing a case per angle.
         */
        streak.fromX = Phaser.Math.Between(-Math.round(width * 0.3), width) - dirX * span * 0.5;
        streak.fromY = Phaser.Math.Between(-Math.round(height * 0.3), height) - dirY * span * 0.5;
        const size = spec.scale ?? [0.85, 1.7];
        streak.image.setScale(Phaser.Math.FloatBetween(size[0], size[1]));
        streak.image.setVisible(true);
        streak.ageMs = 0;
        continue;
      }

      streak.ageMs += deltaMs;
      const life = streak.ageMs / spec.durationMs;
      if (life >= 1) {
        streak.image.setVisible(false);
        streak.ageMs = null;
        streak.waitMs = Phaser.Math.Between(spec.delayMs[0], spec.delayMs[1]);
        continue;
      }

      const travelled = life * span;
      streak.image.setPosition(streak.fromX + dirX * travelled, streak.fromY + dirY * travelled);
      // Bright for most of the crossing and gone at both ends, so it enters and
      // leaves rather than switching on.
      streak.image.setAlpha(Math.min(1, Math.sin(life * Math.PI) * 2.4));
    }
  }

  /**
   * Arms the map's floor current, or disarms it.
   *
   * One bar per straight run plus one head, all built up front at the widest
   * path the spec allows. A run allocates nothing.
   */
  private applyEnergyTraces(mapIndex: number): void {
    this.traces = energyTracesForMap(mapIndex);
    for (const pulse of this.pulses) {
      for (const bar of pulse.bars) {
        bar.destroy();
      }
      pulse.head.destroy();
    }
    this.pulses = [];
    const spec = this.traces;
    if (!spec) {
      return;
    }
    const barKey = this.ensureTraceBarTexture(spec.hue);
    const headKey = this.ensureTraceHeadTexture(spec.hue);
    for (let index = 0; index < spec.count; index += 1) {
      const bars: Phaser.GameObjects.Image[] = [];
      for (let leg = 0; leg < spec.legs[1]; leg += 1) {
        bars.push(
          this.add
            .image(0, 0, barKey)
            // Anchored at the END the run starts from, so growing the display
            // width extends it forwards instead of about its middle.
            .setOrigin(0, 0.5)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setDepth(-0.28)
            .setVisible(false),
        );
      }
      this.pulses.push({
        bars,
        head: this.add
          .image(0, 0, headKey)
          .setOrigin(0.5, 0.5)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(-0.27)
          .setVisible(false),
        path: [],
        legLength: [],
        legStart: [],
        totalLength: 0,
        waitMs: Phaser.Math.Between(spec.delayMs[0], spec.delayMs[1]),
        ageMs: null,
        driftY: 0,
      });
    }
  }

  /**
   * The body of the pulse: a bar that is UNIFORM along its length.
   *
   * That uniformity is what lets one image be stretched to any run length
   * without the texture smearing - all the shape is across the bar, none of it
   * along. A gradient along the length would have to be regenerated every frame
   * as the run grows, which is a texture upload per frame per leg.
   *
   * The profile across it is the project's standard: a core running to white
   * with the hue's skirt around it, composited additively.
   */
  private ensureTraceBarTexture(hue: string): string {
    const key = `trace-bar-${hue}`;
    if (this.textures.exists(key)) {
      return key;
    }
    const width = 8;
    const height = 40;
    const canvas = this.textures.createCanvas(key, width, height);
    if (!canvas) {
      // Texture creation failed. Scenery is never worth an exception.
      return key;
    }
    const tint = Phaser.Display.Color.ValueToColor(hue);
    const context = canvas.getContext();
    const image = context.createImageData(width, height);
    const middle = height / 2;
    for (let y = 0; y < height; y += 1) {
      const across = Math.abs(y + 0.5 - middle) / middle;
      const core = Math.exp(-((across / 0.09) ** 2));
      const glow = Math.exp(-((across / 0.42) ** 2));
      const alpha = Math.min(1, core * 1.15 + glow * 0.5);
      const hot = Math.min(1, core * 1.7);
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * 4;
        image.data[offset] = tint.red + (255 - tint.red) * hot;
        image.data[offset + 1] = tint.green + (255 - tint.green) * hot;
        image.data[offset + 2] = tint.blue + (255 - tint.blue) * hot;
        image.data[offset + 3] = alpha * 255;
      }
    }
    canvas.putData(image, 0, 0);
    canvas.refresh();
    return key;
  }

  /**
   * The head, which is also what a corner is made of.
   *
   * Round, and brighter than the bar it leads. It is the only part of the pulse
   * that is not a stretched bar, and it is what says which way the thing is
   * going: without it the path is a line being revealed, and with it something
   * is travelling and leaving a line behind.
   *
   * The corners need no sprite of their own. Each run overruns its turn by half
   * its own width, so between them the two bars fill the corner square, and the
   * additive overlap brightens the turn without being asked to.
   */
  private ensureTraceHeadTexture(hue: string): string {
    const key = `trace-head-${hue}`;
    if (this.textures.exists(key)) {
      return key;
    }
    const size = 96;
    const canvas = this.textures.createCanvas(key, size, size);
    if (!canvas) {
      return key;
    }
    const tint = Phaser.Display.Color.ValueToColor(hue);
    const context = canvas.getContext();
    const image = context.createImageData(size, size);
    const middle = size / 2;
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const dx = (x + 0.5 - middle) / middle;
        const dy = (y + 0.5 - middle) / middle;
        const radius = Math.sqrt(dx * dx + dy * dy);
        const core = Math.exp(-((radius / 0.12) ** 2));
        const glow = Math.exp(-((radius / 0.44) ** 2));
        const alpha = Math.min(1, core * 1.3 + glow * 0.55);
        const hot = Math.min(1, core * 1.8);
        const offset = (y * size + x) * 4;
        image.data[offset] = tint.red + (255 - tint.red) * hot;
        image.data[offset + 1] = tint.green + (255 - tint.green) * hot;
        image.data[offset + 2] = tint.blue + (255 - tint.blue) * hot;
        image.data[offset + 3] = alpha * 255;
      }
    }
    canvas.putData(image, 0, 0);
    canvas.refresh();
    return key;
  }

  /**
   * Counts each pulse down, lays its path, and runs the ones that are running.
   *
   * A run has three parts and they are not interchangeable: the head DRAWS the
   * path, the finished path STANDS for a moment, then the whole thing fades.
   * Fading from the tail instead would need the body's alpha to vary along its
   * length, which is the one thing a stretched uniform bar cannot do - and the
   * standing beat is what makes the shape readable at all, because a path that
   * starts disappearing the instant it is finished is never seen whole.
   */
  private updateEnergyTraces(deltaMs: number): void {
    const spec = this.traces;
    if (!spec) {
      return;
    }
    const { width, height } = this.scale.gameSize;
    const scroll = this.backdropDrift * this.backdropCover;
    const total = spec.drawMs + spec.holdMs + spec.fadeMs;
    /*
     * How wide the bar is drawn, taken from the arena rather than fixed.
     *
     * The scale mode here is RESIZE, so an arena unit is a CSS pixel and the
     * arena is whatever the window is. A constant thickness would be a bold
     * conduit on a small window and a hairline on a large one; a fraction of
     * the short side is the same beam on both.
     */
    const THICK = Math.max(18, Math.min(width, height) * 0.032);

    for (const pulse of this.pulses) {
      if (pulse.ageMs === null) {
        pulse.waitMs -= deltaMs;
        if (pulse.waitMs > 0) {
          continue;
        }
        this.layTracePath(pulse, spec, width, height);
        pulse.ageMs = 0;
        pulse.driftY = 0;
      }

      pulse.ageMs += deltaMs;
      pulse.driftY += scroll * (deltaMs / 1000);
      if (pulse.ageMs >= total) {
        for (const bar of pulse.bars) {
          bar.setVisible(false);
        }
        pulse.head.setVisible(false);
        pulse.ageMs = null;
        pulse.waitMs = Phaser.Math.Between(spec.delayMs[0], spec.delayMs[1]);
        continue;
      }

      /*
       * Eased, so the head leaves fast and arrives slowly. A pulse crossing at
       * a constant rate looks like a wipe; the deceleration is what makes it
       * read as something being sent rather than something being drawn.
       */
      const drawn = Math.min(1, pulse.ageMs / spec.drawMs);
      const reached = (1 - (1 - drawn) ** 2.2) * pulse.totalLength;
      const after = pulse.ageMs - spec.drawMs - spec.holdMs;
      const alpha = after <= 0 ? 1 : Math.max(0, 1 - after / spec.fadeMs);

      for (let leg = 0; leg < pulse.bars.length; leg += 1) {
        const bar = pulse.bars[leg];
        if (!bar) {
          continue;
        }
        const start = pulse.legStart[leg];
        const length = pulse.legLength[leg];
        const from = pulse.path[leg];
        const to = pulse.path[leg + 1];
        if (start === undefined || length === undefined || !from || !to) {
          bar.setVisible(false);
          continue;
        }
        const shown = Math.min(length, reached - start);
        if (shown <= 0) {
          bar.setVisible(false);
          continue;
        }
        bar.setVisible(true);
        bar.setAlpha(alpha);
        bar.setPosition(from.x, from.y + pulse.driftY);
        bar.setRotation(Math.atan2(to.y - from.y, to.x - from.x));
        // Overrun by half the thickness, so the corner glow has a solid joint
        // underneath it rather than two bars stopping short of each other.
        bar.setDisplaySize(shown + THICK * 0.5, THICK);
      }

      const at = this.tracePointAt(pulse, reached);
      pulse.head.setVisible(true);
      pulse.head.setAlpha(alpha);
      pulse.head.setPosition(at.x, at.y + pulse.driftY);
      pulse.head.setDisplaySize(THICK * 2.6, THICK * 2.6);
    }
  }

  /**
   * Picks a right-angled path across the arena.
   *
   * It starts off one edge and every turn is a quarter, never a reversal, so
   * the pulse always keeps going somewhere instead of doubling back over what
   * it has already drawn. The runs are long relative to the arena on purpose:
   * a path made of short hops reads as a maze, and this is a conduit.
   */
  private layTracePath(
    pulse: (typeof this.pulses)[number],
    spec: EnergyTraceSpec,
    width: number,
    height: number,
  ): void {
    const legs = Phaser.Math.Between(spec.legs[0], spec.legs[1]);
    const span = Math.max(width, height);
    // Straight in from one of the four edges.
    const side = Phaser.Math.Between(0, 3);
    let x = side === 0 ? -60 : side === 1 ? width + 60 : Phaser.Math.Between(0, width);
    let y = side === 2 ? -60 : side === 3 ? height + 60 : Phaser.Math.Between(0, height);
    let dirX = side === 0 ? 1 : side === 1 ? -1 : 0;
    let dirY = side === 2 ? 1 : side === 3 ? -1 : 0;

    pulse.path = [{ x, y }];
    pulse.legLength = [];
    pulse.legStart = [];
    let running = 0;
    for (let leg = 0; leg < legs; leg += 1) {
      const length = Phaser.Math.Between(Math.round(span * 0.3), Math.round(span * 0.7));
      x += dirX * length;
      y += dirY * length;
      pulse.path.push({ x, y });
      pulse.legStart.push(running);
      pulse.legLength.push(length);
      running += length;
      // A quarter turn, either way. Swapping the components IS the rotation.
      const turn = Phaser.Math.Between(0, 1) === 0 ? 1 : -1;
      const wasX = dirX;
      dirX = -dirY * turn;
      dirY = wasX * turn;
    }
    pulse.totalLength = running;
  }

  /** Where the head is when it has travelled `distance` along the path. */
  private tracePointAt(
    pulse: (typeof this.pulses)[number],
    distance: number,
  ): { x: number; y: number } {
    for (let leg = 0; leg < pulse.legLength.length; leg += 1) {
      const start = pulse.legStart[leg] ?? 0;
      const length = pulse.legLength[leg] ?? 0;
      if (distance <= start + length || leg === pulse.legLength.length - 1) {
        const from = pulse.path[leg];
        const to = pulse.path[leg + 1];
        if (!from || !to) {
          break;
        }
        const along = length === 0 ? 0 : Math.min(1, (distance - start) / length);
        return { x: from.x + (to.x - from.x) * along, y: from.y + (to.y - from.y) * along };
      }
    }
    return pulse.path[0] ?? { x: 0, y: 0 };
  }

  private applyLightning(mapIndex: number): void {
    this.lightning = lightningForMap(mapIndex);
    for (const bolt of this.bolts) {
      bolt.image.destroy();
    }
    this.bolts = [];
    if (!this.lightning) {
      return;
    }
    for (let index = 0; index < this.lightning.bolts; index += 1) {
      const image = this.add
        // A texture per bolt, so the three are different shapes. Three copies
        // of one path flashing in the same sky reads as a repeated decal.
        .image(0, 0, this.ensureBoltTexture(this.lightning.hue, index))
        .setOrigin(0.5, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        /*
         * Behind the game, like every other piece of scenery in this file. A
         * bolt drawn over the arena would hide a shot at the exact moment it
         * is brightest, which is how a backdrop becomes a gameplay problem.
         */
        .setDepth(-0.2)
        .setVisible(false);
      this.bolts.push({
        image,
        /*
         * Each starts somewhere different in its own cycle, so the three do
         * not open the map together and then keep meeting. Nothing after this
         * synchronises them.
         */
        waitMs: Phaser.Math.Between(this.lightning.delayMs[0], this.lightning.delayMs[1]),
        ageMs: null,
      });
    }
  }

  /**
   * The bolt, drawn once into a canvas texture.
   *
   * ## Three passes, widest first
   *
   * A single stroke is a bright line and reads as a scratch. What reads as
   * lightning is a very narrow white channel inside a much wider coloured
   * glow, which is the same core-skirt-bloom profile every lit thing in this
   * game uses - here it is three strokes of the same path at falling width and
   * rising brightness, laid widest first so the core survives on top.
   *
   * ## The path is a walk, and it forks
   *
   * Straight down would be a rod. The x wanders a little at every step, and
   * two branches leave the trunk partway and die out - a bolt without forks
   * reads as drawn, because real ones always split.
   */
  private ensureBoltTexture(hue: string, variant: number): string {
    const key = `bolt-${hue}-${variant}`;
    if (this.textures.exists(key)) {
      return key;
    }
    const width = 220;
    const height = 1024;
    const canvas = this.textures.createCanvas(key, width, height);
    if (!canvas) {
      // Texture creation failed. Weather is never worth an exception.
      return key;
    }
    const context = canvas.getContext();
    context.clearRect(0, 0, width, height);
    context.lineCap = 'round';
    context.lineJoin = 'round';

    const trunk: { x: number; y: number }[] = [];
    let x = width / 2;
    const steps = 30;
    for (let step = 0; step <= steps; step += 1) {
      trunk.push({ x, y: (step / steps) * height });
      x += Phaser.Math.FloatBetween(-1, 1) * width * 0.16;
      x = Math.min(width * 0.86, Math.max(width * 0.14, x));
    }

    const branches: { x: number; y: number }[][] = [];
    for (const at of [8, 17]) {
      const from = trunk[at];
      if (!from) {
        continue;
      }
      const branch = [from];
      let bx = from.x;
      let by = from.y;
      const lean = Phaser.Math.FloatBetween(-1, 1);
      for (let step = 0; step < 6; step += 1) {
        bx += lean * width * 0.13 + Phaser.Math.FloatBetween(-1, 1) * width * 0.06;
        by += height * 0.035;
        branch.push({ x: bx, y: by });
      }
      branches.push(branch);
    }

    const trace = (path: { x: number; y: number }[], lineWidth: number, colour: string) => {
      context.strokeStyle = colour;
      context.lineWidth = lineWidth;
      context.beginPath();
      const head = path[0];
      if (!head) {
        return;
      }
      context.moveTo(head.x, head.y);
      for (const point of path.slice(1)) {
        context.lineTo(point.x, point.y);
      }
      context.stroke();
    };

    for (const [lineWidth, colour] of [
      [34, `${hue}30`],
      [16, `${hue}70`],
      [7, hue],
      [2.5, '#ffffff'],
    ] as const) {
      trace(trunk, lineWidth, colour);
      for (const branch of branches) {
        trace(branch, lineWidth * 0.55, colour);
      }
    }

    canvas.refresh();
    return key;
  }

  /**
   * Counts down to the next strike and plays the current one.
   *
   * ## The flicker is what sells it
   *
   * A bolt that faded smoothly out looks like a fading picture of a bolt. Real
   * lightning is several discharges down the same channel a few tens of
   * milliseconds apart, so the alpha here is a decaying envelope multiplied by
   * a hard on/off pulse: it stutters while it dies.
   */
  private updateLightning(deltaMs: number): void {
    const spec = this.lightning;
    if (!spec) {
      return;
    }
    const { width, height } = this.scale.gameSize;

    for (const bolt of this.bolts) {
      if (bolt.ageMs === null) {
        bolt.waitMs -= deltaMs;
        if (bolt.waitMs > 0) {
          continue;
        }
        // A new strike: place it, size it to the arena, and start its clock.
        bolt.image.setPosition(
          Phaser.Math.Between(Math.round(width * 0.1), Math.round(width * 0.9)),
          0,
        );
        bolt.image.setDisplaySize(Phaser.Math.Between(140, 280), height);
        bolt.image.setFlipX(Phaser.Math.Between(0, 1) === 1);
        bolt.image.setVisible(true);
        bolt.ageMs = 0;
        continue;
      }

      bolt.ageMs += deltaMs;
      const life = bolt.ageMs / spec.durationMs;
      if (life >= 1) {
        bolt.image.setVisible(false);
        bolt.ageMs = null;
        bolt.waitMs = Phaser.Math.Between(spec.delayMs[0], spec.delayMs[1]);
        continue;
      }

      const envelope = (1 - life) ** 2.2;
      // Four discharges over the life of the strike, on for a third of each.
      const pulse = (bolt.ageMs / (spec.durationMs / 4)) % 1 < 0.34 ? 1 : 0.25;
      bolt.image.setAlpha(Math.min(1, envelope * pulse * 1.4));
    }
  }

  private applyAirParticles(mapIndex: number): void {
    for (const entry of this.airEmitters) {
      entry.emitter.destroy();
    }
    this.airEmitters = [];

    const specs = airParticlesForMap(mapIndex);
    if (specs.length === 0) {
      return;
    }
    for (const spec of specs) {
      /*
       * Configured with only the emitter options this project can be sure of,
       * and EMITTED BY HAND.
       *
       * An earlier version used `emitZone` to scatter births across the arena
       * and a custom easing function to fade each particle in and out. Both
       * are shapes of the Phaser config whose exact contract varies between
       * versions, and a mistake in either fails silently: every particle born
       * at the origin, or every alpha stuck at zero. `emitParticleAt` takes a
       * position and nothing else, and `alpha: { start, end }` is as old as
       * the emitter itself.
       *
       * NOT tinted, and that is the point of the whole rewrite: the colour
       * ramp lives in the texture, because a tint is one colour and a hot core
       * bleeding into an orange halo is two.
       */
      const emitter = this.add.particles(0, 0, this.ensureAirTexture(spec.texture), {
        lifespan: { min: spec.lifeMs[0], max: spec.lifeMs[1] },
        // Varied per particle and constant over its life. Fire dies by going
        // OUT, not by shrinking, which is what the alpha ramp below does.
        scale: { min: spec.minScale, max: spec.maxScale },
        alpha: { start: spec.alpha, end: 0 },
        speedX: { min: -spec.sway, max: spec.sway },
        speedY: { min: spec.lift - 4, max: spec.lift + 4 },
        rotate: { min: 0, max: 360 },
        blendMode: spec.blend === 'add' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL,
        // Nothing is emitted on its own schedule: `updateAirParticles` decides.
        emitting: false,
      });
      emitter.setDepth(spec.depth);
      this.airEmitters.push({ emitter, spec, owed: 0 });
    }
  }

  /**
   * Births air particles across the visible rectangle, at each spec's rate.
   *
   * ## Why the scene emits rather than the emitter
   *
   * A particle has to be born somewhere in the arena the player can actually
   * see, and that rectangle changes with the window. Emitting by hand needs no
   * zone object to keep in step with a resize - the birth simply reads the
   * current bounds - and it makes the rate a number in this file rather than a
   * frequency the emitter interprets.
   *
   * The debt is carried in `owed` rather than rounded per frame: at five
   * particles a second and a sixty-hertz frame, rounding would emit nothing,
   * forever.
   */
  private updateAirParticles(dt: number): void {
    if (this.airEmitters.length === 0) {
      return;
    }
    const { left, right, top, bottom } = this.visibleBounds;
    // The speed the ground itself is travelling at, which every particle in
    // the air over it inherits. Same figure the ground life uses.
    const scroll = this.backdropDrift * this.backdropCover;
    for (const entry of this.airEmitters) {
      entry.owed += entry.spec.rate * dt;
      // Bounded, so a long frame - an alt-tab, a slow load - cannot dump a
      // whole second of ash into the arena at once.
      const born = Math.min(6, Math.floor(entry.owed));
      entry.owed -= Math.floor(entry.owed);
      for (let index = 0; index < born; index += 1) {
        const particle = entry.emitter.emitParticleAt(
          Phaser.Math.FloatBetween(left, right),
          Phaser.Math.FloatBetween(top, bottom),
        );
        /*
         * EVERY air particle travels with the background. This is a rule, not
         * a setting for this map.
         *
         * The emitter gave each particle only its own small motion - fire
         * rising, cinders falling - so the air hung still while the ground
         * raced underneath it. That reads as the ship hovering over a moving
         * picture. Anything in the air over a map the ship is flying across is
         * being LEFT BEHIND by that flight, so the scroll is the dominant term
         * and a particle's own drift is a perturbation on top of it.
         *
         * Added at birth rather than configured on the emitter: the emitter's
         * speed is fixed when it is built, and the scroll it has to match
         * changes with the map and with the window. This is the one place that
         * knows both.
         */
        if (particle) {
          particle.velocityY += scroll;
        }
      }
    }
  }

  /**
   * Scrolls the ground life with the ground, and recycles it.
   *
   * The downward speed is the BACKDROP's, not a speed of its own: a maggot is
   * not travelling south at forty units a second, the valley under it is
   * moving. Sharing one number is what stops the creatures sliding against the
   * bones they are supposed to be lying among.
   */
  /**
   * How tall one turn of a ladder is, in arena units, right now.
   *
   * Two conditions, and it is the LARGER of them. Taller than the arena plus
   * one sprite, so a rung that wraps has left the bottom before it reappears
   * at the top; and at least one sprite-height per rung, so two rungs can
   * never be near enough to touch whatever their x happens to be. Meeting only
   * the first would still let a crowded ladder overlap itself.
   *
   * Recomputed from the live magnification rather than stored, because the
   * sprites grow with the window and a band fixed at build time would be
   * correct until the first resize.
   */
  private ladderBand(ladder: { readonly span: number; readonly count: number }): number {
    const { top, bottom } = this.visibleBounds;
    const size = ladder.span * this.backdropCover;
    return Math.max(bottom - top + size, ladder.count * size * 1.06);
  }

  private updateGroundLife(dt: number): void {
    if (this.groundLife.length === 0) {
      return;
    }
    const { left, right, top, bottom } = this.visibleBounds;
    const cover = this.backdropCover;
    /*
     * The backdrop's speed, TIMES the factor its tile is magnified by.
     *
     * `backdropDrift` is a texture offset per second, and the tile is drawn at
     * `cover` times its own size - so the ground appears to travel
     * `drift * cover` arena units in a second, and anything lying on it has to
     * do the same or it will drift against the terrain it is supposed to be
     * part of. The creatures are scaled by the same factor for the same
     * reason: magnifying the ground magnifies what is on it.
     */
    for (const creature of this.groundLife) {
      const sprite = creature.sprite;
      const scale = creature.baseScale * cover;
      const halfHeight = creature.halfHeight * cover;
      if (sprite.scaleY !== scale) {
        sprite.setScale(scale);
      }
      sprite.rotation += creature.turn * dt;
      /*
       * The companion is CARRIED, not simulated.
       *
       * It takes the position and the size after they have been decided,
       * including the wrap, so it cannot drift out of the ring it belongs
       * to however that ring is moved. And it is never given the rotation,
       * which is the entire point of it.
       */
      if (creature.companion) {
        creature.companion.setPosition(sprite.x, sprite.y);
        if (creature.companion.scaleY !== scale) {
          creature.companion.setScale(scale);
        }
      }
      sprite.y += this.backdropDrift * cover * dt;
      sprite.x += creature.crawl * cover * dt;
      if (sprite.y - halfHeight > bottom) {
        /*
         * A LADDER recycles by lifting exactly one band and keeping its x.
         *
         * Everything on the ground moves at one speed, so the gaps between
         * these never change - which means spacing them once at build time
         * spaces them forever, and subtracting a whole band preserves that
         * exactly. Dropping one back at a random x is what let two wards
         * land on top of each other, and no amount of re-rolling fixes a
         * random placement: it only makes the collision less frequent.
         */
        if (creature.ladder) {
          sprite.y -= this.ladderBand(creature.ladder);
        } else {
          sprite.y = top - halfHeight;
          sprite.x = Phaser.Math.FloatBetween(left, right);
        }
      }
      // Wrapped sideways rather than recycled: a crawl is slow, and a creature
      // that vanished at the edge would read as one that had been deleted.
      if (sprite.x < left - halfHeight) {
        sprite.x = right + halfHeight;
      } else if (sprite.x > right + halfHeight) {
        sprite.x = left - halfHeight;
      }
    }
  }

  /**
   * Sizes the tile to the VISIBLE area, not to the canonical arena.
   *
   * On anything that is not 16:9 the camera shows more than 1280x720 - the
   * damage flash has the same problem and solves it the same way. A backdrop
   * sized to the arena would leave the clear colour showing at the edges on a
   * 1366x768 window.
   */
  private fitBackdrop(): void {
    if (!this.backdrop) {
      return;
    }
    const { left, top, right, bottom } = this.visibleBounds;
    this.backdrop.setPosition((left + right) / 2, (top + bottom) / 2);
    this.backdrop.setSize(right - left, bottom - top);
    /*
     * Scaled so ONE tile spans the visible width, whatever the window shape.
     *
     * The masters are seamless top to bottom and are 1280 wide precisely
     * because the arena is - so a tile was never meant to repeat sideways. But
     * the camera is RESIZE, not FIT: on a window wider than 16:9 it shows more
     * than 1280 arena units across, the tile wrapped inside its own sprite,
     * and the wrap drew a vertical line down the right of the arena. It was
     * faint on the nebulae and obvious the moment a map had structure in it.
     *
     * The same factor on BOTH axes, which is the whole point: stretching only
     * x would fix the line by distorting the artwork instead. Vertical tiling
     * is unaffected - scaling a seamless wrap keeps it seamless - and on a
     * 16:9 window the factor is 1 and nothing moves at all.
     */
    const cover = Math.max(1, (right - left) / ARENA_W);
    this.backdrop.setTileScale(cover, cover);
    /*
     * Kept, because the ground life has to agree with it.
     *
     * `tilePositionY` is an offset in TEXTURE space, so magnifying the tile by
     * `cover` magnifies the distance the ground appears to travel per unit of
     * offset. The creatures lying on that ground move in ARENA units, and were
     * moving at the unscaled figure - so on any window wider than 16:9 the
     * valley slid out from under its own maggots. At the usual 1920 canvas the
     * factor is about 1.2, which is a fifth too slow and plainly visible.
     */
    this.backdropCover = cover;
  }

  /** The backdrop the scene is currently drawing. Diagnostics and tests. */
  currentMapBackground(): MapBackground {
    return this.mapBackground;
  }

  /**
   * Says a scene-level failure out loud instead of leaving a black rectangle.
   *
   * Goes to the console (where a `pageerror`-watching test sees it) and to
   * React through the bridge, so the UI can tell the player something honest
   * rather than showing an arena that silently stopped.
   */
  /**
   * **Pede a folha desta família neste mapa, e recusa em voz alta** (V28I.BN).
   *
   * Um índice sem arte — fora de 1 a 20, ou reservado para depois do alfa —
   * lança, e a recusa é dita **uma vez por índice** pelo mesmo canal por onde
   * uma adopção falhada já é dita. Não há recuo para o mapa 1: desenhar
   * `alien1` no lugar de `alien7` seria mentir sobre que criatura está no ecrã.
   */
  private requestMapBody(kind: HuntMapBodyKind, mapIndex: number, startNow: boolean): void {
    const memo = `${kind}:${mapIndex}`;
    if (this.refusedMapArt.has(memo)) {
      return;
    }
    try {
      requestHuntMapBody({
        scene: this,
        manifest: this.options.manifest,
        kind,
        mapIndex,
        pending: this.pendingMapArt,
        startNow,
      });
    } catch (error) {
      this.refusedMapArt.add(memo);
      this.reportSceneFailure('art', error);
    }
  }

  /**
   * **Põe as duas folhas deste mapa no ar, e larga as do anterior.**
   *
   * A ordem importa e é esta: o destino é pedido ANTES de o anterior ser
   * largado, para uma viagem de ida e volta ao mesmo mapa não largar a folha que
   * ela própria acabou de pedir. E só se larga o que já não está a ser
   * desenhado — quem chama isto na adopção fá-lo depois de `clearEntityViews`.
   *
   * **Um par de cada vez.** A caça carrega quatro texturas, e a existência de
   * vinte mapas não muda isso: vinte pares seriam uma retenção que sobe a cada
   * viagem e nunca desce.
   */
  private adoptMapArt(mapIndex: number): void {
    this.requestMapBody('mob', mapIndex, true);
    this.requestMapBody('boss', mapIndex, true);
    const previous = this.loadedMapArt;
    this.loadedMapArt = mapIndex;
    if (previous === null || previous === mapIndex) {
      return;
    }
    for (const kind of ['mob', 'boss'] as const) {
      releaseHuntMapBody({ scene: this, kind, mapIndex: previous, pending: this.pendingMapArt });
    }
  }

  private reportSceneFailure(
    stage: 'adopt-session' | 'adopt-views' | 'reconfigure' | 'art',
    error: unknown,
  ): void {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`hunt scene failure (${stage}): ${message}`);
    this.options.bridge.sendToUi('hunt:scene-error', { stage, message });
  }

  private registerBridge(bridge: GameBridge): void {
    this.unsubscribers.push(
      bridge.onGameMessage('ui:set-paused', ({ paused }) => {
        this.clock.setPaused(paused);
      }),
      bridge.onGameMessage('hunt:visible', ({ visible }) => {
        /*
         * Hidden, not stopped and not forgotten.
         *
         * Phaser's own per-scene visibility, which is one flag on the scene's
         * systems: the display list, every sprite, the simulation, the clock,
         * the snapshot and these very subscriptions all survive untouched, and
         * an authoritative bootstrap arriving while this is false is adopted
         * exactly as it would be otherwise. Walking the objects to hide them
         * one at a time would do the same thing worse, and would have to be
         * kept in step with every object the scene ever gains.
         *
         * A duel needs this ON TOP of the pause, because the two answer
         * different questions: the pause stops the hunt's clock, and a stopped
         * hunt still draws its last frame onto the canvas both scenes share.
         */
        this.sys.setVisible(visible);
      }),
      bridge.onGameMessage('hunt:session', (session) => {
        const tickBefore = DEV_TOOLS_ALLOWED ? this.sim.getTickMs() : 0;
        this.adoptSession(session);
        // The one source allowed to move the clock backwards: a bootstrap, a
        // restart or a travel is a different run, and says so.
        this.recordAdoption('session', tickBefore, true);
      }),
      bridge.onGameMessage('hunt:reconcile', ({ progression }) => {
        // The server's answer wins over the local prediction. Entities,
        // timers and the RNG are untouched, so nothing visual jumps.
        this.sim.applyAuthoritativeProgression(progression);
        this.pushSnapshot();
      }),
      bridge.onGameMessage('hunt:reconfigure', ({ snapshot, loadout, artifactEffects }) => {
        // Same hunt, new numbers. The authoritative snapshot already carries
        // the spent points and the adjusted health, and every entity in it is
        // the one already on screen - so the simulation is reconciled and the
        // views simply follow their ids on the next frame. Nothing is cleared,
        // nothing is rebuilt, and no restart is announced.
        const tickBefore = DEV_TOOLS_ALLOWED ? this.sim.getTickMs() : 0;
        try {
          // An equip also changes the CONFIG the run is simulated with, so the
          // restored simulation is built on the new equipment. The snapshot
          // still holds the old shots with their original damage: the server
          // produced it that way, and restoring does not rewrite them.
          const config = {
            ...this.sim.config,
            ...(loadout
              ? {
                  shipIndex: loadout.ship.shipIndex,
                  shipLevel: loadout.ship.level,
                  cannonRarity: loadout.cannon.rarity,
                  cannonLevel: loadout.cannon.level,
                }
              : {}),
            // An artifact obtained or merged changes the numbers the same way
            // a swap does, and changes nothing visible at all: artifacts are
            // not drawn on the ship, so no texture is touched here.
            ...(artifactEffects ? { artifactEffects } : {}),
          };
          if (snapshot) {
            // Those events were already rendered by the run this replaces, and
            // a run that arrives dead still has to say so - see
            // `adoptAuthoritativeSimulation`.
            this.adoptAuthoritativeSimulation(HuntSimulation.restore(config, snapshot));
          } else {
            /*
             * No authority, so nothing is replaced.
             *
             * A non-economic session is settled by nothing, and the snapshot on
             * its row still describes the moment it was created. Adopting it
             * would rewind the arena to that moment. The change itself is not
             * in doubt, though - the vault decided it - so it is applied to the
             * run in progress, which is what `applyLoadout` exists for: shots
             * already in the air keep the damage they were born with, the
             * attack accumulator is untouched, health follows the damage
             * already taken, and a dead ship stays dead.
             */
            this.sim.applyLoadout({
              ...(loadout
                ? {
                    shipIndex: loadout.ship.shipIndex,
                    shipLevel: loadout.ship.level,
                    cannonRarity: loadout.cannon.rarity,
                    cannonLevel: loadout.cannon.level,
                  }
                : {}),
              ...(artifactEffects ? { artifactEffects } : {}),
            });
          }
          if (loadout) {
            this.session = { ...this.session, loadout };
            this.applyEquippedArt(loadout);
          }
          if (artifactEffects) {
            this.session = { ...this.session, artifactEffects };
          }
          this.syncShipOrientation();
          this.pushSnapshot();
          this.recordAdoption('reconfigure', tickBefore, snapshot !== null);
        } catch (error) {
          this.reportSceneFailure('reconfigure', error);
        }
      }),
      bridge.onGameMessage('hunt:artifacts', ({ artifactEffects }) => {
        // The domain owns this rule: maximum health moves, the damage already
        // taken is preserved rather than healed away, a dead ship is not
        // revived, the attack accumulator is not reset, and projectiles keep
        // the damage they were born with. Nothing visual changes at all -
        // artifacts are not drawn on the ship.
        try {
          this.sim.applyLoadout({ artifactEffects });
          this.session = { ...this.session, artifactEffects };
          this.pushSnapshot();
        } catch (error) {
          this.reportSceneFailure('reconfigure', error);
        }
      }),
      bridge.onGameMessage('hunt:pet-appearance', ({ activePet }) => {
        /*
         * Training or appearance changed. NOTHING about the run moves.
         *
         * No snapshot arrives and none is applied: the simulation keeps its
         * mobs, its boss countdown, its RNG streams and every bullet already
         * in the air. The session's copy of the pet is refreshed so the
         * overlay re-reads the colour and the finish, and the training lands
         * on the next volley rather than retroactively on the last one - the
         * server settles the window it already owed before it writes.
         */
        this.session = { ...this.session, activePet };
        /*
         * The new energy applies from the NEXT shot on.
         *
         * Bullets already in the air keep the energy they were fired with -
         * each view froze it - so switching colour never repaints a volley
         * retroactively, exactly as a training point never applies backwards.
         */
        this.petEnergy = findEnergyColor(activePet?.energyId ?? '') ?? null;
        /*
         * The BEAM, unlike a bullet, travels to the new colour.
         *
         * It is connected to the pet rather than launched from it, so there is
         * nothing in flight to repaint retroactively - and restarting the fade
         * here is what makes the change a 150 ms transition instead of a cut.
         * It touches nothing else: the lock, the accumulator and the fight
         * carry on untouched.
         */
        this.beamFadeMs = 0;
        if (activePet) {
          this.sim.applyPet({
            definitionId: activePet.definitionId,
            evolution: activePet.evolution,
            damageFoodCount: activePet.training.damageFoodCount,
            attackSpeedFoodCount: activePet.training.attackSpeedFoodCount,
          });
        }
      }),
      bridge.onGameMessage('hunt:pet', ({ activePet, snapshot }) => {
        // The escort changed - activated, deactivated or evolved. The SAME
        // run continues: mobs, boss countdown, RNG streams, metrics and every
        // bullet in the air are untouched, and only the model and the muzzle
        // count differ from here on.
        const tickBefore = DEV_TOOLS_ALLOWED ? this.sim.getTickMs() : 0;
        try {
          this.session = { ...this.session, activePet };
          this.petEnergy = findEnergyColor(activePet?.energyId ?? '') ?? null;
          /*
           * A snapshot arrives only when it is authoritative.
           *
           * `null` means the session is non-economic: the server holds a stub
           * that never advanced, and restoring from it would take the run back
           * to the state the session was created in - mobs gone, countdown
           * rolled back, simulated time REDUCED. There is nothing to adopt, so
           * the pet is applied to the run already in progress and nothing else
           * moves, which is what this message has always promised.
           */
          if (snapshot) {
            /*
             * Those events belonged to the run this replaces, and a run that
             * arrives dead still has to say so - see
             * `adoptAuthoritativeSimulation`. Nothing is drained when nothing
             * was adopted: that would swallow hits the player is watching land.
             *
             * Before `applyPet` rather than after, which is the same thing:
             * `applyPet` moves the pet's numbers and emits no presentation
             * event at all.
             */
            this.adoptAuthoritativeSimulation(HuntSimulation.restore(this.sim.config, snapshot));
          }
          this.sim.applyPet(
            activePet
              ? { definitionId: activePet.definitionId, evolution: activePet.evolution }
              : null,
          );
          if (!activePet) {
            this.petSprite?.setVisible(false);
          }
          this.pushSnapshot();
          this.recordAdoption('pet', tickBefore, snapshot !== null);
        } catch (error) {
          this.reportSceneFailure('reconfigure', error);
        }
      }),
      bridge.onGameMessage('hunt:pet-render', ({ mode }) => {
        // DEV/E2E ONLY, and presentation ONLY: the simulation is untouched, so
        // damage, cadence, targeting and rewards are identical in both modes.
        this.setPetRenderMode(mode);
      }),
      bridge.onGameMessage('hunt:pet-emitter', ({ origin }) => {
        // Where the 3D model's crystal actually is this frame. Only trusted in
        // 3D mode: in 2D there is no model and the sprite has no hardpoints.
        this.petEmitter = this.petRenderMode === 'pet-3d' ? origin : null;
      }),
      bridge.onGameMessage('hunt:pet-muzzles', ({ origins }) => {
        // The 3D overlay's answer to `hunt:pet-volley`: where its guns really
        // are, after the banking and the rotation. Drawn only in 3D mode - a
        // stale answer arriving after a switch back to 2D would put flashes
        // where the sprite's guns are not.
        if (this.petRenderMode === 'pet-3d') {
          this.flashPetMuzzles(origins);
        }
      }),
      bridge.onGameMessage('hunt:correct', ({ snapshot }) => {
        // A real divergence: continue from the server's state instead of our
        // own. The scene, its canvas and every texture stay exactly as they
        // are - only the simulation behind them is replaced.
        const tickBefore = DEV_TOOLS_ALLOWED ? this.sim.getTickMs() : 0;
        this.adoptAuthoritativeSimulation(HuntSimulation.restore(this.sim.config, snapshot));
        this.clearEntityViews();
        this.syncShipOrientation();
        this.pushSnapshot();
        this.recordAdoption('correct', tickBefore, true);
      }),
      bridge.onGameMessage('hunt:card-activated', ({ snapshot }) => {
        /*
         * A ORDEM foi respondida pela autoridade (gate V28H.I).
         *
         * A instantânea é adoptada em qualquer dos casos - aceite ou recusada -
         * porque em ambos ela é o estado real: a ordem passou por uma
         * liquidação, portanto o tique andou mesmo quando a carta não ligou.
         *
         * A cena não desenha nada por causa desta mensagem. Os dois canhões
         * nascem de `superCannonPositions()`, que lê a simulação; o clarão nasce
         * do evento `card-volley` que a própria simulação emite. Uma segunda
         * fonte de verdade para o mesmo efeito é como um efeito passa a aparecer
         * duas vezes.
         */
        const tickBefore = DEV_TOOLS_ALLOWED ? this.sim.getTickMs() : 0;
        this.adoptAuthoritativeSimulation(HuntSimulation.restore(this.sim.config, snapshot));
        this.clearEntityViews();
        this.syncShipOrientation();
        this.pushSnapshot();
        this.recordAdoption('correct', tickBefore, true);
      }),
      bridge.onGameMessage('hunt:bleed-detonated', ({ snapshot, detonated }) => {
        /*
         * A ORDEM foi executada pela autoridade (gate V28G.N).
         *
         * O efeito PRIMEIRO, porque ele precisa de saber onde os alvos estavam - e
         * a adoção abaixo substitui a simulação, o que apaga um alvo que a
         * detonação tenha matado.
         */
        for (const target of detonated) {
          const anchor = this.bleedAnchors.get(target.targetId);
          if (anchor === undefined) {
            continue;
          }
          this.bleedBursts?.spawn(
            {
              key: `${snapshot.tickMs}:${target.targetId}`,
              x: anchor.x,
              /* O CASCO, e não a faixa: as marcas convergem para ele e rompem lá. */
              y: anchor.top + anchor.diameter / 2,
              diameter: anchor.diameter,
              marks: target.marks,
              /*
               * A semente sai do TIQUE autoritativo e do alvo, nunca de
               * `Math.random()`: uma re-simulação do mesmo instante desenha os
               * mesmos fragmentos.
               */
              seed: (snapshot.tickMs * 2_654_435_761 + target.targetId) >>> 0,
              /*
               * O valor REALMENTE aplicado, guardado com o efeito.
               *
               * Um por ALVO, que é o que o contrato pede: uma detonação é uma
               * instância de dano por casco, e não uma por marca. Impresso pelo
               * `onFinale` do sistema, quando o corte abre - o dono pediu que o
               * total apareça só no fim da sequência.
               */
              damage: target.applied,
            },
            this.time.now,
          );
        }
        /*
         * E o estado autoritativo é adotado NO LUGAR, como uma correção é.
         *
         * A cena, o canvas e as texturas ficam exatamente onde estão: o que muda é
         * a simulação por trás delas - as marcas saíram, e a vida dos alvos desceu.
         */
        const tickBefore = DEV_TOOLS_ALLOWED ? this.sim.getTickMs() : 0;
        this.adoptAuthoritativeSimulation(HuntSimulation.restore(this.sim.config, snapshot));
        this.clearEntityViews();
        this.syncShipOrientation();
        this.pushSnapshot();
        this.recordAdoption('correct', tickBefore, true);
      }),
      bridge.onGameMessage('hunt:dev', ({ command }) => {
        if (!DEV_TOOLS_ALLOWED || !this.options.debug) {
          return; // dev commands are inert unless the BUILD allows tooling
        }
        if (command === 'kill-ship') {
          this.sim.devForceDeath();
        } else if (command === 'toggle-rapid-fire') {
          // Cadence only. Never a change of how a shot is drawn.
          this.rapidFire = !this.rapidFire;
        } else if (command === 'force-bomb') {
          /*
           * DEV ONLY: a PRÓXIMA salva traz uma bomba (gate V28G.J).
           *
           * Uma comparação, e nada mais. A rolagem continua a sair do fluxo -
           * portanto ligar isto não desloca um spawn, um temporizador de boss
           * nem um proc - e o dano, o raio, a velocidade, a colisão e o empurrão
           * saem todos do caminho normal. Uma nave sem a joia continua a não
           * produzir bomba nenhuma.
           */
          this.sim.devForceNextVolleyBomb();
        } else if (command === 'force-missile') {
          /*
           * DEV ONLY: a PRÓXIMA salva lança um míssil (gate V28G.L).
           *
           * Uma comparação, e nada mais - a gémea exata do `force-bomb`. A
           * rolagem continua a sair do fluxo, portanto ligar isto não desloca um
           * spawn, um temporizador de boss nem um proc; e o dano, o alvo, a
           * velocidade, o giro, a colisão e o tempo de vida saem todos do caminho
           * normal. Uma nave sem a joia continua a não lançar míssil nenhum.
           */
          this.sim.devForceNextVolleyMissile();
        } else if (command === 'force-poison') {
          /*
           * DEV ONLY: o PRÓXIMO acerto direto envenena (gate V28G.M).
           *
           * Uma comparação, e nada mais - o irmão exato dos dois de cima. A
           * rolagem continua a sair do fluxo, portanto ligar isto não desloca um
           * spawn, um temporizador de boss nem um proc; e a magnitude, a
           * duração, as seis parcelas e a remoção saem todas do caminho normal.
           * Uma nave sem a joia continua a não envenenar coisa nenhuma.
           */
          this.sim.devForceNextPoison();
        } else if (command === 'force-bleeding') {
          /*
           * DEV ONLY: o PRÓXIMO acerto direto marca (gate V28G.N).
           *
           * Uma comparação, e nada mais - o irmão exato dos três de cima. A
           * rolagem continua a sair do fluxo, portanto ligar isto não desloca um
           * spawn, um temporizador de boss nem um proc; e a magnitude congelada, a
           * autoria e a remoção saem todas do caminho normal. Uma nave sem a joia
           * continua a não marcar coisa nenhuma.
           *
           * E não DETONA: detonar é uma ordem do jogador, e nenhuma afordância de
           * depuração a dá.
           */
          this.sim.devForceNextBleeding();
        }
      }),
      bridge.onGameMessage('hunt:dev-cannon', ({ rarity, level }) => {
        if (!DEV_TOOLS_ALLOWED || !this.options.debug) {
          return; // inert unless the BUILD allows tooling
        }
        this.equipCannonForTesting(rarity, level);
      }),
      bridge.onGameMessage('hunt:dev-allocate', (allocation) => {
        if (!DEV_TOOLS_ALLOWED || !this.options.debug) {
          return; // a real allocation always goes through the server
        }
        this.sim.allocatePoints(allocation);
        this.pushSnapshot();
      }),
    );
  }

  /**
   * DEV ONLY - swaps the cannon of a LOCAL, non-economic simulation.
   *
   * It rebuilds the simulation in the browser and touches nothing on the
   * server: the account's loadout, its session and its `hunt_sessions.loadout`
   * copy are unchanged, and every checkpoint is still re-simulated server-side
   * on the equipment the session was created with. So this can change what a
   * developer SEES; it cannot change what anyone EARNS. The dev panel says so.
   */
  private equipCannonForTesting(rarity: CannonRarity, level: number): void {
    this.sim = new HuntSimulation({
      mapIndex: this.session.mapIndex,
      seed: simulationSeed(BigInt(this.session.seed)),
      // The real equipped ship stays; only the cannon under test changes.
      shipIndex: this.session.loadout.ship.shipIndex,
      shipLevel: this.session.loadout.ship.level,
      cannonRarity: rarity,
      cannonLevel: level,
      initialProgression: this.session.progression,
      ...(this.bossFast ? { debugBossDelaySeconds: 10 } : {}),
      ...(this.rapidFire ? { devAttackSpeedBoost: 40 } : {}),
    });
    this.projectileColor = Phaser.Display.Color.HexStringToColor(
      CANNON_CATALOG[rarity].projectileColor,
    ).color;
    this.dashTexture = ensureDashTexture(this, this.projectileColor);
    this.deadNotified = false;
    this.clearEntityViews();
    this.rebuildCannonSprites();
    this.syncShipOrientation();
    this.options.bridge.sendToUi('hunt:restarted', {});
    this.pushSnapshot();
  }

  /**
   * Builds the pooled starfield.
   *
   * Every sprite is allocated ONCE here and then recycled forever: a star that
   * leaves the bottom is repositioned above the top rather than destroyed and
   * replaced. Textures are procedural, cached per (kind, colour), and shared by
   * every star that uses them.
   */
  private createStars(): void {
    this.stars = [];
    // Three shared white textures for the whole sky; colour comes from tint,
    // which keeps every star of a kind in one batched draw call.
    const pointKey = ensureStarGlowTexture(this);
    const crossKey = ensureStarCrossTexture(this);
    const streakKey = ensureStarStreakTexture(this);

    for (const layer of STAR_LAYERS) {
      // The rare cross-flare stars come first so they are easy to count.
      for (let i = 0; i < layer.count; i += 1) {
        const tint = STAR_TINTS[Phaser.Math.Between(0, STAR_TINTS.length - 1)] ?? 0xffffff;
        const isCross = i < layer.crossCount;
        const key = layer.streak ? streakKey : isCross ? crossKey : pointKey;

        // Seeded across the whole VISIBLE rectangle, which may reach well
        // outside the logical arena on a wide window.
        const sprite = this.add.image(
          Phaser.Math.FloatBetween(this.visibleBounds.left, this.visibleBounds.right),
          Phaser.Math.FloatBetween(this.visibleBounds.top, this.visibleBounds.bottom),
          key,
        );
        const baseAlpha = Phaser.Math.FloatBetween(layer.minAlpha, layer.maxAlpha);
        sprite
          .setTint(tint)
          .setScale(Phaser.Math.FloatBetween(layer.minScale, layer.maxScale))
          .setAlpha(baseAlpha)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(layer.depth);
        if (layer.streak) {
          // Streaks point along their travel: straight down.
          sprite.setRotation(Math.PI / 2);
        }
        this.stars.push({
          sprite,
          speed: layer.speed,
          baseAlpha,
          twinkleAmplitude: layer.twinkle,
          // Independent phase and rate: stars shimmer out of step, gently.
          twinklePhase: Phaser.Math.FloatBetween(0, Math.PI * 2),
          twinkleRate: Phaser.Math.FloatBetween(0.6, 1.8),
        });
      }
    }
  }

  private createShip(): void {
    const ship = this.sim.shipPosition();
    const height = HUNT_BALANCE.ship.displayHeight;
    this.shipSprite = this.add.image(ship.x, ship.y, 'ship');
    this.shipSprite.setScale(height / this.shipSprite.height).setDepth(30);
    this.rebuildCannonSprites();
    this.syncShipOrientation();
  }

  /**
   * One sprite per cannon in the equipped formation.
   *
   * The count comes from the simulation, so the drawn wing and the muzzles
   * shots are born at are always the same set of positions.
   */
  private rebuildCannonSprites(): void {
    for (const sprite of this.cannonSprites) {
      sprite.destroy();
    }
    /*
     * Os temporários usam a MESMA textura, portanto morrem aqui também.
     *
     * Isto cobre troca de equipamento, mudança de mundo, morte e a adopção de
     * uma simulação corrigida pelo servidor: todas passam por esta função. Se a
     * habilidade continuar activa, o próximo quadro recria-os já com a arte
     * nova - nunca com a textura da nave anterior.
     */
    this.clearSuperCannons();
    // The art follows the equipped rarity, resolved through the typed catalog.
    const textureKey = this.cannonTextureForEquipped();
    this.cannonSprites = this.sim.cannonPositions().map((position) => {
      const sprite = this.add.image(position.x, position.y, textureKey);
      sprite.setScale(HUNT_BALANCE.ship.cannonDisplaySize / sprite.height).setDepth(29);
      return sprite;
    });
  }

  /**
   * Repaints the ship, the cannons and the projectile tint for new equipment.
   *
   * Only ART: the entities, the timers, the RNG and every shot in flight are
   * the simulation's business and were already restored from the server's
   * snapshot. Textures for a rarity the build did not preload are fetched on
   * demand, and the sprite is swapped when the load completes - a missing
   * texture leaves the previous art rather than a blank frame.
   */
  private applyEquippedArt(loadout: LoadoutWire): void {
    this.projectileColor = Phaser.Display.Color.HexStringToColor(
      CANNON_CATALOG[loadout.cannon.rarity].projectileColor,
    ).color;
    this.dashTexture = ensureDashTexture(this, this.projectileColor);

    const shipAssetId = `ship${loadout.ship.shipIndex}`;
    this.loadTextureThen(`ship-${loadout.ship.shipIndex}`, shipAssetId, (key) => {
      /*
       * The base hull is REMEMBERED first, and only drawn when nothing is worn.
       *
       * Writing the ship texture unconditionally would undress a skin every
       * time the loadout was re-applied - a bootstrap, a point spend, an equip -
       * because this callback and `applySkin` set the same sprite. Recording it
       * either way is what lets `applySkin(null, null)` restore the RIGHT hull
       * later rather than whatever ship the account started with.
       */
      this.baseHullTextureKey = key;
      if (!this.skinRenderer) {
        this.shipSprite?.setTexture(key);
      }
    });

    const cannonAsset = cannonAssetId(loadout.cannon.rarity);
    if (cannonAsset) {
      this.loadTextureThen(cannonTextureKey(loadout.cannon.rarity), cannonAsset, () => {
        this.rebuildCannonSprites();
      });
    } else {
      this.rebuildCannonSprites();
    }

    /*
     * And LAST, the skin the server says is worn.
     *
     * After the projectile colour, the hull texture and the battery, so the
     * same ordering argument that holds on the server holds here: everything a
     * hunt is configured from is already decided when the cosmetic arrives.
     *
     * This is also the only place it needs to be. `applyEquippedArt` already
     * runs at every authoritative point the product has - a bootstrap, a
     * session swap, a reconfigure after spending a point, an equip - so wiring
     * the skin here gives it those for free, instead of a second event and a
     * second source of truth that could disagree.
     */
    void this.applyWornSkin(loadout.skin ?? null);
  }

  /**
   * Dresses the ship from the AUTHORITATIVE loadout, or undresses it.
   *
   * The wire says a `definitionId`; this build's own validated manifest decides
   * whether that names something drawable. Nothing the server sends becomes a
   * file name, and `instanceId` never reaches the picture - two copies of one
   * skin are two economic objects and one hull.
   */
  private async applyWornSkin(worn: LoadoutWire['skin']): Promise<void> {
    const manifest = await this.skinManifest();
    // The hunt's own profile. A scene never guesses a modality: this one is a
    // hunt, so it says so, and `PvpScene` says `pvp1x1` or `pvp3x3`.
    const next = resolveSkinContent(worn, manifest, 'hunt');

    if (!skinContentChanged(this.wornSkin, next)) {
      /*
       * Swapping between two copies of the same skin lands here.
       *
       * Rebuilding would restart every emitter and drop every particle in
       * flight to redraw an identical ship - a visible flicker caused by an
       * economic change the picture does not have. The wardrobe still shows the
       * new instance, because that is the wardrobe's fact and not this one's.
       */
      this.wornSkin = next;
      return;
    }
    this.wornSkin = next;

    if (!next) {
      // Explicit restoration, never "just stop drawing": the sprite keeps a
      // texture, an origin and a scale a skin may have changed.
      this.applySkin(null, null);
      return;
    }

    const url = `${manifest?.baseUrl ?? ''}${next.plan.assetFile}`;
    this.loadSkinTextureThen(next.textureKey, url, () => {
      /*
       * Re-checked after the load, because it is asynchronous.
       *
       * A player who equips and then unequips before the PNG arrives must not
       * be dressed by the callback of a decision they already reversed.
       */
      if (this.wornSkin?.textureKey === next.textureKey) {
        this.applySkin(next.plan, next.textureKey);
      }
    });
  }

  /**
   * The skin sidecar, fetched at most once per scene.
   *
   * Held as a promise rather than as a value so two loadout changes arriving
   * together share one request instead of racing two.
   */
  private skinManifest(): Promise<SkinRuntimeManifest | null> {
    this.skinManifestPromise ??= fetchSkinManifest().catch(() => null);
    return this.skinManifestPromise;
  }

  /**
   * Loads one skin PNG, then runs `then`. A failure leaves the normal hull.
   *
   * Separate from `loadTextureThen` because a skin's URL comes from the skin
   * manifest's own base rather than from the asset manifest, and because a
   * missing skin must fall back silently rather than leave the previous art.
   */
  private loadSkinTextureThen(key: string, url: string, then: () => void): void {
    /*
     * The five painted lights ride along in the SAME batch as the hull.
     *
     * One loader run, one `COMPLETE`. Queuing them separately would mean two
     * `once(COMPLETE)` handlers racing, and the second would fire on the first
     * batch - which is how a skin gets applied before its own art has arrived.
     * Shared across every skin, so this is a no-op after the first load.
     */
    const missingLights = SKIN_VFX_TEXTURES.filter((one) => !this.textures.exists(one.key));
    if (this.textures.exists(key) && missingLights.length === 0) {
      then();
      return;
    }
    for (const light of missingLights) {
      this.load.image(light.key, `${SKIN_VFX_BASE_URL}${light.file}`);
    }
    if (!this.textures.exists(key)) {
      this.load.image(key, url);
    }
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      if (this.textures.exists(key)) {
        then();
        return;
      }
      // The PNG did not arrive. The normal hull is the honest outcome, and the
      // scene reports it once rather than retrying into a loop.
      this.wornSkin = null;
      this.applySkin(null, null);
      this.reportSkinAssetMissing(key);
    });
    this.load.start();
  }

  /** Says a skin asset was missing, once per key. Never per frame. */
  private reportSkinAssetMissing(key: string): void {
    if (this.reportedMissingSkins.has(key)) {
      return;
    }
    this.reportedMissingSkins.add(key);
    console.warn(`[skins] asset "${key}" did not load; drawing the normal hull.`);
  }

  /**
   * Ensures a manifest texture exists, then runs `then` with its key.
   *
   * A hunt preloads only what it is flying, so equipping something new has to
   * fetch its art. Nothing here invents an image: an asset the manifest does
   * not know about is simply not swapped in.
   */
  private loadTextureThen(key: string, assetId: string, then: (key: string) => void): void {
    if (this.textures.exists(key)) {
      then(key);
      return;
    }
    const url = staticAssetUrl(this.options.manifest, assetId);
    if (!url) {
      return;
    }
    /*
     * **Pelo registo do jogo inteiro** (gate V28I.CF.3). Uma chave que outra cena
     * já está a buscar não é pedida outra vez: esta junta-se à espera. Era um
     * segundo carregador para a mesma chave que punha "Texture key already in
     * use: cannon-anomaly" na consola ao trocar para a caça.
     */
    requestSharedTexture(this, key, url, (loaded) => {
      if (loaded) {
        then(key);
      }
    });
  }

  /**
   * Texture of the equipped cannon.
   *
   * A production build only loads the starting Excellent art, so if a rarity
   * were ever equipped without its texture present we fall back rather than
   * render a blank sprite.
   */
  private cannonTextureForEquipped(): string {
    const key = cannonTextureKey(this.sim.getOverviewSnapshot().cannonRarity);
    return this.textures.exists(key) ? key : cannonTextureKey(this.session.loadout.cannon.rarity);
  }

  /**
   * Points the ship art (and the cannon riding in front of it) along the
   * simulation's facing.
   *
   * The facing itself is computed and rate-limited inside the simulation, so
   * the drawn nose and the muzzle a shot is actually born at can never
   * disagree. Sprite art points up, which is `-PI/2` in world terms - hence
   * the quarter-turn offset.
   */
  /* ------------------------------------------------------------------ */
  /*  Skins (ADR 0041)                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * Wears a skin, or takes it off. Presentation only.
   *
   * The hull IMAGE changes texture, origin and scale; nothing else does. The
   * simulation's position, facing, hitbox, damage, cadence and muzzle are
   * untouched - this method has no way to reach any of them - so a ship wearing
   * a skin flies, collides and shoots exactly as the same ship without one.
   *
   * `null` restores the base hull art. A skin whose texture failed to load also
   * restores it, which is the one behaviour worth stating: a missing image
   * leaves a ship somebody can still see and aim at, never a hole.
   *
   * There is no `if` by skin id here or anywhere below. The renderer consumes a
   * compiled plan and cannot tell which skin it is drawing.
   */
  applySkin(plan: SkinVisualPlan | null, textureKey: string | null): void {
    if (this.skinRenderer) {
      this.skinRendererTeardowns += 1;
    }
    this.skinRenderer?.destroy();
    this.skinRenderer = null;

    const sprite = this.shipSprite;
    if (!sprite) {
      return;
    }
    if (!plan || !textureKey || !this.textures.exists(textureKey)) {
      /*
       * Back to the base hull, explicitly. Restoring the origin matters as much
       * as the texture: a skin may have moved it, and an ordinary hull drawn
       * about somebody else's pivot would sit off its own position.
       */
      if (sprite.texture.key !== this.baseHullTextureKey) {
        this.hullTextureSwaps += 1;
      }
      sprite.setTexture(this.baseHullTextureKey);
      sprite.setOrigin(0.5, 0.5);
      sprite.clearTint();
      sprite.setScale(HUNT_BALANCE.ship.displayHeight / sprite.height);
      this.syncShipOrientation();
      return;
    }
    if (sprite.texture.key !== textureKey) {
      this.hullTextureSwaps += 1;
    }
    this.skinRendererBuilds += 1;
    this.skinRenderer = new SkinRenderer({
      scene: this,
      plan,
      hull: sprite,
      textureKey,
    });
    this.syncShipOrientation();
  }

  /** Drops every skin particle in the air. For a teleport or a correction. */
  clearSkinResidue(): void {
    this.skinRenderer?.clearResidue();
  }

  /** How much the skin subsystem is costing, for the census. */
  skinCensus(): {
    emitters: number;
    particles: number;
    active: boolean;
    trailEmitting: boolean;
    travelDeg: number | null;
    trailParticles: number;
    speedHullsPerSecond: number;
  } {
    const motion = this.skinRenderer?.motion() ?? {
      trailEmitting: false,
      trailParticles: 0,
    };
    return {
      emitters: this.skinRenderer?.emitterCount() ?? 0,
      particles: this.skinRenderer?.aliveParticles() ?? 0,
      active: this.skinRenderer !== null,
      ...motion,
      /*
       * The ship's own drift, in hull diameters per second.
       *
       * Reported because the trail's gate is stated in that unit, and a gate
       * nobody can measure against is a gate nobody can prove is ever crossed.
       * This one was not: every descriptor asked for 0.65 and the hunt's ship
       * peaks at 0.5162, so the trail emitted nothing at all until this gate.
       */
      travelDeg: this.lastTravelDeg,
      speedHullsPerSecond: this.lastSkinSpeed / HUNT_BALANCE.ship.displayHeight,
    };
  }

  private syncShipOrientation(): void {
    const facing = this.sim.shipFacing();
    /*
     * The one contract, rather than the literal that used to be here.
     *
     * The quarter turn is the same fact in both modes - the art is drawn facing
     * up - and a duel got it wrong for the barrels precisely because it was a
     * literal in one file and an omission in another.
     */
    const rotation = spriteRotationForWorldAngle(facing);
    /*
     * Position AND rotation, from the simulation, every frame.
     *
     * The hull used to be placed once at creation because it never moved. Now
     * it wanders, and the cannons below already followed it - so leaving this
     * out would have flown the wing around an empty spot with the ship parked
     * where it started. Both come from `sim`, never from a tween or a
     * renderer-side path: the muzzle a shot is born at has to be the muzzle
     * the player is looking at.
     */
    const position = this.sim.shipPosition();
    /*
     * How the ship is really moving, measured whether or not it is wearing
     * anything.
     *
     * It used to be measured only inside the skin branch, which meant a bare
     * hull reported no direction at all - and a visual gate that wants to know
     * whether residue lands BEHIND the ship needs the same "behind" for the
     * bare control as for the skin it is compared with. Presentation only:
     * nothing derived here is fed back into the simulation.
     */
    const now = this.time.now;
    const deltaMs = this.lastSkinFrameMs === null ? 16 : Math.min(250, now - this.lastSkinFrameMs);
    this.lastSkinFrameMs = now;
    const previous = this.lastSkinShipAt;
    const moved = previous ? { x: position.x - previous.x, y: position.y - previous.y } : null;
    const speed = moved && deltaMs > 0 ? Math.hypot(moved.x, moved.y) / (deltaMs / 1000) : 0;
    if (moved && (moved.x !== 0 || moved.y !== 0)) {
      this.lastTravelDeg = Phaser.Math.RadToDeg(Math.atan2(moved.y, moved.x));
    }
    this.lastSkinShipAt = { x: position.x, y: position.y };
    this.lastSkinSpeed = speed;
    if (this.skinRenderer && this.shipSprite) {
      /*
       * A skin owns the hull's placement, because its pivot and its hull centre
       * may differ and the sprite has to be nudged so the SHIP still lands on
       * the position the simulation owns. The position handed in is that one,
       * unchanged - the renderer cannot move it.
       */
      this.skinRenderer.update(
        {
          x: position.x,
          y: position.y,
          rotation,
          hullWorldUnits: HUNT_BALANCE.ship.displayHeight,
          visualScale: 1,
          speed,
        },
        deltaMs,
      );
    } else {
      this.shipSprite?.setPosition(position.x, position.y);
      this.shipSprite?.setRotation(rotation);
    }
    // Every cannon rides its own slot and shares the ship's rotation, so the
    // whole wing turns as one rigid body.
    const positions = this.sim.cannonPositions();
    for (let i = 0; i < this.cannonSprites.length; i += 1) {
      const sprite = this.cannonSprites[i];
      const position = positions[i];
      if (!sprite || !position) {
        continue;
      }
      sprite.setPosition(position.x, position.y);
      sprite.setRotation(rotation);
    }
    this.syncSuperCannons(rotation);
  }

  /**
   * **Os dois canhões temporários** (gate V28H.H).
   *
   * ## O que ele lê, e o que ele decide
   *
   * Lê a posição autoritativa dos dois e se a habilidade está activa. Não decide
   * nada: se eles existem, quando desaparecem e onde estão são respostas da
   * simulação, e esta função só as desenha.
   *
   * ## Duas cópias ampliadas da arte que já está carregada
   *
   * A textura do canhão equipado, ao dobro da escala. Não há PNG novo, não há
   * textura gerada e não há atlas: é a mesma `Image` que o casco já usa, duas
   * vezes, com `setScale` a dobrar.
   *
   * ## A montagem e o desaparecimento
   *
   * Curtos e por `alpha` + `scale`, num tween que substitui o anterior em vez de
   * se acumular. "Armamento pesado temporário" é o que se quer ler: eles entram
   * a crescer com um brilho quente e saem a encolher. Nada aqui pulsa, orbita ou
   * segue de longe - isso leria como pet ou escudo.
   */
  private syncSuperCannons(rotation: number): void {
    const positions = this.sim.superCannonPositions();
    const active = positions.length > 0;

    if (active && this.superCannonSprites.length === 0) {
      const textureKey = this.cannonTextureForEquipped();
      const base = HUNT_BALANCE.ship.cannonDisplaySize * HUNT_BALANCE.cards.superCannons.artScale;
      this.superCannonSprites = positions.map(() => {
        const sprite = this.add.image(0, 0, textureKey);
        sprite
          .setScale(base / sprite.height)
          /*
           * ATRÁS dos canhões equipados (29) e do casco.
           *
           * Eles montam-se fora da asa, e uma profundidade menor garante que,
           * onde quer que a nave gire, o casco e a formação continuam a ler-se
           * por cima em vez de serem tapados por uma peça temporária.
           */
          .setDepth(27)
          .setVisible(false);
        return sprite;
      });
    }

    if (!active) {
      if (this.superCannonsShown) {
        /* Sai a encolher: o mesmo gesto da entrada, ao contrário. */
        this.superCannonTween?.remove();
        this.superCannonTween = this.tweens.add({
          targets: this.superCannonSprites,
          alpha: 0,
          duration: 180,
          onComplete: () => {
            for (const sprite of this.superCannonSprites) {
              sprite.setVisible(false);
            }
          },
        });
        this.superCannonsShown = false;
      }
      return;
    }

    for (const [index, sprite] of this.superCannonSprites.entries()) {
      const position = positions[index];
      if (!position) {
        continue;
      }
      sprite.setPosition(position.x, position.y);
      sprite.setRotation(rotation);
    }

    if (!this.superCannonsShown) {
      const base = HUNT_BALANCE.ship.cannonDisplaySize * HUNT_BALANCE.cards.superCannons.artScale;
      this.superCannonTween?.remove();
      for (const sprite of this.superCannonSprites) {
        sprite
          .setVisible(true)
          .setAlpha(0)
          .setScale((base / sprite.height) * 0.55);
      }
      this.superCannonTween = this.tweens.add({
        targets: this.superCannonSprites,
        alpha: 1,
        /*
         * A escala final é a mesma de `setScale` acima, recalculada por sprite
         * porque `scale` num tween é um número e não uma função da altura.
         */
        scale: base / (this.superCannonSprites[0]?.height ?? base),
        ease: 'Back.easeOut',
        duration: 220,
      });
      this.superCannonsShown = true;
    }
  }

  /** Destroys the two temporary cannons and the tween that animates them. */
  private clearSuperCannons(): void {
    this.superCannonTween?.remove();
    this.superCannonTween = null;
    for (const sprite of this.superCannonSprites) {
      sprite.destroy();
    }
    this.superCannonSprites = [];
    this.superCannonsShown = false;
  }

  private createFxObjects(): void {
    // Neon dash texture for the equipped cannon colour, generated once and
    // reused by every shot (and cached per colour for future cannons/skins).
    this.dashTexture = ensureDashTexture(this, this.projectileColor);

    // Trail: short elongated streaks, oriented along the PREVIOUS heading.
    this.trailParticles = this.add.particles(0, 0, ensureStreakTexture(this), {
      // Barely any drift: the streak should mark where the shot has been, not
      // spray outwards like sparks.
      speed: { min: 0, max: 12 },
      lifespan: { min: 110, max: 190 },
      // Halved along with the dash so the wake stays in proportion to it.
      scaleX: { start: STREAK_RENDER_SCALE, end: STREAK_RENDER_SCALE * 0.25 },
      scaleY: { start: STREAK_RENDER_SCALE * 0.9, end: STREAK_RENDER_SCALE * 0.35 },
      alpha: { start: 0.55, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    });
    this.trailParticles.setDepth(24);

    // Impacts stay round - the only place a circular burst is allowed.
    this.impactParticles = this.add.particles(0, 0, ensureImpactTexture(this), {
      speed: { min: 30, max: 140 },
      lifespan: { min: 160, max: 420 },
      scale: { start: 0.9, end: 0 },
      alpha: { start: 0.9, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    });
    this.impactParticles.setDepth(40);

    this.barsGraphics = this.add.graphics().setDepth(45);

    // Sized to the visible rectangle so the flash reaches the screen edges on
    // a wide window, not just the logical arena.
    this.damageFlash = this.add
      .rectangle(
        ARENA_W / 2,
        ARENA_H / 2,
        this.visibleBounds.right - this.visibleBounds.left,
        this.visibleBounds.bottom - this.visibleBounds.top,
        0xff1f1f,
        0,
      )
      .setDepth(60);
  }

  /* ------------------------------------------------------------------ */
  /*  Frame update                                                      */
  /* ------------------------------------------------------------------ */

  override update(time: number, _deltaMs: number): void {
    // The RAW frame time, not Phaser's `delta`. Phaser smooths and clamps its
    // delta to keep tweens pretty, which means the deltas it hands out do not
    // add up to the time that actually passed - they can add up to slightly
    // MORE. For a rendering engine that is a feature; for an economy it is a
    // client that quietly runs ahead of the server's clock, gets its window
    // capped at every checkpoint, and diverges from the server's
    // re-simulation forever after. Simulated time must track the wall clock.
    const rawDeltaMs = this.lastFrameTimeMs === null ? 0 : time - this.lastFrameTimeMs;
    this.lastFrameTimeMs = time;
    // While hidden, and for the first frame after returning, this yields null:
    // the hunt grants exactly zero progress for time spent in a background
    // tab. Offline rewards are the SERVER's job, never the browser's clock.
    const step = this.clock.consume(rawDeltaMs);
    if (step === null) {
      return;
    }
    // Clamped inside the simulation too, as a second line of defence.
    this.sim.advance(step);
    this.handleEvents();
    this.syncShipOrientation();
    this.syncMobViews();
    this.syncBossView();
    this.syncPetView(step / 1000);
    /* E as invocações de carta, depois do pet: as duas são escoltas. */
    this.syncSummonsView();
    /* E o que as outras cartas desenham no casco. */
    this.syncCardVfx();
    this.syncProjectileViews();
    /* Os mísseis têm a sua lista e a sua sincronização (gate V28G.L). */
    this.syncMissileViews(step);
    this.syncBeamView(step);
    this.drawHealthBars();
    this.updateStars(step / 1000);
    this.updateBackdrop(step / 1000);
    // After the backdrop, because it scrolls at the backdrop's own speed.
    this.updateGroundLife(step / 1000);
    this.updateAirParticles(step / 1000);
    this.updateLightning(step);
    this.updateShootingStars(step);
    this.updateEnergyTraces(step);

    this.snapshotTimerMs += step;
    if (this.snapshotTimerMs >= 250) {
      this.snapshotTimerMs = 0;
      this.pushSnapshot();
    }
  }

  /**
   * Tells React what the run is - and, when the run is dead, that it is dead.
   *
   * The two travel together on purpose. `bridge` is synchronous pub/sub and
   * React batches every state change made inside one call, so publishing the
   * death here means the player never gets a render of a dead run with no way
   * out of it. That is the whole fix: not "announce sooner", but "announce in
   * the same batch", which is a property of where the call sits rather than of
   * how fast the machine is.
   *
   * Every door an authoritative run enters through - `create`, `adoptSession`,
   * `hunt:correct`, `hunt:reconfigure`, `hunt:pet` - ends here, which is what
   * makes this the one owner of the question.
   */
  private pushSnapshot(): void {
    this.announceDeathIfNeeded();
    this.options.bridge.sendToUi('hunt:snapshot', {
      snapshot: this.sim.getOverviewSnapshot(),
      fps: Math.round(this.game.loop.actualFps),
      rapidFire: this.rapidFire,
      // Upper bound for the next checkpoint; the server still decides.
      tickMs: this.sim.now(),
      // Exported with the tick it belongs to, so the digest React computes
      // describes the state at exactly that tick and not a later frame.
      deterministicSnapshot: this.sim.exportSnapshot(),
    });
  }

  /**
   * O sistema visual do corte. Nulo depois do `shutdown`.
   */
  private cycloneCuts: CycloneCutSystem | null = null;

  /** O sistema visual da explosão atômica. Nulo depois do `shutdown`. */
  private atomicBlasts: AtomicBlastSystem | null = null;
  /** O sistema das marcas corrosivas. Nulo depois do `shutdown` (V28G.M). */
  private sawBlades: SawBladeSystem | null = null;
  private poisonMarks: PoisonMarkSystem | null = null;
  private bleedMarks: BleedMarkSystem | null = null;
  private bleedBursts: BleedBurstSystem | null = null;
  /**
   * Onde cada inimigo estava desenhado no último quadro (gate V28G.N).
   *
   * A resposta do servidor a uma detonação chega FORA do laço de desenho, e o
   * alvo que ela nomeia pode já não estar na simulação - a detonação pode
   * tê-lo matado. Isto é o último sítio onde ele esteve, que é exatamente onde o
   * corte carmesim tem de aparecer.
   *
   * Presentação, e só: nada nesta tabela decide dano, alvo ou estado.
   */
  private bleedAnchors = new Map<
    number,
    { id: number; x: number; top: number; diameter: number; marks: { id: number }[] }
  >();
  /** O sistema de fogo do míssil. Sem onda de choque, por contrato (V28G.L). */
  private missileBlasts: MissileBlastSystem | null = null;
  /**
   * Um sprite por míssil vivo, pela identidade que a simulação lhe deu.
   *
   * Um mapa próprio e não uma entrada em `projectileViews`: são entidades
   * diferentes com listas diferentes na simulação, e misturá-las faria a
   * reconciliação de uma apagar os sprites da outra.
   */
  private readonly missileViews = new Map<number, MissileView>();
  /** Os sprites de chama livres. Um pool partilhado, nunca um por míssil. */
  private readonly missileFlamePool: Phaser.GameObjects.Image[] = [];
  /** As marcas da esteira, num pool que nunca aloca dentro de um quadro. */
  private readonly missileTrailPool: Phaser.GameObjects.Image[] = [];
  private readonly missileTrailLive: {
    sprite: Phaser.GameObjects.Image;
    remainingMs: number;
  }[] = [];

  /**
   * Uma copia do que foi anunciado, so para diagnostico.
   *
   * Limitada, porque uma lista sem teto numa corrida longa e uma fuga. Guarda
   * as ultimas, que e o que uma captura precisa.
   */
  private readonly procLog: {
    procId: string;
    projectileId: number;
    targetId: number;
    x: number;
    y: number;
    atMs: number;
    /*
     * O TIQUE autoritativo e, quando o efeito e uma cura, a magnitude que ela
     * carregou (gate V28G.I).
     *
     * O tique e o que torna a consolidacao verificavel: "estas N curas foram no
     * MESMO tique e escreveram UM numero" e uma afirmacao sobre o tique, e um
     * pixel nao diz nenhum. `atMs` e o relogio da simulacao e nao serve - dois
     * eventos do mesmo tique podem ser lidos com milissegundos diferentes.
     */
    tick: number;
    healedMilli?: number;
  }[] = [];

  private noteProc(
    procId: string,
    projectileId: number,
    targetId: number,
    x: number,
    y: number,
    tick: number,
    healedMilli?: number,
  ): void {
    if (!DEV_TOOLS_ALLOWED) {
      return;
    }
    this.procLog.push({
      procId,
      projectileId,
      targetId,
      x,
      y,
      atMs: this.sim.now(),
      tick,
      ...(healedMilli === undefined ? {} : { healedMilli }),
    });
    if (this.procLog.length > 64) {
      this.procLog.shift();
    }
  }

  /**
   * **A largura DESENHADA do alvo, na mesma fonte que o renderer já usa.**
   *
   * `syncMobViews` dimensiona cada mob com `sprite.setScale(mob.size /
   * sprite.width)`, portanto `displayWidth` É `mob.size` - a largura que o mob
   * ocupa na arena. Ler o sprite em vez de recalcular `mob.size` é o que
   * garante que não há uma segunda fórmula a envelhecer ao lado da primeira: se
   * alguém mudar como um mob é dimensionado, o corte acompanha sozinho.
   *
   * ## Porque isto funciona mesmo quando o corte MATA
   *
   * Porque `handleEvents()` corre ANTES de `syncMobViews()` no mesmo quadro. A
   * simulação já removeu o mob da sua lista, mas a vista ainda cá está - ela só
   * é destruída na sincronização que vem a seguir. É o "último valor
   * apresentado por identidade", e está disponível sem tocar no protocolo.
   *
   * O recurso final é a largura de outro mob vivo, ou o tamanho base do mapa 1.
   * Ele nunca é usado num
   * caso normal, e existe para um alvo que já não tenha vista nenhuma - um boss
   * removido por uma correção, por exemplo - não produzir um corte de tamanho
   * zero.
   */
  private drawnDiameterOf(targetId: number): number {
    const mob = this.mobViews.get(targetId);
    if (mob) {
      return mob.sprite.displayWidth;
    }
    if (this.bossView && this.sim.getBoss()?.id === targetId) {
      return this.bossView.displayWidth;
    }
    return this.sim.getMobs()[0]?.size ?? HUNT_BALANCE.mobs.displaySize;
  }

  private handleEvents(): void {
    /*
     * As curas deste quadro, somadas por TIQUE (gate V28G.I).
     *
     * Há uma nave, portanto a chave `(atacante, tique)` do contrato reduz-se ao
     * tique. Somar em MILLI inteiro é o que torna a soma exata: vinte e oito
     * acertos de 0,09 somados em vírgula flutuante não dão 2,52.
     *
     * Local ao quadro e nunca guardado entre quadros: `drainEvents` esvazia a
     * fila que `advance` encheu, portanto todos os eventos de um tique chegam
     * juntos - e um tique seguinte é uma ocorrência seguinte, com o seu próprio
     * número.
     */
    const healed: ConfirmedHeal[] = [];
    for (const event of this.sim.drainEvents()) {
      switch (event.type) {
        case 'proc':
          /*
           * A UNICA porta: um facto que a simulacao confirmou.
           *
           * Despachado pelo EFEITO, nunca pela joia. Sem este ramo, uma cura
           * desenharia um corte: os dois viajam no mesmo canal, e o canal existe
           * precisamente para carregar mais do que um efeito.
           */
          if (event.procId === CYCLONE_CUT_EFFECT) {
            /*
             * A posicao vem do evento e nao do sprite do alvo - um corte letal
             * remove o mob no mesmo instante, e procurar o sprite depois seria
             * nao desenhar exatamente o golpe mais importante de ver.
             */
            this.cycloneCuts?.spawn({
              id: event.projectileId,
              x: event.x,
              y: event.y,
              diameter: this.drawnDiameterOf(event.targetId),
            });
          } else if (event.procId === LIFESTEAL_HEAL_EFFECT && event.healedMilli !== undefined) {
            /*
             * Acumula, e não desenha ainda: o número é o TOTAL do tique, e o
             * tique só acaba quando os seus eventos acabarem.
             */
            healed.push({ attacker: HUNT_SHIP, tick: event.tick, healedMilli: event.healedMilli });
          }
          this.noteProc(
            event.procId,
            event.projectileId,
            event.targetId,
            event.x,
            event.y,
            event.tick,
            event.healedMilli,
          );
          break;
        case 'vital-heal':
          /*
           * **A AUTOCURA, e ela é a única forma que a caça produz** (V28G.U).
           *
           * Aqui há uma nave, portanto a lei de seleção do domínio escolhe
           * sempre a própria - não há um ramo de caça, há a mesma lei com um
           * candidato. Um feixe precisaria de duas pontas e não há segunda, e
           * desenhá-lo com comprimento zero seria desenhar nada.
           *
           * O número entra na MESMA agregação do sifão: o `+TOTAL` é um por nave
           * e por tique, e uma cura vinda daqui soma-se a ele em vez de abrir um
           * segundo sistema de números.
           */
          if (event.healedMilli > 0) {
            /*
             * **Zero não desenha nada** (gate V28G.U).
             *
             * A simulação não anuncia uma cura de zero - ela sai antes do evento
             * quando a vida estava cheia ou a nave em baixo - e esta guarda diz o
             * mesmo do lado que desenha. Sem ela, um zero acenderia o brilho SEM
             * número, porque `healNumbersFrom` descarta um total não positivo.
             */
            healed.push({
              attacker: HUNT_SHIP,
              tick: event.tick,
              healedMilli: event.healedMilli,
            });
            this.drawVitalSelfHeal();
          }
          break;
        case 'hit':
          /*
           * The spark belongs to whoever fired, in the colour that shot was
           * BORN with.
           *
           * The missile, its trail and its blast were already frozen at launch;
           * this one spark was the last thing still reading the pet's current
           * energy, so changing colour mid-flight gave a violet missile a green
           * impact for one frame. `projectileId` is on the event precisely so
           * the frozen colour can be looked up here.
           *
           * A beam has no projectile and therefore no frozen colour - it is
           * connected to the pet rather than launched from it, so it follows the
           * live energy by design. That is the `?? live` branch.
           */
          this.impactParticles.particleTint = event.fromPet
            ? (this.frozenShotColor(event.projectileId) ??
              this.petShotColor(PET_BULLET_PHASE_TURNS.impact))
            : this.projectileColor;
          this.impactParticles.emitParticleAt(event.x, event.y, 4);
          if (event.fromPet && event.projectileId === undefined) {
            // A pet hit with no projectile behind it is a BEAM instance: pulse
            // the core and the impact bloom without interrupting the beam.
            this.beamFlash = 1;
          }
          // One number per hit, showing what was actually applied after the
          // overkill clamp - never an invented figure, never a sum. Ten a
          // second from a beam is ten numbers, exactly as three shots are three.
          this.showDamage(event.amount, event.x, event.y);
          /*
           * **E um CORTE, quando foi a lâmina** (gate V28H.N).
           *
           * O contrato pede que "inimigos atingidos recebam um corte/impacto
           * curto". Um acerto sem projétil e sem pet não veio de tiro nenhum, e
           * enquanto o círculo está no ecrã a lâmina é a única coisa capaz de o
           * produzir - a Lança tem o seu impacto na ponta do feixe.
           */
          if (
            event.projectileId === undefined &&
            !event.fromPet &&
            this.sim.cardVisuals().circle?.active === true
          ) {
            this.cardCircle?.cut(
              event.x,
              event.y,
              HUNT_BALANCE.ship.displayHeight * 0.6,
              this.time.now,
              cardColourStore.current(),
            );
          }
          break;
        case 'explosion':
          /*
           * **Duas explosões diferentes, e a SIMULAÇÃO diz qual** (gate V28G.J).
           *
           * `effectId` está no evento precisamente para isto: adivinhar pelo
           * raio seria a cena a decidir a arte a partir de um número de
           * equilíbrio, e o dia em que uma bola do pet tivesse o mesmo raio de
           * uma bomba seria o dia em que ela ganhava fogo nuclear.
           *
           * O raio que chega é o MECÂNICO, o mesmo que decidiu quem levou dano -
           * portanto a onda de choque desenhada é a verdade e não uma
           * aproximação dela.
           */
          /*
           * **As duas explosões de CARTA** (gate V28H.T), antes da atómica.
           *
           * Elas partilhavam a arte da Bomba, e o resultado foi duas cartas
           * diferentes com a mesma explosão - e o Catolé, que tem seis vezes o
           * raio dela, a ler-se como uma esfera de fogo. Agora cada uma tem a
           * sua, escolhida pelo `effectId` e nunca pelo raio.
           */
          if (event.effectId === FIREBALL_BLAST_EFFECT || event.effectId === CATOLE_BLAST_EFFECT) {
            const kind = event.effectId === CATOLE_BLAST_EFFECT ? 'catole' : 'fireball';
            if (!this.cardBlasts) {
              this.cardBlasts = new CardBlastFx(this, Phaser.BlendModes.ADD, 24);
            }
            this.cardBlasts.burst(
              {
                id: event.projectileId,
                kind,
                x: event.x,
                y: event.y,
                /* O raio MECÂNICO: a onda abre até onde ela feriu, e nem mais. */
                radius: event.radius,
              },
              this.time.now,
            );
            if (kind === 'catole') {
              /*
               * **Um tremor CURTO**, e só no Catolé (gate V28H.T).
               *
               * Ele é a maior explosão do jogo, e o peso dela precisa de ser
               * sentido e não só visto. Curto de propósito: um tremor longo faz
               * perder o alvo, e uma carta que atrapalhe quem a usou é uma carta
               * que ninguém volta a usar.
               */
              this.cameras.main.shake(CATOLE_SHAKE_MS, CATOLE_SHAKE_INTENSITY);
            }
            break;
          }
          if (event.effectId === ATOMIC_BLAST_EFFECT) {
            this.atomicBlasts?.spawn({
              id: event.projectileId,
              x: event.x,
              y: event.y,
              radius: event.radius,
            });
            break;
          }
          /*
           * **A terceira explosão: o míssil** (gate V28G.L).
           *
           * O raio que o evento traz é ZERO, e é a verdade - o míssil não tem
           * dano em área nenhum. O tamanho DESENHADO vem daqui, derivado do
           * corpo do míssil, e é por isso que ele não pode ser confundido com um
           * raio de dano: não há nenhum raio de dano para representar.
           *
           * `MissileBlastSystem` não tem camada de onda de choque, o que torna
           * impossível desenhar um círculo que pareça uma zona de dano.
           */
          if (event.effectId === MISSILE_EFFECT) {
            this.missileBlasts?.spawn({
              id: event.projectileId,
              x: event.x,
              y: event.y,
              radius: HUNT_MISSILE_BODY_RADIUS * 2 * MISSILE_BLAST_RADIUS_FACTOR,
            });
            break;
          }
          /*
           * Painted in the colour the SHOT was born with.
           *
           * The view for the projectile that just went off is still in the map:
           * events are drained immediately after the tick and the views are
           * reconciled afterwards. Looking the frozen colour up here is what
           * stops a violet missile leaving a green blast when the player
           * changed energy while it was in the air.
           */
          this.drawExplosion(
            event.x,
            event.y,
            event.radius,
            this.projectileViews.get(event.projectileId)?.color,
          );
          break;
        case 'kill':
          this.impactParticles.particleTint = event.wasBoss ? 0xffd43b : 0x3dff7a;
          this.impactParticles.emitParticleAt(event.x, event.y, event.wasBoss ? 40 : 12);
          break;
        case 'leak':
          this.flashDamage();
          // The pet has no health of its own, so "hurt" means the SHIP was.
          this.lastShipDamageAtMs = this.sim.now();
          break;
        case 'pet-volley':
          if (this.petRenderMode === 'pet-3d') {
            // The 3D model banks and rotates, so its gun mouths are nowhere
            // near the sprite's fixed offsets. Ask the overlay where they
            // really are; it answers with `hunt:pet-muzzles` and the flashes
            // are drawn there instead. Cosmetic, and one frame late at worst.
            this.options.bridge.sendToUi('hunt:pet-volley', { origins: event.origins });
            break;
          }
          // One red flash per machine gun, at the muzzle the bullet really
          // left - the origins come from the simulation, not from the sprite.
          this.flashPetMuzzles(event.origins);
          break;
        case 'card-activated':
          /*
           * Um curto empurrão da câmara na montagem, e nada mais (V28H.H).
           *
           * Muito mais fraco do que o do chefe (0.004 durante 600 ms): a
           * habilidade dispara dezenas de vezes numa corrida e um abanão a
           * sério seria intolerável. A montagem em si é dos dois sprites.
           */
          this.cameras.main.shake(140, 0.0012);
          break;
        case 'card-volley':
          /*
           * Um clarão por boca temporária, na boca de onde o tiro saiu.
           *
           * As origens vêm da simulação - a mesma lista que os dois projéteis
           * usaram - e não das posições dos sprites, que podem estar um quadro
           * atrás. Sem recuo do casco: o casco não disparou.
           */
          this.flashSuperCannonMuzzles(event.origins);
          break;
        case 'boss-spawned':
          this.cameras.main.shake(600, 0.004);
          break;
        case 'ship-death':
          this.announceDeath();
          break;
        default:
          break;
      }
    }
    /*
     * Um número por TIQUE, depois de todos os eventos dele estarem somados.
     *
     * Sobre a NAVE, e nunca sobre o inimigo: o que o jogador precisa de saber é
     * quanto a sua nave recuperou, e de qual alvo cada fração saiu não muda
     * decisão nenhuma. Tiques diferentes são ocorrências diferentes, e por isso
     * o ciclo tem mais do que uma iteração quando um quadro cobre vários.
     */
    for (const number of healNumbersFrom(healed)) {
      this.showHeal(number.attacker, number.milli);
    }
  }

  /**
   * Shows one damage number for one hit.
   *
   * Deliberately unthrottled: at a high attack speed twenty-one projectiles
   * landing in the same frame produce twenty-one numbers. Sampling them would
   * quietly lie about what the ship is doing, and the pool makes the honest
   * version cheap. If a measurement ever shows this is too much, the fix is a
   * decision to take then - not a silent cap now.
   */
  private showDamage(amount: number, x: number, y: number): void {
    const text = this.damagePool.pop() ?? this.createDamageText();
    // Simultaneous hits fan out slightly so they never stack into one blob.
    // Cosmetic only: this counter never touches the simulation RNG, which
    // must stay identical on the server.
    //
    // A ARITMETICA saiu daqui para `combat-text.ts` (gate V28G.I) para a cura
    // poder usar a MESMA, e usa-la por construcao em vez de por copia. O indice
    // continua a ser deste caminho: uma cura nao empurra o proximo numero de
    // dano.
    this.damageSpreadIndex = (this.damageSpreadIndex + 1) % COMBAT_TEXT_SPREAD_STEPS;
    const { x: offsetX, y: offsetY } = combatTextSpread(this.damageSpreadIndex);

    text
      .setText(formatCombatNumber(amount))
      /*
       * A cor é REPOSTA, e não assumida: o pool é partilhado com o `+VALOR` da
       * cura, e um slot reciclado de lá traria a cor dela.
       */
      .setColor(COMBAT_TEXT_DAMAGE_COLOR)
      .setPosition(x + offsetX, y + offsetY)
      .setAlpha(1)
      .setScale(0.75)
      .setVisible(true)
      .setActive(true);
    this.liveDamageTexts.add(text);

    this.tweens.add({
      targets: text,
      scale: 1,
      duration: 110,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: text,
      y: text.y - COMBAT_TEXT_RISE_PX,
      alpha: 0,
      duration: COMBAT_TEXT_LIFE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.recycleDamageText(text);
      },
    });
  }

  /**
   * **`+VALOR` sobre a nave curada, e mais nada** (gate V28G.I).
   *
   * ## Reutiliza o texto de combate, e não cria uma segunda família
   *
   * O mesmo pool, a mesma fonte, o mesmo tamanho, o mesmo contorno, a mesma
   * subida e a mesma duração dos números de dano. Só a COR muda, e é a única
   * coisa que este efeito acrescenta ao que já existia.
   *
   * ## Sem decoração nenhuma
   *
   * Sem partícula, sem símbolo, sem ícone, sem ligação ao inimigo e sem nada
   * sobre ele. O número nasce junto da nave, sobe um pouco e desaparece.
   *
   * ## O valor é AUTORITATIVO
   *
   * Vem somado dos eventos, em milli, e é a vida que a simulação realmente
   * acrescentou depois do clamp. Não é derivado do dano, da percentagem, do
   * nível nem da diferença observada na barra - nenhum deles está ao alcance
   * desta função, e é isso que garante que ela não pode inventar.
   *
   * `formatHealAmount` devolve `null` quando o total é pequeno demais para o
   * formato dizer a verdade, e aí não nasce texto nenhum.
   */
  /**
   * O brilho verde sobre a silhueta da nave (gate V28G.U).
   *
   * A textura e a transformação saem do sprite que a cena já desenha - é isso
   * que faz o brilho ter a forma da nave em vez de uma forma inventada - e uma
   * corrida em que o casco ainda não existe simplesmente não desenha.
   */
  private drawVitalSelfHeal(): void {
    const sprite = this.shipSprite;
    if (!sprite) {
      return;
    }
    this.vitalHeals?.glow({
      id: HUNT_SHIP,
      textureKey: sprite.texture.key,
      x: sprite.x,
      y: sprite.y,
      rotation: sprite.rotation,
      width: sprite.displayWidth,
      height: sprite.displayHeight,
    });
  }

  private showHeal(attacker: string, milli: number): void {
    const update = this.healTally.add(attacker, milli, () => {
      const text = this.healPool.pop() ?? this.createHealText();
      const ship = this.sim.shipPosition();
      text
        .setPosition(ship.x, ship.y - COMBAT_TEXT_HEAL_OFFSET_PX)
        .setAlpha(1)
        .setScale(0.75)
        .setVisible(true)
        .setActive(true);
      this.liveHealTexts.add(text);
      return text;
    });
    if (update === null) {
      return;
    }
    /*
     * O TEXTO, e mais nada.
     *
     * Uma atualização reescreve o número e deixa a animação onde ela estava: a
     * duração continua a ser a que começou na primeira cura, a posição é onde
     * ele nasceu, e a escala e o alpha seguem o seu curso. Reiniciar qualquer
     * uma delas daria a uma nave que cura sem parar um número que nunca
     * desaparece - e um número imortal é outro efeito visual, não este.
     */
    update.text.setText(update.label);
    if (!update.created) {
      return;
    }
    /* A MESMA animação do dano: o mesmo `Back.easeOut` e a mesma subida. */
    this.tweens.add({ targets: update.text, scale: 1, duration: 110, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: update.text,
      y: update.text.y - COMBAT_TEXT_RISE_PX,
      alpha: 0,
      duration: COMBAT_TEXT_LIFE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        /*
         * Só liberta se a entrada ainda for DESTE período.
         *
         * Sem a geração havia uma corrida real: o texto expira, este callback
         * fica agendado, uma cura nova abre o período seguinte, e o callback
         * velho chegava depois e apagava o número que o jogador estava a ver.
         */
        const expired = this.healTally.expire(attacker, update.generation);
        if (expired !== null) {
          this.recycleHealText(expired);
        }
      },
    });
  }

  private createDamageText(): Phaser.GameObjects.Text {
    /* O estilo PARTILHADO com o duelo: uma constante, para os dois modos
     * escreverem um número da mesma maneira. */
    const text = this.add.text(0, 0, '', {
      ...COMBAT_TEXT_STYLE,
      color: COMBAT_TEXT_DAMAGE_COLOR,
    });
    text.setOrigin(0.5, 1).setDepth(45);
    text.setShadow(0, 0, '#5ad9ff', 6, false, true);
    return text;
  }

  /**
   * Um texto de cura: o mesmo estilo do dano, e a cor decidida uma vez.
   *
   * Com o pool separado a cor deixa de ser reposta a cada uso - um objeto deste
   * pool nunca foi outra coisa - e por isso ela vive aqui, onde o objeto nasce.
   */
  private createHealText(): Phaser.GameObjects.Text {
    const text = this.add.text(0, 0, '', {
      ...COMBAT_TEXT_STYLE,
      color: COMBAT_TEXT_HEAL_COLOR,
    });
    text.setOrigin(0.5, 1).setDepth(45);
    text.setShadow(0, 0, '#5ad9ff', 6, false, true);
    return text;
  }

  private recycleHealText(text: Phaser.GameObjects.Text): void {
    this.liveHealTexts.delete(text);
    text.setVisible(false).setActive(false);
    this.healPool.push(text);
  }

  /** Devolve todos os `+TOTAL` ao seu pool - uma fronteira, e mais nada. */
  private clearHealTexts(): void {
    for (const text of this.healTally.takeAll()) {
      this.tweens.killTweensOf(text);
      this.recycleHealText(text);
    }
    /*
     * E o que sobrar no conjunto de vivos sem estar no mapa.
     *
     * Não devia haver nenhum - o mapa é o dono - mas um texto que escapasse
     * ficaria visível para sempre, sem tween que o apagasse e sem entrada que o
     * largasse. Custa uma linha e fecha essa possibilidade.
     */
    for (const text of [...this.liveHealTexts]) {
      this.tweens.killTweensOf(text);
      this.recycleHealText(text);
    }
  }

  private recycleDamageText(text: Phaser.GameObjects.Text): void {
    this.liveDamageTexts.delete(text);
    text.setVisible(false).setActive(false);
    this.damagePool.push(text);
  }

  /**
   * **O número de uma DETONAÇÃO: grande e carmesim néon** (gate V28G.N).
   *
   * ## Porque não é o número de dano comum
   *
   * Porque não é a mesma coisa, e o dono viu-o no duelo antes de eu o ver: um
   * número de dano comum é um tiro entre muitos, e este é a soma de tudo o que
   * as marcas guardaram, cobrada de uma vez, a sair no MESMO instante em que os
   * tiros normais também estão a imprimir os seus.
   *
   * A caça leva-o igual ao duelo. As duas modalidades usam a mesma regra de
   * domínio para esta joia, e não haveria justificação para o mesmo golpe se ler
   * de duas maneiras consoante o ecrã.
   *
   * ## Sem leque
   *
   * O leque separa números que nascem juntos, e é um por acerto. Aqui é **um por
   * alvo detonado**, portanto não há nada para separar.
   */
  private showBleedDamage(amount: number, x: number, y: number): void {
    const text = this.bleedTextPool.pop() ?? this.createBleedText();
    text
      .setText(formatCombatNumber(amount))
      .setPosition(x, y)
      .setAlpha(1)
      /* Nasce maior e assenta: um golpe cobrado de uma vez chega como um golpe. */
      .setScale(1.6)
      .setVisible(true)
      .setActive(true);
    this.liveBleedTexts.add(text);
    this.tweens.add({
      targets: text,
      scale: 1,
      duration: Math.round(COMBAT_TEXT_LIFE_MS / 3),
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: text,
      y: text.y - COMBAT_TEXT_RISE_PX * 1.8,
      alpha: 0,
      duration: COMBAT_TEXT_LIFE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.recycleBleedText(text);
      },
    });
  }

  private createBleedText(): Phaser.GameObjects.Text {
    const text = this.add.text(0, 0, '', COMBAT_TEXT_BLEED_STYLE);
    /* Acima dos números comuns: quando os dois saem no mesmo tique, este manda. */
    text.setOrigin(0.5, 0.5).setDepth(46);
    return text;
  }

  private recycleBleedText(text: Phaser.GameObjects.Text): void {
    this.tweens.killTweensOf(text);
    this.liveBleedTexts.delete(text);
    text.setVisible(false).setActive(false).setScale(1);
    this.bleedTextPool.push(text);
  }

  /**
   * Devolve os números de detonação ao pool. **Só no encerramento.**
   *
   * Deliberadamente FORA de `clearEntityViews`, ao contrário do dano e da cura:
   * a detonação imprime o seu número e a seguir adota o estado autoritativo, que
   * chama `clearEntityViews` no mesmo instante. Limpar aqui apagaria o número
   * um quadro depois de ele nascer - ninguém o veria nunca.
   *
   * É seguro deixá-lo viver: ao contrário do `+TOTAL` da cura, este texto não
   * está registado em mapa nenhum e não pertence a nenhuma entidade. É uma
   * posição no mundo e 580 ms de vida.
   */
  private clearBleedTexts(): void {
    for (const text of [...this.liveBleedTexts]) {
      this.recycleBleedText(text);
    }
  }

  /** Returns every popup to the pool - used when a run ends or is replaced. */
  private clearDamageTexts(): void {
    for (const text of [...this.liveDamageTexts]) {
      this.tweens.killTweensOf(text);
      this.recycleDamageText(text);
    }
    /*
     * E o leque do DANO volta ao princípio.
     *
     * Uma corrida nova, um mundo substituído ou uma nave reiniciada não herdam a
     * posição onde a anterior ia: o primeiro número depois de uma fronteira
     * nasce sempre no mesmo sítio, seja qual for a fronteira.
     */
    this.damageSpreadIndex = 0;
  }

  private flashDamage(): void {
    this.damageFlash.setAlpha(0.32);
    this.tweens.add({ targets: this.damageFlash, alpha: 0, duration: 380 });
    this.cameras.main.shake(220, 0.006);
  }

  private clearEntityViews(): void {
    for (const text of this.healthTexts.values()) {
      text.destroy();
    }
    this.healthTexts.clear();
    this.clearDamageTexts();
    /*
     * E os `+TOTAL`, que têm o seu próprio pool desde que os dois se separaram.
     *
     * Ficou de fora quando a separação foi feita: `clearHealTexts` existia e era
     * chamada no SHUTDOWN, mas não aqui - portanto uma morte, uma correção do
     * mundo, uma adoção de sessão ou uma troca de canhão deixavam o número da
     * cura a subir sobre uma corrida que já não existia, com o mapa a apontar
     * para ele. O contrato apanhou-o.
     */
    this.clearHealTexts();
    /*
     * E nenhum brilho de cura sobrevive à substituição do mundo (gate V28G.U):
     * ele descrevia uma cura de uma corrida que já não existe.
     */
    this.vitalHeals?.clear();
    for (const view of this.mobViews.values()) {
      view.sprite.destroy();
    }
    this.mobViews.clear();
    this.bossView?.destroy();
    this.bossView = null;
    /* A chave morre com a vista: a próxima nasce a perguntar de novo. */
    this.bossBodyKey = '';
    this.bossGlow?.destroy();
    this.bossGlow = null;
    for (const view of this.projectileViews.values()) {
      view.sprite.destroy();
      view.glow?.destroy();
    }
    /* E os mísseis, que têm o seu próprio mapa e a sua própria esteira. */
    this.clearMissileViews();
    this.projectileViews.clear();
    // The beam is hidden rather than destroyed: it belongs to the pet, not to
    // the enemies being cleared, and rebuilding it would allocate on a restart.
    this.hideBeam();
    this.barsGraphics.clear();
  }

  /* ------------------------------------------------------------------ */
  /*  Entity views                                                      */
  /* ------------------------------------------------------------------ */

  private syncMobViews(): void {
    const seen = new Set<number>();
    /*
     * **A folha do mapa desta sessão, e nunca a de outro** (gate V28I.BN).
     *
     * A chave sai do `mapIndex` autoritativo. Na primeira sessão o `preload` já
     * a trouxe e o ramo do marcador nunca corre; numa viagem, o corpo fica no
     * marcador até a folha do destino chegar, e troca no quadro em que ela
     * existe. `requestMapBody` é idempotente — uma busca quando a textura já cá
     * está — e a recusa de um índice sem arte é dita uma vez.
     */
    const mapIndex = this.session.mapIndex;
    const bodyKey = huntMapBodyTextureKey('mob', mapIndex);
    if (!this.textures.exists(bodyKey)) {
      this.requestMapBody('mob', mapIndex, true);
    }
    const animKey = huntMapBodyAnimKey('mob', mapIndex);
    /*
     * A folha E a animação. As duas nascem juntas — a segunda é criada no
     * `filecomplete` da primeira — mas exigir as duas é o que garante que um
     * corpo nunca é criado sobre uma textura sem quadros numerados, seja qual
     * for a ordem por que o carregador as entregue.
     */
    const ready = this.textures.exists(bodyKey) && this.anims.exists(animKey);
    for (const mob of this.sim.getMobs()) {
      seen.add(mob.id);
      let view = this.mobViews.get(mob.id);
      if (!view) {
        const sprite = this.add.sprite(
          mob.x,
          mob.y,
          ready ? bodyKey : HUNT_MAP_BODY_PENDING_TEXTURE,
        );
        sprite.setScale(mob.size / sprite.width).setDepth(20);
        if (ready) {
          // Animation speed is independent from descent speed.
          sprite.play({ key: animKey, startFrame: mob.id % 9 });
        }
        view = { sprite, bodyKey: ready ? bodyKey : '' };
        this.mobViews.set(mob.id, view);
      } else if (view.bodyKey !== bodyKey && ready) {
        /*
         * A folha chegou. O corpo troca UMA vez, a escala é refeita sobre a
         * largura nova — o marcador tem 64 e um quadro de alienígena tem
         * centenas — e a proporção do desenho é preservada porque a escala é a
         * mesma nos dois eixos, exactamente como sempre foi.
         */
        view.sprite.setTexture(bodyKey);
        view.sprite.setScale(mob.size / view.sprite.width);
        view.bodyKey = bodyKey;
        view.sprite.play({ key: animKey, startFrame: mob.id % 9 });
      }
      view.sprite.setPosition(mob.x, mob.y);
    }
    for (const [id, view] of this.mobViews) {
      if (!seen.has(id)) {
        view.sprite.destroy();
        this.mobViews.delete(id);
      }
    }
  }

  private syncBossView(): void {
    const boss = this.sim.getBoss();
    if (!boss) {
      if (this.bossView) {
        this.bossView.destroy();
        this.bossView = null;
        this.bossBodyKey = '';
        this.bossGlow?.destroy();
        this.bossGlow = null;
      }
      return;
    }
    const bossKey = huntMapBodyTextureKey('boss', this.session.mapIndex);
    const bossAnimKey = huntMapBodyAnimKey('boss', this.session.mapIndex);
    if (!this.textures.exists(bossKey)) {
      this.requestMapBody('boss', this.session.mapIndex, true);
    }
    /* A folha E a animação, pela mesma razão exacta dos alienígenas. */
    const bossReady = this.textures.exists(bossKey) && this.anims.exists(bossAnimKey);
    if (!this.bossView) {
      // Suspenseful entrance: dark glow first, sprite fading in gradually.
      this.bossGlow = this.add
        .circle(boss.x, boss.y, boss.size * 0.62, 0xff1f1f, 0.14)
        .setDepth(18);
      this.tweens.add({
        targets: this.bossGlow,
        alpha: { from: 0.1, to: 0.4 },
        scale: { from: 0.85, to: 1.06 },
        duration: 700,
        yoyo: true,
        repeat: -1,
      });
      /*
       * **A folha do CHEFE deste mapa** (gate V28I.BN). A mesma disciplina dos
       * alienígenas: marcador enquanto a folha não chega, troca uma vez, escala
       * refeita sobre a largura nova. A variação de tamanho do chefe é a que a
       * simulação decidiu — `boss.size` — e não muda com a troca de textura.
       */
      this.bossView = this.add.sprite(
        boss.x,
        boss.y,
        bossReady ? bossKey : HUNT_MAP_BODY_PENDING_TEXTURE,
      );
      this.bossView.setScale(boss.size / this.bossView.width).setDepth(19);
      this.bossBodyKey = bossReady ? bossKey : '';
      if (bossReady) {
        this.bossView.play(bossAnimKey);
      }
      this.impactParticles.particleTint = 0xff1f1f;
      this.impactParticles.emitParticleAt(boss.x, boss.y, 24);
    } else if (this.bossBodyKey !== bossKey && bossReady) {
      this.bossView.setTexture(bossKey);
      this.bossView.setScale(boss.size / this.bossView.width);
      this.bossBodyKey = bossKey;
      this.bossView.play(bossAnimKey);
    }
    const reveal =
      boss.phase === 'revealing'
        ? 1 - boss.revealRemainingMs / HUNT_BALANCE.boss.revealDurationMs
        : 1;
    this.bossView.setAlpha(0.12 + 0.88 * reveal);
    this.bossView.setPosition(boss.x, boss.y);
    this.bossGlow?.setPosition(boss.x, boss.y);
  }

  /**
   * One sprite per projectile. Always.
   *
   * KENOCYBER never converts shots into lasers - see `CLAUDE.md`. An earlier
   * build replaced the sprites with a continuous beam past an attack-speed
   * threshold; artifacts make that threshold ordinary (Speed Fast alone is
   * +30.72 attacks per second at level 10), and the rule is now permanent, so
   * the beam is gone entirely. High cadence is drawn as more discrete
   * projectiles, overlapping naturally, each with its own damage number.
   */
  /* ------------------------------------------------------------------ */
  /*  Pet                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * Draws the active pet in 2D, from the simulation's own state.
   *
   * The sprite follows the simulation - it never decides where the pet is, how
   * it is angled or when it fires. That matters because the muzzles the
   * bullets are born at come from the same state, so a shot always leaves the
   * gun as drawn.
   *
   * In 3D mode this returns immediately and hides the sprite: the Three.js
   * overlay draws the same simulation state, and the two must never both be
   * on screen.
   */
  /**
   * The blast, drawn at exactly the radius the SIMULATION used.
   *
   * Never a radius the renderer chose: the ring is a statement about who took
   * damage, and one that did not match would be a lie about the game rather
   * than a stylistic choice. It is drawn as an expanding, fading ring plus a
   * burst of particles - a filled disc would cover the very enemies it is
   * telling the player about, and a full-screen flash would hide the damage
   * numbers that each of them is printing.
   *
   * Every object here is created for the blast and destroyed when it ends, and
   * blasts are at most a few per second: no pool is warranted, and one that
   * existed would have to be sized for a cadence nobody has measured.
   */
  private drawExplosion(x: number, y: number, radius: number, frozen?: number): void {
    /*
     * The FROZEN colour when the shot that caused it is still known.
     *
     * Falls back to the pet's current energy only when it is not - a blast
     * whose projectile view was already reaped is better painted in something
     * than in nothing.
     */
    const color = frozen ?? this.petShotColor(PET_BULLET_PHASE_TURNS.impact);
    /*
     * A missile's blast has no radius, because it damages nobody but its
     * target. It still needs a visible impact, so a floor is used for the
     * DRAWING only - the number the simulation used stays zero.
     */
    const drawnRadius = radius > 0 ? radius : 30;
    // The wave. Starts small and bright, ends at the true radius and invisible.
    const wave = this.add
      .image(x, y, ensureShockwaveTexture(this))
      .setBlendMode(Phaser.BlendModes.ADD)
      .setTint(color)
      .setDepth(26)
      .setAlpha(0.9);
    const finalScale = (drawnRadius * 2) / wave.width;
    wave.setScale(finalScale * 0.25);
    this.tweens.add({
      targets: wave,
      scaleX: finalScale,
      scaleY: finalScale,
      alpha: 0,
      duration: 340,
      ease: 'Cubic.easeOut',
      onComplete: () => wave.destroy(),
    });

    // A hot core that collapses rather than expands, so the middle of the
    // blast reads as the source instead of a second ring.
    const core = this.add
      .image(x, y, ensureImpactTexture(this))
      .setBlendMode(Phaser.BlendModes.ADD)
      .setTint(color)
      .setDepth(27)
      .setScale(drawnRadius / 10);
    this.tweens.add({
      targets: core,
      scaleX: drawnRadius / 34,
      scaleY: drawnRadius / 34,
      alpha: 0,
      duration: 260,
      ease: 'Quad.easeOut',
      onComplete: () => core.destroy(),
    });

    /*
     * Fragments, thrown outward.
     *
     * Emitted from the RIM rather than the centre, so they read as debris the
     * wave carried rather than as a firework. Count scales with the radius, and
     * is bounded - a bigger blast is busier, never unbounded.
     */
    this.impactParticles.particleTint = this.petShotColor(PET_BULLET_PHASE_TURNS.trail);
    const fragments = Math.min(26, Math.round(drawnRadius / 4));
    for (let index = 0; index < fragments; index += 1) {
      const angle = (index / fragments) * Math.PI * 2;
      this.impactParticles.emitParticleAt(
        x + Math.cos(angle) * drawnRadius * 0.55,
        y + Math.sin(angle) * drawnRadius * 0.55,
        1,
      );
    }
  }

  /**
   * The pet's colour right now, for one part of a shot.
   *
   * The energy alone decides it. A fixed palette ignores the clock entirely and
   * returns the catalog's colour; Anomaly and Prismatic are functions of the
   * SIMULATION's time, so two clients at the same tick draw the same bolt and a
   * background tab does not advance the spectrum.
   */
  private petShotColor(phaseTurns: number, energy = this.petEnergy): number {
    if (!energy) {
      return HuntScene.PET_BULLET_FALLBACK;
    }
    return petEnergyColorAt(energy, this.sim.now(), phaseTurns);
  }

  /**
   * The colour a shot was BORN with, by its id, or null when it has no view.
   *
   * Events are drained immediately after the tick and the projectile views are
   * reconciled afterwards, so the view for a shot that just landed is still in
   * the map. Null covers the two honest cases: a hit that no projectile caused
   * - a beam - and a shot the renderer never saw, which cannot have a frozen
   * colour to remember.
   */
  private frozenShotColor(projectileId: number | undefined): number | null {
    if (projectileId === undefined) {
      return null;
    }
    return this.projectileViews.get(projectileId)?.color ?? null;
  }

  /**
   * One flash per machine gun, in the pet's energy, plus the body's recoil.
   *
   * Shared by both renderers so the two representations kick and flash
   * identically - the only difference is WHERE the muzzles are, which is the
   * whole point of asking the 3D model for its own.
   */
  private flashPetMuzzles(origins: readonly { x: number; y: number }[]): void {
    this.impactParticles.particleTint = this.petShotColor(PET_BULLET_PHASE_TURNS.muzzle);
    for (const origin of origins) {
      this.impactParticles.emitParticleAt(origin.x, origin.y, 3);
    }
    // A short kick backwards along the heading, decayed in `syncPetView`.
    // Cosmetic - it never moves the simulation's pet.
    this.petRecoil = 7;
  }

  /**
   * O clarão das duas bocas temporárias (gate V28H.H).
   *
   * A mesma cor do projétil comum, porque é o mesmo tipo de tiro: o que
   * distingue os Super Canhões é a cadência e a origem, nunca uma munição
   * diferente. Três partículas por boca, do pool de impacto que já existe -
   * nenhuma textura, emissor ou canvas novo.
   */
  private flashSuperCannonMuzzles(origins: readonly { x: number; y: number }[]): void {
    this.impactParticles.particleTint = this.projectileColor;
    for (const origin of origins) {
      this.impactParticles.emitParticleAt(origin.x, origin.y, 3);
    }
  }

  /**
   * **A lâmina, o feixe e a carga** (gate V28H.M).
   *
   * ## O defeito que este método existe para corrigir
   *
   * A primeira bancada da caça mostrou o Drone e **mais nada**: o Turbilhão e a
   * Lança Solar feriam e não apareciam. O desenho delas tinha sido escrito
   * apenas na cena do duelo. Um jogador que liga uma carta e não vê nada não
   * consegue sequer saber se ela funcionou - foi exactamente o que aconteceu.
   *
   * ## A geometria vem da SIMULAÇÃO
   *
   * `cardVisuals()` devolve as medidas com que o dano é decidido, portanto a
   * lâmina é desenhada onde ela apanha e o feixe onde ele cobra. Um número
   * escolhido aqui seria uma lâmina que parece apanhar quem não apanha.
   */
  private syncCardVfx(): void {
    const visuals = this.sim.cardVisuals();
    /*
     * As bombas em voo são independentes das outras duas cartas: elas existem
     * depois de a carta acabar, e por isso são desenhadas ANTES da saída
     * antecipada. Foi este atalho que já escondeu um efeito uma vez.
     */
    this.syncCardBlasts();
    /*
     * **E a esfera a CRESCER enquanto o dedo está na tecla** (gate V28H.V).
     *
     * Antes da saída antecipada e ao lado das bombas em voo, pela mesma razão
     * exacta: ela não é uma das duas composições que a saída trata, e um atalho
     * desses já escondeu um efeito uma vez.
     *
     * A carga passou de 1,2 s para NOVE segundos, e nove segundos sem nada no
     * ecrã não seriam uma carta, seriam uma nave encravada. O raio vem da
     * simulação - é o da explosão que ela vai abrir - e o corpo é a mesma
     * fracção dele que o duelo desenha.
     */
    if (visuals.sphere !== null && !this.cardBlasts) {
      this.cardBlasts = new CardBlastFx(this, Phaser.BlendModes.ADD, 24);
    }
    this.cardBlasts?.charging(
      visuals.sphere === null
        ? []
        : [
            {
              key: 'ship',
              x: visuals.sphere.x,
              y: visuals.sphere.y,
              /*
               * O número vem PRONTO da simulação (gate V28I.C): nesta arena o
               * raio da bola a carregar é o do corpo, e multiplicá-lo aqui seria
               * uma segunda escala para a mesma coisa.
               */
              radius: visuals.sphere.radius,
            },
          ],
      this.time.now,
    );
    if (
      visuals.circle === null &&
      visuals.beam === null &&
      visuals.charge === null &&
      visuals.bastion === null
    ) {
      this.syncBastion(null);
      /*
       * Nenhuma das duas cartas está equipada: as composições que nunca chegaram
       * a ser construídas não são construídas agora. Uma caçada inteira sem elas
       * não paga um único objecto por elas.
       */
      this.cardCircle?.update(null, this.time.now);
      this.cardLance?.update({ charge: null, beam: null, hullRadius: 0 }, this.time.now);
      return;
    }
    if (!this.cardCircle) {
      this.cardCircle = new MagicCircleFx(this, Phaser.BlendModes.ADD, 23);
    }
    if (!this.cardLance) {
      this.cardLance = new SolarLanceFx(this, Phaser.BlendModes.ADD, 24);
    }
    /*
     * **A cor é do JOGADOR** (gate V28H.O), lida no quadro e não subscrita.
     *
     * A caixa guarda um inteiro; a cena lê-o quando desenha. Assim a serra muda
     * de cor no quadro seguinte à escolha, sem um re-render de React entre as
     * duas coisas - e sem a cena ter de conhecer React de todo.
     */
    const tint = cardColourStore.current();
    this.cardCircle.update(
      visuals.circle === null ? null : { ...visuals.circle, tint },
      this.time.now,
    );
    this.syncBastion(visuals.bastion);
    this.cardLance.update(
      {
        /*
         * A carga só vai para a LANÇA, e é por isso que ela pergunta pelo
         * `locksMovement`: é a Lança que prende o movimento, e é a única carta
         * que o faz por descrição aprovada. A Esfera Ígnea também carrega, e a
         * corona dela seria uma promessa de feixe que não vem.
         */
        /*
         * **A corona é da LANÇA**, e a pergunta passou a ser essa (V28H.V).
         *
         * Ela era `locksMovement`, e funcionou enquanto só a Lança prendia o
         * movimento. Desde que a Esfera passou a prender também, essa pergunta
         * respondia "sim" às duas - e a corona da Lança acendia por cima de uma
         * esfera. Agora a carga diz o que vai PRODUZIR, que era a pergunta certa
         * desde o princípio.
         */
        charge: visuals.charge?.kind === 'beam' ? visuals.charge : null,
        beam: visuals.beam,
        hullRadius: HUNT_BALANCE.ship.displayHeight / 2,
      },
      this.time.now,
    );
  }

  /**
   * **As bombas de carta em voo** (gate V28H.T).
   *
   * A lista vem da SIMULAÇÃO, como as invocações: elas são estado, e é por isso
   * que existem no ecrã do jogador. Antes deste gate a Esfera e o Catolé
   * rebentavam no instante da ordem e só produziam um evento - que morria no
   * servidor, porque a caça adopta a instantânea em vez de repetir a ordem.
   */
  private syncCardBlasts(): void {
    const bodies = this.sim.cardBlastList();
    if (bodies.length === 0 && this.cardBlasts === null) {
      return;
    }
    if (!this.cardBlasts) {
      this.cardBlasts = new CardBlastFx(this, Phaser.BlendModes.ADD, 24);
    }
    this.cardBlasts.update(
      bodies.map((blast) => ({
        id: blast.id,
        kind: blast.kind,
        x: blast.x,
        y: blast.y,
        ageMs: blast.ageMs,
        radius: blast.radius,
        /*
         * **O corpo desta arena**, para as duas bombas, e por razões diferentes.
         *
         * A ESFERA (gate V28I.C): a simulação possui a medida - é a mesma que
         * decide a que distância ela apanha - e desenhar outra seria a cena a
         * inventar um tamanho para uma coisa que colide.
         *
         * O CATOLÉ (gate V28I.D): aqui é só desenho. Ele saía da fracção que a
         * arte partilhada aplica - 34% do raio da explosão - e com uma explosão
         * de seis vezes a Bomba isso dava um míssil maior do que a nave que o
         * largou. Um quinto disso, por decisão do dono, e sem tocar no duelo:
         * lá o corpo é uma constante da arena e continua onde estava.
         */
        bodyRadius:
          blast.kind === 'fireball'
            ? this.sim.fireballBodyRadius()
            : (blast.radius * HUNT_CATOLE_BODY_BPS) / 10_000,
      })),
      this.time.now,
    );
  }

  /**
   * **O Bastião: estrelas e cintilação** (gates V28H.T e V28H.U).
   *
   * ## O anel azul foi REPROVADO, e o que veio no lugar
   *
   * Aqui esteve um anel que apertava com o tempo. O dono reprovou-o e escreveu o
   * que queria: invulnerabilidade como nos jogos clássicos - estrelinhas à volta
   * da nave, o casco a cintilar, branco, dourado e néon em alternância
   * controlada, **sem copiar assets**. A composição vive em {@link BastionFx},
   * e é a MESMA que o duelo desenha: uma segunda versão escrita numa das cenas
   * seria uma segunda versão a divergir, que é a lição que o círculo e a lança
   * já ensinaram.
   *
   * ## O que este método continua a decidir, e o que já não decide
   *
   * Decide QUANDO: a composição existe enquanto a ranhura autoritativa corre, e
   * desaparece no quadro em que ela chega a zero - o mesmo instante em que os
   * tiros voltam a acertar, porque o desenho e a regra leem o mesmo
   * temporizador. Já não decide nada sobre o aspecto.
   *
   * A fracção que resta deixou de ser desenhada, e é deliberado: a contagem
   * pertence ao HUD, e um anel a apertar era uma segunda barra de tempo à volta
   * de uma nave - exactamente o que a nova direcção de arte não pede.
   */
  private syncBastion(
    bastion: { x: number; y: number; radius: number; progress: number } | null,
  ): void {
    if (bastion === null) {
      this.bastionFx?.update(null, this.time.now);
      return;
    }
    if (!this.bastionFx) {
      this.bastionFx = new BastionFx(this, Phaser.BlendModes.ADD, 22);
    }
    this.bastionFx.update(
      { x: bastion.x, y: bastion.y, hullRadius: bastion.radius },
      this.time.now,
    );
  }

  /**
   * **As invocações de carta, desenhadas** (gate V28H.M).
   *
   * ## O Drone usa o sprite do CHEFE DO MAPA 1, e é temporário
   *
   * Decisão do dono, e dita como provisória por ele. Não custa um carregamento:
   * a caça já carrega o chefe do **mapa 1** sob a chave `boss` - ela lê
   * `MAP_CATALOG[0]` - portanto é literalmente a arte aprovada, sem uma
   * textura nova e sem uma pasta nova.
   *
   * ## O Leviatã é uma FORMA, e é o que se pode desenhar honestamente
   *
   * O contrato registou o que ele há-de ser - helicóptero com entradas laterais
   * e setas animadas - e nada disso existe como ficheiro. Inventar um PNG seria
   * inventar arte, que o `CLAUDE.md` proíbe. O que se desenha é o que a
   * simulação garante e o contrato descreve: um corpo grande com um rotor, as
   * **duas entradas laterais** marcadas, e **setas animadas** a apontar para
   * dentro delas - que é o que diz a um jogador por onde se entra.
   *
   * ## O tamanho sai da NAVE que o criou, com um mínimo grande
   *
   * Também aprovado. A escala vem do casco desenhado; o mínimo existe porque um
   * Leviatã pequeno não é um Leviatã - uma nave tem de caber lá dentro para a
   * entrada física fazer sentido.
   */
  private syncSummonsView(): void {
    const present = new Set<number>();
    for (const summon of this.sim.summonList()) {
      present.add(summon.id);
      let container = this.summonSprites.get(summon.id);
      if (container === undefined) {
        container = this.buildSummonSprite(summon.kind);
        this.summonSprites.set(summon.id, container);
      }
      container.setPosition(summon.x, summon.y);
      /* A arte do chefe é desenhada de nariz para cima, como a do pet. */
      container.setRotation(summon.facing + Math.PI / 2);
      /* A barra encolhe para um lado só, e é a razão que a sala já conhece. */
      const bar = container.getByName('bar') as Phaser.GameObjects.Rectangle | null;
      bar?.setScale(
        summon.maxHealthMilli === 0
          ? 0
          : Math.max(0, Math.min(1, summon.healthMilli / summon.maxHealthMilli)),
        1,
      );
      /* As setas do Leviatã só piscam enquanto ninguém entrou. */
      const arrows = container.getByName('arrows') as Phaser.GameObjects.Container | null;
      arrows?.setVisible(!summon.boarded);
      if (arrows && !summon.boarded) {
        arrows.setAlpha(0.45 + 0.35 * Math.sin(this.time.now / 180));
      }
    }
    for (const id of [...this.summonSprites.keys()]) {
      if (present.has(id)) {
        continue;
      }
      this.summonSprites.get(id)?.destroy(true);
      this.summonSprites.delete(id);
    }
  }

  /** O corpo de uma invocação: a arte, a barra, e - no Leviatã - as entradas. */
  private buildSummonSprite(kind: 'drone' | 'leviathan'): Phaser.GameObjects.Container {
    const hull = HUNT_BALANCE.ship.displayHeight;
    const parts: Phaser.GameObjects.GameObject[] = [];
    let size: number;
    if (kind === 'drone') {
      size = hull * 0.7;
      /*
       * **A arte do CHEFE DO MAPA 1, e UM quadro dela** (gate V28H.Q).
       *
       * ## O defeito que esta linha corrigiu
       *
       * Isto era `add.image(0, 0, 'boss')`. A chave `boss` é um **spritesheet
       * 3x3**, e um `Image` sobre uma folha usa o quadro `__BASE` - a folha
       * inteira. O drone da caça era portanto uma grelha de **nove chefes em
       * miniatura** encolhida num quadrado de 67 unidades, e não um chefe.
       *
       * A decisão aprovada é "o Drone usa temporariamente o sprite do chefe do
       * mapa 1", no singular. Nove miniaturas contradizem-na, e o duelo já
       * desenhava um quadro só - os dois modos discordavam.
       *
       * ## A chave é a do DUELO, e passou a ser por uma razão (gate V28I.BN)
       *
       * Ela era `boss`, e isso só era o chefe do mapa 1 porque a caça carregava
       * o mapa 1 em todos os mapas. Corrigido esse defeito, `boss` passaria a ser
       * o chefe do mapa em que a nave está, e o Drone mudaria de aspeto de mapa
       * para mapa **sem ninguém ter decidido isso** — a troca silenciosa que este
       * gate existe para impedir, do outro lado.
       *
       * `SUMMON_DRONE_TEXTURE` é a chave que o duelo e o mundo aberto já usam, e
       * ela está presa ao mapa 1 dentro de `summon-art.ts`. Os três modos passam
       * a desenhar a MESMA arte a partir do mesmo sítio, que é o que "a decisão
       * do dono" quer dizer.
       *
       * ## Um quadro, e não uma animação
       *
       * `Sprite` com o índice `0`: a esta escala uma animação de nove quadros
       * não se lê, e o que se lê é a silhueta. A decisão do dono foi o SPRITE, e
       * não o movimento dele.
       *
       * `__DEFAULT` não tem quadro `0` - só `__BASE` - portanto o caso em que a
       * arte não chegou continua a ser um `Image`. Um quadro inexistente pedido a
       * uma textura de reserva seria um aviso do motor a cada invocação.
       */
      requestDroneTexture(this, this.options.manifest, this.pendingMapArt);
      parts.push(
        this.textures.exists(SUMMON_DRONE_TEXTURE)
          ? this.add.sprite(0, 0, SUMMON_DRONE_TEXTURE, 0).setDisplaySize(size, size)
          : this.add.image(0, 0, '__DEFAULT').setDisplaySize(size, size),
      );
    } else {
      /* O mínimo grande é aprovado: quatro cascos. */
      size = Math.max(hull * 4, hull * 3.5);
      parts.push(
        this.add.rectangle(0, 0, size, size * 0.5, 0x101a26, 0.95).setStrokeStyle(3, 0x63d2ff, 0.9),
      );
      /* O rotor, por cima: é o que o faz ler-se como helicóptero. */
      parts.push(
        this.add.rectangle(0, -size * 0.32, size * 1.1, Math.max(3, size * 0.035), 0x63d2ff, 0.75),
      );
      /* As DUAS entradas laterais, marcadas. */
      const doors: Phaser.GameObjects.GameObject[] = [];
      for (const side of [-1, 1]) {
        parts.push(
          this.add
            .rectangle(side * size * 0.3, size * 0.25, size * 0.22, size * 0.1, 0x63d2ff, 0.9)
            .setBlendMode(Phaser.BlendModes.ADD),
        );
        /* E a seta a apontar PARA DENTRO da entrada. */
        doors.push(
          this.add
            .triangle(
              side * size * 0.3,
              size * 0.42,
              -size * 0.06,
              0,
              size * 0.06,
              0,
              0,
              -size * 0.1,
              0xfff2b0,
              0.9,
            )
            .setBlendMode(Phaser.BlendModes.ADD),
        );
      }
      const arrows = this.add.container(0, 0, doors);
      arrows.setName('arrows');
      parts.push(arrows);
    }
    const bar = this.add
      .rectangle(-size / 2, -size * 0.55, size, Math.max(3, size * 0.06), 0xff5a5a, 0.9)
      .setOrigin(0, 0.5);
    bar.setName('bar');
    parts.push(bar);
    /* Acima das estrelas e dos mobs, abaixo da nave e do HUD - como o pet. */
    return this.add.container(0, 0, parts).setDepth(22);
  }

  private syncPetView(dt: number): void {
    const pet = this.sim.getPet();
    const beam = this.sim.getPetBeam();
    if (this.petRenderMode === 'pet-3d') {
      /*
       * The 3D overlay is told where the pet is EVERY FRAME.
       *
       * It used to read the position out of `hunt:snapshot`, which is the
       * overview push and is deliberately throttled to 250 ms - so the overlay
       * held one position for about fifteen frames and then wrote the next,
       * which is a fifteen-unit teleport four times a second. The 2D sprite
       * below never had the problem because it reads the simulation directly,
       * once per frame, which is exactly what this restores for 3D.
       *
       * Three numbers on a synchronous in-process bus, published from the frame
       * that already computed them. It is presentation, it never travels to the
       * server, and it is not the checkpoint's business: the digest hashes the
       * simulation's own snapshot, which this only reads.
       */
      this.options.bridge.sendToUi('hunt:pet-transform', {
        pet: pet ? { x: pet.x, y: pet.y, facing: pet.facing } : null,
        /*
         * The expression, decided from the scene's OWN authoritative state.
         *
         * `bossOnField` is set by the boss events and cleared when the
         * encounter ends; `lastShipDamageAt` is stamped by the leak the
         * simulation already reports. No request, nothing persisted, and the
         * priority lives in one pure function rather than in this loop.
         */
        /*
         * The boss is READ, not tracked.
         *
         * An event-driven flag would have to enumerate every way an encounter
         * can end - defeated, breached, the ship dying, a divergence
         * correction restoring a run without one - and would be wrong the
         * first time one was missed. `getBoss()` is the simulation's own
         * answer, and it covers the reveal, the descent and every ending at
         * once.
         */
        mood: petMood({
          bossActive: this.sim.getBoss() !== null,
          msSinceShipDamage:
            this.lastShipDamageAtMs === null ? null : this.sim.now() - this.lastShipDamageAtMs,
        }),
        // The simulation's own answer. Always false for the three pets that
        // fire projectiles, which is every pet but one.
        beaming: beam !== null,
        /*
         * And where it ends, so the OVERLAY can draw it.
         *
         * The arena cannot: this scene's canvas is underneath the pet's, so a
         * beam drawn here is painted over by the pet's own body for as far as
         * its silhouette reaches. Drawn in the 3D scene it leaves the crystal
         * and passes over the wings.
         */
        beamTarget: beam ? { x: beam.x, y: beam.y } : null,
      });
      this.petSprite?.setVisible(false);
      return;
    }
    if (!pet) {
      this.petSprite?.setVisible(false);
      return;
    }
    const activePet = this.session.activePet;
    if (!activePet) {
      this.petSprite?.setVisible(false);
      return;
    }
    const textureKey = `pet-${activePet.assetKey}`;
    if (!this.petSprite) {
      this.petSprite = this.add
        .image(pet.x, pet.y, this.textures.exists(textureKey) ? textureKey : '__DEFAULT')
        // Above the starfield and the mobs, below the ship and the HUD.
        .setDepth(22)
        .setDisplaySize(96, 96);
      this.loadTextureThen(textureKey, activePet.assetKey, (key) => {
        this.petSprite?.setTexture(key).setDisplaySize(96, 96);
      });
    } else if (this.petSprite.texture.key !== textureKey) {
      // The form changed (an evolution). Swap the art in place - the entity,
      // its drift and its cadence are untouched.
      this.loadTextureThen(textureKey, activePet.assetKey, (key) => {
        this.petSprite?.setTexture(key).setDisplaySize(96, 96);
      });
    }

    // Recoil decays toward zero; it never accumulates across volleys.
    this.petRecoil = Math.max(0, this.petRecoil - dt * 34);
    const back = this.petRecoil;
    this.petSprite
      .setVisible(true)
      .setPosition(pet.x - Math.cos(pet.facing) * back, pet.y - Math.sin(pet.facing) * back)
      // The art is drawn nose-up, and `facing` is measured from +x, so the
      // sprite is rotated a quarter turn to match the simulation's heading.
      .setRotation(pet.facing + Math.PI / 2);
  }

  /** DEV/E2E ONLY - swaps which representation of the pet is drawn. */
  setPetRenderMode(mode: 'pet-2d' | 'pet-3d'): void {
    this.petRenderMode = mode;
    if (mode === 'pet-3d') {
      this.petSprite?.setVisible(false);
    }
  }

  /**
   * The CONTINUOUS LASER, and the only one this game will ever draw.
   *
   * `docs/PETS_V1_SPEC.md` authorises a beam for the Void Butterfly alone. The
   * ship's bolts, the cannons, the Orb's shots, the Abyssal Drone's balls and
   * the Ballistic Drone's missiles are discrete projectiles and stay that way -
   * nothing in this method is reachable from any of them.
   *
   * ## What is drawn, and from where
   *
   * Whether the beam is ON, and WHO it is locked to, are the simulation's
   * answers (`getPetBeam`). This decides only how that reads: three concentric
   * strips from the pet's crystal to the enemy, packets of energy running along
   * it, and a bloom where it lands. Nothing here can change what was damaged.
   *
   * ## Nothing is allocated per frame
   *
   * The sprites are built once, on the first frame a beam exists, and are
   * hidden rather than destroyed when it stops. A weapon that is on for minutes
   * at a time cannot afford to create and free objects every frame.
   */
  private syncBeamView(stepMs: number): void {
    const beam = this.sim.getPetBeam();
    const pet = this.sim.getPet();
    if (!beam || !pet) {
      this.hideBeam();
      return;
    }

    const views = this.beamViews ?? this.createBeamViews();
    /*
     * The beam starts at the CRYSTAL, not at the simulation's pet position.
     *
     * The 3D model is drawn at a smoothed position pursuing the authoritative
     * one, with a vertical bob on top, so `pet.x/y` is a frame behind wherever
     * the butterfly actually is - and a line anchored there came out of her
     * flank while she was moving. The overlay reports the emitter's projected
     * position from the frame that drew it; the simulation's own position is
     * only the fallback for the moments before a model exists.
     */
    const origin = this.petEmitter ?? { x: pet.x, y: pet.y };
    /*
     * And it thickens with the form.
     *
     * EV. III applies ten damage instances a second to EV. I's six, and a
     * weapon that hits nearly twice as hard should not draw the same line.
     */
    const gauge = BEAM_EVOLUTION_GAUGE[Math.min(3, Math.max(1, pet.evolution)) - 1] ?? 1;
    /*
     * The colour follows the LIVE energy, and travels to a new one.
     *
     * A beam is connected to the pet rather than launched from it, so there is
     * nothing in flight to repaint retroactively and the frozen-colour rule
     * that governs projectiles does not apply. Changing energy simply changes
     * the beam - over 150 ms, so it reads as a transition rather than a cut.
     */
    this.beamFadeMs = Math.min(PET_BEAM_COLOR_FADE_MS, this.beamFadeMs + stepMs);
    const blend = this.beamFadeMs / PET_BEAM_COLOR_FADE_MS;

    const dx = beam.x - origin.x;
    const dy = beam.y - origin.y;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);

    /*
     * A brief flare on each damage instance.
     *
     * `hit` events already fired this frame set `beamFlash`, which decays. The
     * beam itself never switches off for it - a pulse in the core and at the
     * impact is what the brief asks for, and blinking the whole thing would
     * read as the weapon stuttering.
     */
    this.beamFlash = Math.max(0, this.beamFlash - stepMs / 90);
    const flare = 1 + this.beamFlash * 0.55;

    /*
     * The STRIPS belong to whichever layer can actually show them.
     *
     * In 3D the overlay draws them, because this canvas is underneath the pet
     * and a beam drawn here is painted over by her own wings for as far as they
     * reach. In 2D there is no overlay, so the arena draws them itself. The
     * packets and the impact bloom stay here either way: they are out along the
     * beam and at the enemy, where nothing occludes them.
     */
    const stripsHere = this.petRenderMode !== 'pet-3d';
    for (const [index, layer] of BEAM_LAYERS.entries()) {
      const strip = views.strips[index];
      if (!strip) {
        continue;
      }
      if (!stripsHere) {
        strip.setVisible(false);
        continue;
      }
      const target = this.petShotColor(PET_BEAM_PHASE_TURNS[layer]);
      const color = this.beamColorTo(layer, target, blend);
      strip.setVisible(true);
      strip.setPosition(origin.x, origin.y);
      strip.setRotation(angle);
      strip.setTint(color);
      // Length is the distance; thickness is the layer's own, in arena units.
      strip.setDisplaySize(
        distance,
        BEAM_ARENA_THICKNESS[layer] * gauge * (index === 2 ? flare : 1),
      );
      strip.setAlpha(Math.min(1, BEAM_LAYER_ALPHA[layer] * flare));
    }

    /*
     * Packets travelling emitter to target.
     *
     * Phases are fixed fractions offset per packet and advanced by the clock,
     * so they are evenly spaced and never bunch. Deterministic from the
     * simulation's own time - no randomness anywhere in the effect.
     */
    const pulseColor = this.beamColorTo(
      'pulse',
      this.petShotColor(PET_BEAM_PHASE_TURNS.pulse),
      blend,
    );
    for (const [index, pulse] of views.pulses.entries()) {
      const phase =
        (((this.sim.now() / BEAM_PULSE_TRAVEL_MS + index / BEAM_PULSE_COUNT) % 1) + 1) % 1;
      pulse.setVisible(true);
      pulse.setPosition(origin.x + dx * phase, origin.y + dy * phase);
      pulse.setTint(pulseColor);
      // Growing as it goes and fading at both ends, so a packet arrives rather
      // than simply appearing at the target.
      const packet = (7 + phase * 5) * gauge;
      pulse.setDisplaySize(packet, packet);
      pulse.setAlpha(Math.sin(phase * Math.PI) * 0.85);
    }

    const impactColor = this.beamColorTo(
      'impact',
      this.petShotColor(PET_BEAM_PHASE_TURNS.impact),
      blend,
    );
    views.impact.setVisible(true);
    views.impact.setPosition(beam.x, beam.y);
    views.impact.setTint(impactColor);
    const bloom = (20 + this.beamFlash * 16) * gauge;
    views.impact.setDisplaySize(bloom, bloom);
    views.impact.setAlpha(0.5 + this.beamFlash * 0.45);
  }

  /** Hides every beam part without destroying it. */
  private hideBeam(): void {
    const views = this.beamViews;
    if (!views) {
      return;
    }
    for (const strip of views.strips) {
      strip.setVisible(false);
    }
    for (const pulse of views.pulses) {
      pulse.setVisible(false);
    }
    views.impact.setVisible(false);
    this.beamFlash = 0;
  }

  /**
   * One beam part's colour, travelling toward its target over the fade.
   *
   * The previous colour is remembered per part, so the five layers cross-fade
   * together rather than one of them snapping while the others slide.
   */
  private beamColorTo(part: string, target: number, blend: number): number {
    const from = this.beamColors.get(part);
    if (from === undefined || blend >= 1) {
      this.beamColors.set(part, target);
      return target;
    }
    const mixed = Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.IntegerToColor(from),
      Phaser.Display.Color.IntegerToColor(target),
      100,
      Math.round(blend * 100),
    );
    return (mixed.r << 16) | (mixed.g << 8) | mixed.b;
  }

  /** Builds the beam's sprites once, on the first frame one is needed. */
  private createBeamViews(): BeamViews {
    const stripFor = (layer: BeamLayer, depth: number) =>
      this.add
        .image(0, 0, ensureBeamTexture(this, layer))
        .setOrigin(0, 0.5)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(depth)
        .setVisible(false);
    const pulseTexture = ensureBeamPulseTexture(this);
    const views: BeamViews = {
      // Widest first, so the core is drawn on top of its own glow.
      strips: BEAM_LAYERS.map((layer, index) => stripFor(layer, 26 + index)),
      pulses: Array.from({ length: BEAM_PULSE_COUNT }, () =>
        this.add
          .image(0, 0, pulseTexture)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(30)
          .setVisible(false),
      ),
      impact: this.add
        .image(0, 0, pulseTexture)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(31)
        .setVisible(false),
    };
    this.beamViews = views;
    return views;
  }

  /**
   * **Os mísseis do Arsenal Ígneo, desenhados a partir do estado autoritativo.**
   *
   * ## Uma passagem à parte da dos projéteis, e porquê
   *
   * Porque são duas listas na simulação e duas entidades no jogo. Reconciliá-los
   * no mesmo laço obrigaria a distinguir um do outro por um campo, que é a
   * dedução que a Bomba já ensinou a não fazer - e faria a limpeza de uma apagar
   * os sprites da outra.
   *
   * ## O tamanho sai de uma conta só, e é a mesma da Bomba
   *
   * `(raio de corpo x 2) / MISSILE_BODY_SPAN`, com o raio de corpo do míssil -
   * que é 70 % do da Bomba por derivação em `hunt/balance.ts`. Um único fator nos
   * dois eixos, portanto 70 % do comprimento E 70 % da largura máxima, e a
   * proporção não muda com o zoom porque não há dois fatores para divergirem.
   *
   * ## A chama é uma troca de textura, e mais nada
   *
   * O quadro sai de {@link missileFlameFrame}, função pura do relógio da
   * SIMULAÇÃO - portanto o servidor, o cliente e uma re-simulação chegam ao
   * mesmo, e uma aba em segundo plano não adianta o ciclo. `setTexture` numa
   * imagem existente reutiliza a textura em cache; não gera nenhuma.
   */
  private syncMissileViews(stepMs: number): void {
    const missiles = this.sim.getMissiles();
    const seen = new Set<number>();
    const scale = (HUNT_MISSILE_BODY_RADIUS * 2) / MISSILE_BODY_SPAN;
    const length = HUNT_MISSILE_BODY_RADIUS * 2;
    const texture = ensureMissileBodyTexture(this);
    /* Um quadro para TODOS: a chama é a mesma imagem partilhada nesta cena. */
    const flameTexture = ensureMissileFlameTexture(this, missileFlameFrame(this.sim.now()));
    for (const missile of missiles) {
      seen.add(missile.id);
      /* A ÚNICA transformação de orientação, partilhada com o duelo. */
      const angle = missileRotation(missile.dirX, missile.dirY);
      let view = this.missileViews.get(missile.id);
      if (!view) {
        const sprite = this.add
          .image(missile.x, missile.y, texture)
          /*
           * `NORMAL` e nunca `ADD`: o míssil é um objeto manufaturado com corpo
           * branco, e somar luz sobre um fundo claro apagaria a rampa cilíndrica
           * que a textura assou. As pontas vermelhas e a chama são brilhantes o
           * suficiente sozinhas.
           */
          .setBlendMode(Phaser.BlendModes.NORMAL)
          /*
           * Pivota no MEIO, como a Bomba: o ponto da simulação é o centro do
           * corpo, e é dele que a hitbox é medida. Um pivô no bico poria o corpo
           * meio comprimento à frente do sítio onde ele realmente está.
           */
          .setOrigin(0.5, 0.5)
          .setScale(scale, scale)
          .setDepth(25);
        /*
         * **A CHAMA: um sprite do pool, preso à traseira REAL.**
         *
         * `ADD`, porque fogo é luz - ao contrário do corpo, que é um objeto
         * branco e ficaria transparente. Um por míssil vivo, tirado de um pool
         * partilhado: nunca um emitter, e nunca uma alocação dentro de um quadro.
         */
        const flame = this.missileFlamePool.pop() ?? this.createMissileFlame();
        flame
          .setTexture(flameTexture)
          .setScale(scale * MISSILE_FLAME_SCALE)
          .setVisible(true);
        view = { sprite, flame, trailCooldownMs: 0 };
        this.missileViews.set(missile.id, view);
      }
      view.sprite.setTexture(texture);
      view.sprite.setPosition(missile.x, missile.y);
      view.sprite.setRotation(angle);
      /*
       * O deslocamento local da tubeira, RODADO pela mesma rotação do corpo.
       *
       * É isso que a mantém sempre atrás: não há ângulo em que ela possa
       * aparecer no nariz ou de lado, porque a posição dela é a do corpo mais um
       * vetor que gira com ele.
       */
      const back = MISSILE_FLAME_ANCHOR * length;
      view.flame.setTexture(flameTexture);
      view.flame.setRotation(angle);
      view.flame.setPosition(
        missile.x + Math.cos(angle) * back,
        missile.y + Math.sin(angle) * back,
      );
      /*
       * **A ESTEIRA: curta, fina, e nunca uma linha contínua.**
       *
       * Marcas soltas com um tempo de vida próprio, largadas a um intervalo
       * fixo e desvanecidas depois. Nenhuma delas dura os nove segundos - a mais
       * longa vive {@link MISSILE_TRAIL_FADE_MS} - portanto a esteira desenhada
       * é sempre um rasto curto atrás do corpo, e nunca um traço que atravesse o
       * mapa. O `CLAUDE.md` proíbe a segunda coisa por escrito.
       */
      view.trailCooldownMs -= stepMs;
      if (view.trailCooldownMs <= 0) {
        view.trailCooldownMs = MISSILE_TRAIL_INTERVAL_MS;
        this.dropMissileTrail(missile.x, missile.y, angle, scale);
      }
    }
    for (const [id, view] of this.missileViews) {
      if (seen.has(id)) {
        continue;
      }
      /*
       * Saiu da lista autoritativa: acertou, expirou, tocou o limite ou o mundo
       * foi substituído. A cena não desenha uma explosão aqui - quem anuncia uma
       * explosão é a autoridade, e inventar uma seria inventar um facto.
       */
      view.sprite.destroy();
      /* A chama apaga-se NO MESMO instante e volta ao pool. Nunca sobrevive. */
      view.flame.setVisible(false);
      this.missileFlamePool.push(view.flame);
      this.missileViews.delete(id);
    }
    this.ageMissileTrail(stepMs);
  }

  /** Uma marca de esteira, tirada do pool e devolvida quando apaga. */
  private dropMissileTrail(x: number, y: number, angle: number, scale: number): void {
    const sprite = this.missileTrailPool.pop() ?? this.createMissileTrail();
    sprite
      .setPosition(x, y)
      .setRotation(angle)
      .setScale(scale * MISSILE_TRAIL_SCALE)
      .setAlpha(0.75)
      .setVisible(true);
    this.missileTrailLive.push({ sprite, remainingMs: MISSILE_TRAIL_FADE_MS });
  }

  /** Um sprite de chama novo. Só quando o pool está vazio. */
  private createMissileFlame(): Phaser.GameObjects.Image {
    return (
      this.add
        .image(0, 0, ensureMissileFlameTexture(this, 0))
        .setBlendMode(Phaser.BlendModes.ADD)
        /* A raiz encosta ao lado direito da moldura, que é onde a tubeira está. */
        .setOrigin(1, 0.5)
        .setDepth(24)
        .setVisible(false)
    );
  }

  private createMissileTrail(): Phaser.GameObjects.Image {
    return this.add
      .image(0, 0, ensureMissileTrailTexture(this))
      .setBlendMode(Phaser.BlendModes.ADD)
      .setOrigin(0.5, 0.5)
      .setDepth(24)
      .setVisible(false);
  }

  /**
   * Envelhece as marcas e devolve ao pool as que apagaram.
   *
   * Sem tweens: são muitas, curtas, e um tween por marca seria um objeto de
   * animação por marca. Uma subtração e uma escrita de alpha fazem o mesmo.
   */
  private ageMissileTrail(stepMs: number): void {
    let write = 0;
    for (const entry of this.missileTrailLive) {
      entry.remainingMs -= stepMs;
      if (entry.remainingMs <= 0) {
        entry.sprite.setVisible(false);
        this.missileTrailPool.push(entry.sprite);
        continue;
      }
      entry.sprite.setAlpha(0.75 * (entry.remainingMs / MISSILE_TRAIL_FADE_MS));
      this.missileTrailLive[write] = entry;
      write += 1;
    }
    this.missileTrailLive.length = write;
  }

  /** Larga os sprites dos mísseis e recolhe a esteira. Nunca destrói o pool. */
  private clearMissileViews(): void {
    for (const view of this.missileViews.values()) {
      view.sprite.destroy();
      view.flame.setVisible(false);
      this.missileFlamePool.push(view.flame);
    }
    this.missileViews.clear();
    for (const entry of this.missileTrailLive) {
      entry.sprite.setVisible(false);
      this.missileTrailPool.push(entry.sprite);
    }
    this.missileTrailLive.length = 0;
  }

  private syncProjectileViews(): void {
    const projectiles = this.sim.getProjectiles();
    const seen = new Set<number>();
    for (const projectile of projectiles) {
      seen.add(projectile.id);
      // Heading of the dash: exactly the projectile's current velocity vector,
      // so it always points where it is going. Homing turns the vector a
      // little each frame and the sprite follows smoothly.
      const angle = Math.atan2(projectile.dirY, projectile.dirX);
      // A pet bullet is a discrete projectile exactly like the ship's - same
      // dash art, same flight, same impact - painted in the pet's own ENERGY so
      // the two sources of fire are legible. Never a beam, at any cadence.
      const fromPet = projectile.fromPet === true;
      let view = this.projectileViews.get(projectile.id);
      if (!view) {
        /*
         * The shot's appearance is decided HERE, once, and then frozen.
         *
         * `syncProjectileViews` meets a projectile on the frame it is born, so
         * the energy stored on the view is the energy the pet was wearing when
         * it fired. Changing colour mid-flight would repaint bullets already in
         * the air, which is the retroactive change the design forbids - the
         * same reason a training point applies from the next volley on.
         */
        const energy = fromPet ? this.petEnergy : null;
        const phase = fromPet ? bulletPhaseTurns(projectile.id) : 0;
        const color = fromPet
          ? this.petShotColor(phase + PET_BULLET_PHASE_TURNS.bolt, energy)
          : this.projectileColor;
        /*
         * A ball for an explosive shot, a dash for everything else.
         *
         * The blast radius is on the PROJECTILE, decided by the simulation at
         * fire time - so the renderer never guesses which weapon fired: it
         * reads the same field the damage resolution reads.
         */
        const explosive = (projectile.blastRadius ?? 0) > 0;
        /*
         * A MISSILE is a third shape, and the simulation says which.
         *
         * `remainingMs` is only ever set on a guided missile, so the renderer
         * reads the same field the flight code does rather than guessing from
         * the pet's id - a future pet that fires missiles gets the artwork
         * without a second lookup table.
         */
        const guided = projectile.remainingMs !== undefined;
        /*
         * O NÚCLEO ATÔMICO, e a simulação diz qual projétil é (gate V28G.J).
         *
         * `atomicBomb` é o mesmo campo que a faz voar mais devagar, detetar por
         * proximidade e explodir - portanto a arte e a mecânica leem a mesma
         * verdade, e não há uma segunda tabela a envelhecer ao lado da primeira.
         *
         * Testado ANTES de `guided`, porque uma bomba também tem `remainingMs`:
         * ela tem fim de vida por contrato, e um míssil também. A distinção é a
         * entidade, e o campo que a nomeia é este.
         */
        const bomb = projectile.atomicBomb !== undefined;
        const ballScale = ENERGY_BALL_ARENA_SIZE / ENERGY_BALL_TEXTURE_SIZE;
        /*
         * O comprimento desenhado: o dobro do raio de corpo mecânico.
         *
         * DERIVADO da constante da simulação em vez de escrito, o que mantém a
         * ordem que o §3 e o §5 exigem sem que ninguém tenha de a verificar à
         * mão: corpo (58) < proximidade (81,2) < explosão (648), em raios.
         */
        const bombScale = (HUNT_BOMB_BODY_RADIUS * 2) / ATOMIC_BOMB_BODY_SPAN;
        /*
         * The halo goes in FIRST, so it sits behind the hull it surrounds.
         *
         * One extra sprite for the one projectile type that cannot glow on its
         * own, and only while it is in the air: a missile lives 3.6 seconds and
         * the pet fires 0.85 volleys a second, so this is a handful of objects,
         * not a per-frame allocation.
         */
        const glow = guided
          ? this.add
              .image(projectile.x, projectile.y, ensureMissileGlowTexture(this, color))
              .setBlendMode(Phaser.BlendModes.ADD)
              .setOrigin(MISSILE_ORIGIN_X, 0.5)
              .setScale(MISSILE_RENDER_SCALE * MISSILE_GLOW_SCALE)
              .setDepth(24)
          : null;
        const sprite = this.add
          .image(
            projectile.x,
            projectile.y,
            bomb
              ? ensureAtomicBombTexture(this, atomicBombRollFrame(this.sim.now()))
              : guided
                ? ensureMissileTexture(this, color)
                : explosive
                  ? ensureEnergyBallTexture(this, color)
                  : ensureDashTexture(this, color),
          )
          /*
           * A missile is drawn NORMALLY, and the other two additively.
           *
           * Additive blending turns a dark hull transparent, and the whole
           * point of a missile is that it is a manufactured object with a body
           * rather than a piece of light. Its nose and exhaust are bright
           * enough to read on their own.
           */
          /*
           * A BOMBA é `NORMAL` pela mesma razão que o míssil: ela é um objeto
           * de metal preto, e somar luz tornaria um corpo escuro transparente -
           * que é o oposto do volume que a textura assou.
           */
          .setBlendMode(bomb || guided ? Phaser.BlendModes.NORMAL : Phaser.BlendModes.ADD)
          /*
           * A ball has no front, so it pivots about its middle. A dash and a
           * missile pivot just behind the tip, which is where the simulation's
           * point is - each at its own texture's proportions.
           */
          /*
           * A bomba pivota no seu MEIO: o ponto da simulação é o centro do corpo,
           * e é dele que o raio de proximidade é medido. Um pivô no bico poria o
           * casco meio comprimento à frente do sítio onde ele realmente está.
           */
          .setOrigin(bomb ? 0.5 : guided ? MISSILE_ORIGIN_X : explosive ? 0.5 : DASH_ORIGIN_X, 0.5)
          /*
           * Full-resolution texture drawn at half size: same neon, half the
           * footprint.
           *
           * The SHIP's bolts are then widened by 29.95% on the sprite's local
           * lateral axis only - x stays the authored length, y is the
           * thickness - and the sprite is rotated to the heading every frame,
           * so the extra width is always perpendicular to where the shot is
           * actually flying. The pet's shots keep the original proportions, and
           * an energy ball is uniform on both axes because it is a sphere.
           */
          .setScale(
            bomb
              ? bombScale
              : guided
                ? MISSILE_RENDER_SCALE
                : explosive
                  ? ballScale
                  : DASH_RENDER_SCALE,
            bomb
              ? bombScale
              : guided
                ? MISSILE_RENDER_SCALE
                : explosive
                  ? ballScale
                  : DASH_RENDER_SCALE * (fromPet ? 1 : DASH_LATERAL_SCALE),
          )
          .setDepth(25);
        view = {
          sprite,
          glow,
          lastAngle: angle,
          trailCooldown: 0,
          energy,
          phase,
          color,
          explosive: explosive && !guided && !bomb,
          bomb,
        };
        this.projectileViews.set(projectile.id, view);
      }
      view.sprite.setPosition(projectile.x, projectile.y);
      if (view.bomb) {
        /*
         * **A ROLAGEM**, e ela é uma troca de textura e mais nada.
         *
         * O quadro sai de {@link atomicBombRollFrame}, que é uma função pura do
         * relógio da SIMULAÇÃO - portanto o servidor, o cliente e uma
         * re-simulação chegam ao mesmo quadro, e uma aba em segundo plano não
         * adianta o ciclo. `setTexture` numa imagem existente reutiliza a
         * textura em cache; não gera nenhuma.
         *
         * O corpo continua alinhado com a VELOCIDADE, como o contrato pede: a
         * rotação é a direção do voo, e a rolagem vive dentro da textura.
         */
        view.sprite.setTexture(ensureAtomicBombTexture(this, atomicBombRollFrame(this.sim.now())));
        view.sprite.setRotation(angle);
      } else if (view.explosive) {
        /*
         * The ball TURNS rather than points.
         *
         * Rotating a radially symmetric sprite to its heading does nothing
         * visible, so instead it spins slowly on its own - which is what makes
         * a sphere of plasma read as churning rather than as a decal.
         */
        view.sprite.setRotation(view.sprite.rotation + 0.06);
        // And it breathes, so the ball looks like contained pressure.
        const pulse = 1 + Math.sin(this.sim.now() * 0.012 + view.phase * 6) * 0.12;
        const ball = ENERGY_BALL_ARENA_SIZE / ENERGY_BALL_TEXTURE_SIZE;
        view.sprite.setScale(ball * pulse, ball * pulse);
      } else {
        view.sprite.setRotation(angle);
      }
      if (view.glow) {
        /*
         * The halo tracks the hull exactly, and BREATHES.
         *
         * A missile at cruising speed is a steady object; a glow that pulses
         * around it is what says the motor is still burning. Same position and
         * same rotation, so the two can never separate.
         */
        view.glow.setPosition(projectile.x, projectile.y);
        view.glow.setRotation(angle);
        const breath = 1 + Math.sin(this.sim.now() * 0.02 + view.phase * 6) * 0.08;
        view.glow.setScale(MISSILE_RENDER_SCALE * MISSILE_GLOW_SCALE * breath);
      }

      // Trail: an elongated streak dropped behind, aligned with the heading
      // the shot had BEFORE this frame's homing correction. Emitted every
      // other frame so a fast volley never floods the scene with objects.
      view.trailCooldown -= 1;
      if (view.trailCooldown <= 0) {
        view.trailCooldown = 2;
        /*
         * The wake reads the FROZEN energy, offset from the bolt.
         *
         * For the eight fixed palettes that is a constant and the wake matches
         * the bolt exactly. For Anomaly and Prismatic it advances as the shot
         * travels, so a bolt leaves a ribbon that walks the spectrum behind it
         * instead of a stripe of one colour - and because the energy is the
         * frozen one, swapping colour mid-flight still changes nothing here.
         */
        this.trailParticles.particleTint = view.energy
          ? this.petShotColor(view.phase + PET_BULLET_PHASE_TURNS.trail, view.energy)
          : view.color;
        this.trailParticles.particleRotate = Phaser.Math.RadToDeg(view.lastAngle);
        this.trailParticles.emitParticleAt(projectile.x, projectile.y, 1);
      }
      view.lastAngle = angle;
    }
    for (const [id, view] of this.projectileViews) {
      if (!seen.has(id)) {
        view.sprite.destroy();
        view.glow?.destroy();
        this.projectileViews.delete(id);
      }
    }
  }

  /**
   * Enemy health bars.
   *
   * There is deliberately no marker around the focused enemy: the ship's nose
   * and the direction its shots leave in already tell the player who is being
   * attacked, without a ring cluttering the monster art.
   */
  private drawHealthBars(): void {
    const bars = this.barsGraphics;
    bars.clear();
    /**
     * Red neon bar: near-black trough, a soft halo, then a vivid red core.
     * The halo is what keeps it readable over bright or busy monster art.
     */
    const drawBar = (
      x: number,
      y: number,
      width: number,
      fraction: number,
      thickness: number,
    ): void => {
      const w = Math.max(26, width);
      const left = x - w / 2;
      const filled = Math.max(0, Math.min(1, fraction)) * (w - 2);
      // Trough.
      bars.fillStyle(ENEMY_BAR_BACKDROP, 0.72).fillRect(left, y, w, thickness);
      bars.lineStyle(1, ENEMY_BAR_BORDER, 0.55).strokeRect(left, y, w, thickness);
      if (filled <= 0) {
        return;
      }
      // Neon halo, then the hot core on top of it.
      bars
        .fillStyle(ENEMY_BAR_GLOW, 0.35)
        .fillRect(left, y - 1, filled + 2, thickness + 2)
        .fillStyle(ENEMY_BAR_CORE, 0.98)
        .fillRect(left + 1, y + 1, filled, thickness - 2);
    };

    /*
     * **As marcas corrosivas, na MESMA passagem** (gate V28G.M).
     *
     * Aqui e não noutro método porque a âncora de uma marca é o TOPO de uma
     * barra, e esse número é calculado nestas linhas. Um segundo sítio a
     * recalculá-lo seria uma segunda fórmula para a mesma linha, e o dia em que
     * uma delas mudasse era o dia em que as gotas passavam a flutuar.
     *
     * Os stacks vêm da simulação autoritativa e são agrupados por alvo. A cena
     * não decide se existem, quanto duram ou quando pulsam: ela desenha o que a
     * autoridade tem, e o pulso é a contagem de parcelas a subir.
     */
    /*
     * Onde cada inimigo VIVO está desenhado, para as duas faixas e para o efeito.
     *
     * Recolhido nesta passagem porque é aqui que o topo da barra e o diâmetro
     * desenhado são calculados - um segundo sítio a recalculá-los seria uma
     * segunda fórmula para a mesma linha.
     */
    const bleedAnchors = new Map<
      number,
      { id: number; x: number; top: number; diameter: number; marks: { id: number }[] }
    >();
    const stacksByTarget = new Map<number, { id: number; ticks: number }[]>();
    for (const stack of this.sim.getPoisonStacks()) {
      const existing = stacksByTarget.get(stack.targetId);
      const entry = { id: stack.id, ticks: stack.ticksDone };
      if (existing) {
        existing.push(entry);
      } else {
        stacksByTarget.set(stack.targetId, [entry]);
      }
    }
    const marked: PoisonMarkTarget[] = [];

    const alive = new Set<number>();
    for (const mob of this.sim.getMobs()) {
      alive.add(mob.id);
      // Bar width tracks the enemy's rendered scale.
      const top = mob.y - mob.size / 2 - 10;
      drawBar(mob.x, top, mob.size * 0.6, mob.health / mob.maxHealth, MOB_BAR_THICKNESS);
      this.drawHealthText(mob.id, mob.x, top + MOB_BAR_THICKNESS / 2, mob.health, mob.maxHealth);
      bleedAnchors.set(mob.id, {
        id: mob.id,
        x: mob.x,
        top,
        diameter: this.drawnDiameterOf(mob.id),
        marks: [],
      });
      const stacks = stacksByTarget.get(mob.id);
      if (stacks) {
        marked.push({
          key: String(mob.id),
          x: mob.x,
          /* ACIMA da barra: o topo dela, e nunca sobre o número ou sobre o casco. */
          y: top,
          /*
           * A largura DESENHADA, lida na mesma fonte que o corte já usa - o
           * sprite - em vez de recalculada a partir de `size`. Se alguém mudar
           * como um mob é dimensionado, as gotas acompanham sozinhas.
           */
          diameter: this.drawnDiameterOf(mob.id),
          stacks,
        });
      }
    }
    const boss = this.sim.getBoss();
    if (boss && boss.phase === 'descending') {
      alive.add(boss.id);
      // Same style, thicker and wider so the boss reads as the bigger threat.
      const top = boss.y - boss.size / 2 - 18;
      drawBar(boss.x, top, boss.size * 0.8, boss.health / boss.maxHealth, BOSS_BAR_THICKNESS);
      this.drawHealthText(
        boss.id,
        boss.x,
        top + BOSS_BAR_THICKNESS / 2,
        boss.health,
        boss.maxHealth,
        true,
      );
      bleedAnchors.set(boss.id, {
        id: boss.id,
        x: boss.x,
        top,
        diameter: this.drawnDiameterOf(boss.id),
        marks: [],
      });
      const stacks = stacksByTarget.get(boss.id);
      if (stacks) {
        marked.push({
          key: String(boss.id),
          x: boss.x,
          y: top,
          diameter: this.drawnDiameterOf(boss.id),
          stacks,
        });
      }
    }
    /*
     * Uma chamada por quadro, e ela reposiciona sempre e redesenha só quando o
     * que se vê muda. Um alvo ausente desta lista larga o seu objeto gráfico no
     * mesmo quadro - que é o que faz a marca desaparecer com o sexto tique e com
     * a morte.
     */
    /*
     * **AS SERRAS, sincronizadas com o estado autoritativo** (gate V28G.P).
     *
     * Uma chamada por quadro, e ela não anima nada: a simulação já sabe onde
     * cada lâmina está, e isto põe o sprite lá. Uma lâmina que sai do estado -
     * porque a nave morreu, porque a joia saiu, porque a sessão foi corrigida -
     * sai do ecrã no mesmo quadro.
     *
     * O dono é uma constante porque a caça tem UMA nave. O campo existe na mesma
     * para o sistema ser o mesmo do duelo, onde há seis.
     */
    this.sawBlades?.sync(this.sim.getSawBlades().map((blade) => ({ ...blade, owner: 'ship' })));
    this.poisonMarks?.sync(marked, this.time.now);
    /*
     * **As marcas de sangramento, na faixa DE CIMA** (gate V28G.N).
     *
     * A âncora é o topo da barra MAIS a altura que as gotas ocupam - perguntada
     * ao módulo do veneno, e nunca afinada - o que faz as duas faixas não se
     * tocarem em escala nenhuma e faz a de baixo descer sozinha quando o veneno
     * acaba. Nenhum dos dois módulos conhece o outro: a cena é a única coisa que
     * sabe que os dois existem.
     */
    const cut: BleedMarkTarget[] = [];
    for (const mark of this.sim.getBleedingMarks()) {
      const anchor = bleedAnchors.get(mark.targetId);
      if (anchor === undefined) {
        continue;
      }
      anchor.marks.push({ id: mark.id });
    }
    for (const anchor of bleedAnchors.values()) {
      if (anchor.marks.length === 0) {
        continue;
      }
      cut.push({
        key: String(anchor.id),
        x: anchor.x,
        y:
          anchor.top -
          poisonBandHeight(
            stacksByTarget.get(anchor.id)?.length ?? 0,
            Math.max(POISON_MARK_MIN_PX, anchor.diameter * POISON_MARK_TARGET_RATIO),
            Math.max(POISON_MARK_MIN_PX, anchor.diameter),
          ),
        diameter: anchor.diameter,
        marks: anchor.marks,
      });
    }
    this.bleedMarks?.sync(cut);
    /* E os rebentamentos avançam um quadro, no relógio da CENA. */
    this.bleedBursts?.update(this.time.now);
    /* Guardado para a resposta do servidor saber onde desenhar o efeito. */
    this.bleedAnchors = bleedAnchors;
    // A number never outlives the thing it belonged to.
    for (const [id, text] of this.healthTexts) {
      if (!alive.has(id)) {
        text.destroy();
        this.healthTexts.delete(id);
      }
    }
  }

  /**
   * Draws `current / maximum` inside an enemy's bar.
   *
   * One persistent text per entity, moved and re-labelled each frame, so a
   * screen full of mobs costs a handful of objects rather than one per frame.
   */
  private drawHealthText(
    id: number,
    x: number,
    y: number,
    health: number,
    maxHealth: number,
    boss = false,
  ): void {
    let text = this.healthTexts.get(id);
    if (!text) {
      text = this.add.text(x, y, '', {
        fontStyle: boss ? '700' : '600',
        fontSize: boss ? '13px' : '10px',
        fontFamily: '"Segoe UI", "Inter", system-ui, sans-serif',
        color: '#ffffff',
        stroke: COMBAT_STROKE,
        strokeThickness: boss ? 3 : 2,
      });
      text.setOrigin(0.5, 0.5).setDepth(41);
      this.healthTexts.set(id, text);
    }
    const label = formatHealthPair(health, maxHealth);
    if (text.text !== label) {
      text.setText(label);
    }
    text.setPosition(x, y);
  }

  /**
   * DEV ONLY - structured description of how projectiles are being drawn, so
   * an E2E test can assert the presentation rather than eyeballing a
   * screenshot. Stripped from production builds along with the whole hook.
   */
  /**
   * DEV ONLY - what the arena is currently printing over the fight.
   *
   * Damage popups and health readouts are Phaser text, invisible to the DOM,
   * so a test can only see them through a hook like this one. Read-only:
   * it reports, it never changes a number, and the whole hook is stripped from
   * a production build together with the rest of the tooling.
   */
  private inspectCombatText() {
    return {
      damage: [...this.liveDamageTexts, ...this.liveHealTexts].map((text) => ({
        text: text.text,
        x: Math.round(text.x),
        y: Math.round(text.y),
        alpha: Number(text.alpha.toFixed(3)),
        /*
         * A cor e o tamanho COMPUTADOS do objeto (gate V28G.I).
         *
         * Uma folha visual que varre pixeis mede a cor depois de o tween a ter
         * escurecido e de o contorno a ter misturado com o fundo - responde
         * "parecida com", nunca "e". Estes dois campos respondem "e": sao o que
         * a cena aplicou, lidos do proprio texto que esta a desenhar.
         *
         * O pool e partilhado entre dano e cura, portanto a lista traz os dois,
         * e e por isso que a cor tem de vir por ENTRADA e nao por lista.
         */
        color: text.style.color,
        fontSize: text.style.fontSize,
      })),
      /**
       * Os dois pools, contados **em separado**.
       *
       * `pooled` era um número só, de um pool só, e escondia qual dos dois
       * estava a crescer: a caça acusou 33 e 54 objetos ociosos e foi preciso
       * ler o produto para saber que o excesso era do dano. Agora cada lado
       * responde por si.
       */
      pooled: this.damagePool.length + this.healPool.length,
      damagePooled: this.damagePool.length,
      /**
       * **Os tres numeros da cura, e o que cada um significa.**
       *
       * `healIdle` sao os objetos OCIOSOS, no pool, prontos a ser reutilizados.
       * `healLive` sao os que estao no ecra agora. `healTallies` sao as entradas
       * do mapa que decide de quem e cada texto.
       *
       * Os tres sao lidos de sitios diferentes de proposito: uma fronteira que
       * limpasse os textos e esquecesse o mapa deixaria `healLive` a zero e
       * `healTallies` acima de zero, e e essa a diferenca que uma prova precisa
       * de conseguir ver.
       */
      healIdle: this.healPool.length,
      healLive: this.liveHealTexts.size,
      healTallies: this.healTally.size,
      health: [...this.healthTexts].map(([id, text]) => ({
        id,
        text: text.text,
        x: Math.round(text.x),
        y: Math.round(text.y),
      })),
    };
  }

  /**
   * DEV/E2E ONLY - writes down what one adoption did to the clock.
   *
   * Guarded on the build constant so the body folds away in a production
   * bundle, and on the scene's own debug flag so a dev build that nobody asked
   * for tooling in keeps an empty list. Read-only data: two integers, a label
   * and a timestamp. No identifier a player owns, no cookie, no token.
   */
  private recordAdoption(
    source: SceneAdoption['source'],
    tickBefore: number,
    restored: boolean,
  ): void {
    if (!DEV_TOOLS_ALLOWED || !this.options.debug) {
      return;
    }
    // Bounded: a long session must not grow this without limit.
    if (this.adoptions.length >= 128) {
      this.adoptions.shift();
    }
    this.adoptions.push({
      source,
      restored,
      tickBefore,
      tickAfter: this.sim.getTickMs(),
      at: performance.now(),
    });
  }

  /**
   * Raises the death screen, once per run.
   *
   * Called both when the ship dies under the player's eyes and when a scene
   * adopts a run that was already dead - a reload, a travel, a second tab. The
   * `deadNotified` latch is what keeps the second case from re-announcing a
   * death the player has already dismissed, and it is cleared wherever a fresh
   * simulation is adopted.
   *
   * Silent when the phase is not dead, so every caller can be unconditional.
   */
  private announceDeath(): void {
    this.pushSnapshot();
  }

  /**
   * Publishes the death the run owes React, if it owes one.
   *
   * The decision itself is {@link deathAnnouncement}, which runs in a test with
   * no Phaser in it. Two things live here because they are the scene's: sending
   * the message, and dropping the entity views of a run that has ended.
   *
   * The latch moves only with the message. It used to be set before the numbers
   * were looked up, so a death with no stats latched and published nothing, and
   * nothing ever tried again - a permanent "Abatida" with no overlay from one
   * missing field.
   */
  private announceDeathIfNeeded(): void {
    const decision = deathAnnouncement(
      {
        phase: this.sim.getPhase(),
        deathStats: this.sim.getDeathStats(),
        deathCause: this.sim.getDeathCause(),
      },
      this.deadNotified,
    );
    this.deadNotified = decision.announced;
    if (!decision.announce) {
      return;
    }
    this.options.bridge.sendToUi('hunt:death', decision.announce);
    this.clearEntityViews();
  }

  private inspectProjectiles() {
    const dash = this.textures.get(this.dashTexture)?.getSourceImage();
    const dashWidth = dash?.width ?? 0;
    const dashHeight = dash?.height ?? 0;

    const live = this.sim.getProjectiles();
    const samples = live.slice(0, 4).flatMap((projectile) => {
      const view = this.projectileViews.get(projectile.id);
      if (!view) {
        return [];
      }
      return [
        {
          id: projectile.id,
          rotation: view.sprite.rotation,
          headingAngle: Math.atan2(projectile.dirY, projectile.dirX),
          textureKey: view.sprite.texture.key,
          displayWidth: view.sprite.displayWidth,
          displayHeight: view.sprite.displayHeight,
          blendMode: view.sprite.blendMode,
          scaleX: view.sprite.scaleX,
          scaleY: view.sprite.scaleY,
        },
      ];
    });

    const shipPosition = this.sim.shipPosition();
    return {
      dashTexture: { key: this.dashTexture, width: dashWidth, height: dashHeight },
      projectiles: samples,
      liveProjectileCount: live.length,
      ship: {
        x: shipPosition.x,
        y: shipPosition.y,
        facing: this.sim.shipFacing(),
        desiredFacing: this.sim.desiredFacing(),
        spriteRotation: this.shipSprite?.rotation ?? 0,
        restingFacing: HUNT_BALANCE.ship.restingFacingRad,
      },
      // One entry per drawn cannon, paired with where the simulation says it
      // should be - so a drift between wing and muzzles is directly visible.
      cannons: this.cannonSprites.map((sprite, index) => ({
        x: sprite.x,
        y: sprite.y,
        rotation: sprite.rotation,
        expected: this.sim.cannonPositions()[index] ?? { x: Number.NaN, y: Number.NaN },
      })),
      muzzles: this.sim.muzzlePositions().map((muzzle) => ({ x: muzzle.x, y: muzzle.y })),
      loadout: {
        rarity: this.sim.getOverviewSnapshot().cannonRarity,
        level: this.sim.getOverviewSnapshot().cannonLevel,
        cannonCount: this.cannonSprites.length,
        projectilesPerCannon: this.sim.projectilesPerCannon(),
        projectilesPerVolley: this.sim.projectilesPerVolley(),
      },
      camera: {
        zoom: this.cameras.main.zoom,
        width: this.scale.width,
        height: this.scale.height,
        visible: { ...this.visibleBounds },
        arena: { width: ARENA_W, height: ARENA_H },
      },
      // Kept so a test can assert what the volley SHOULD produce in sprites.
      // There is no beam counterpart: shots are only ever discrete projectiles.
      volley: {
        perCannon: this.sim.projectilesPerCannon(),
        total: this.sim.muzzlePositions().length * this.sim.projectilesPerCannon(),
      },
      cannonTexture: this.cannonSprites[0]?.texture.key ?? null,
      cannonSource: this.cannonSourceFile(),
      dashScale: this.projectileViews.values().next().value?.sprite.scaleX ?? null,
      /**
       * The lateral axis of a SHIP bolt, and the length beside it.
       *
       * Reported as a pair because "13% wider" is only true of one of the two:
       * a single number could not tell a widened dash from a uniformly bigger
       * one, which is the mistake this exists to make impossible.
       */
      dashScaleY:
        [...this.projectileViews.values()].find((view) => view.energy === null)?.sprite.scaleY ??
        null,
      /** What the pet's shots are painted with right now. Energy only. */
      petBullet: {
        energyId: this.petEnergy?.id ?? null,
        // The colours BAKED into the bolts currently in the air, newest last.
        colors: [...this.projectileViews.values()]
          .filter((view) => view.energy !== null)
          .map((view) => view.color),
      },
      stars: {
        layers: STAR_LAYERS.length,
        total: this.stars.length,
        // How far the sky reaches, so a test can prove it covers the edges.
        minX: this.stars.reduce((min, s) => Math.min(min, s.sprite.x), Number.POSITIVE_INFINITY),
        maxX: this.stars.reduce((max, s) => Math.max(max, s.sprite.x), Number.NEGATIVE_INFINITY),
        // Distinct procedural textures shared by the pool - never per-star, and
        // never per-colour, so the whole sky batches.
        textureKeys: [...new Set(this.stars.map((s) => s.sprite.texture.key))].sort(),
        tints: [...new Set(this.stars.map((s) => s.sprite.tintTopLeft))].length,
      },
      hasTarget: this.sim.currentTargetId() !== null,
      // Proof that the focus ring is gone: nothing tracks it any more.
      targetIndicator: null,
      trail: {
        textureKey: this.trailParticles.texture.key,
        alive: this.trailParticles.getAliveParticleCount(),
      },
      impact: { textureKey: this.impactParticles.texture.key },
      /**
       * Proof for the E2E suite that no beam exists any more.
       *
       * `graphicsObjects` counts every `Graphics` in the scene: exactly one,
       * the health bars. A continuous ray between cannon and target would need
       * another, so a beam creeping back in fails the assertion.
       */
      beam: {
        active: false,
        graphicsObjects: this.children.list.filter(
          (child) => child instanceof Phaser.GameObjects.Graphics,
        ).length,
      },
      configuredColor: this.projectileColor,
      additiveBlendMode: Phaser.BlendModes.ADD,
    };
  }

  /**
   * DEV ONLY - the manifest file the drawn cannon texture was loaded from.
   *
   * Comparing texture KEYS only proves the renderer asked for different
   * textures; this proves the pixels came from three different PNGs.
   */
  private cannonSourceFile(): string | null {
    const key = this.cannonSprites[0]?.texture.key;
    if (!key) {
      return null;
    }
    return this.cannonSourceUrls.get(key) ?? null;
  }

  /*
   * `findTargetEntity` used to live here, resolving a target id for the focus
   * ring the dev inspector reported. The ring is gone - the ship's nose says
   * who is being shot - so the helper had no caller left. Deleted rather than
   * kept "in case": dead code that still compiles is the kind that gets
   * reintroduced by accident.
   */

  /**
   * Advances and recycles the starfield.
   *
   * Nothing is allocated here: a star that passes the bottom edge is simply
   * moved back above the top with a new horizontal position.
   */
  /**
   * Scrolls the backdrop downwards.
   *
   * `tilePositionY` is a texture offset: no allocation, no object churn, and
   * one draw call however long the run lasts. Wrapped against the texture
   * height so the number never grows without bound - a float that climbs for
   * hours loses precision, and the drift would visibly stutter.
   */
  private updateBackdrop(dt: number): void {
    const backdrop = this.backdrop;
    if (!backdrop?.visible) {
      return;
    }
    const height = backdrop.texture.getSourceImage().height || 1;
    backdrop.tilePositionY = (backdrop.tilePositionY - this.backdropDrift * dt) % height;
  }

  private updateStars(dt: number): void {
    const { left, right, top, bottom } = this.visibleBounds;
    const margin = 24;
    for (const star of this.stars) {
      const sprite = star.sprite;
      sprite.y += star.speed * dt;
      // Recycled against the visible rectangle, so a wide window keeps stars
      // all the way out to the real screen edges.
      if (sprite.y > bottom + margin) {
        sprite.y = top - margin;
        sprite.x = Phaser.Math.FloatBetween(left, right);
      }
      if (star.twinkleAmplitude > 0) {
        // Gentle sine shimmer, each star on its own phase and rate so the sky
        // never pulses in unison or blinks harshly.
        star.twinklePhase += star.twinkleRate * dt;
        const shimmer = 1 + Math.sin(star.twinklePhase) * star.twinkleAmplitude;
        sprite.setAlpha(Math.max(0.05, Math.min(1, star.baseAlpha * shimmer)));
      }
    }
  }
}

export type { ProjectileEntity };
